import React, { useState, useEffect, useRef } from "react";
import { Document, Packer, Paragraph, TextRun, Header, Footer, ImageRun, AlignmentType } from "docx";
import { PublicClientApplication, InteractionRequiredAuthError } from "@azure/msal-browser";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";

// ─── API & Auth Configuration ────────────────────────────────────────────────
const API_URL = "https://rg-h2-track-ajfqdwb5a3h7g0gk.canadacentral-01.azurewebsites.net";
const API_SCOPE = "api://54f5a40f-7685-4ef8-acf5-02c5d5da4972/access_as_user";

const msalConfig = {
  auth: {
    clientId: "af061846-3d3f-4adf-88db-c29b17fb8921",
    authority: "https://login.microsoftonline.com/6087723e-8b26-4fd2-83c9-941c351d656c",
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: "sessionStorage", storeAuthStateInCookie: false },
};

const msalInstance = new PublicClientApplication(msalConfig);
const loginRequest = { scopes: [API_SCOPE] };

// Token acquisition helper
const getAccessToken = async (instance, account) => {
  try {
    const resp = await instance.acquireTokenSilent({ scopes: [API_SCOPE], account });
    return resp.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const resp = await instance.acquireTokenPopup({ scopes: [API_SCOPE] });
      return resp.accessToken;
    }
    throw err;
  }
};

// API helpers
const apiRead = async (instance, account) => {
  const token = await getAccessToken(instance, account);
  const resp = await fetch(`${API_URL}/api/read`, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  if (!resp.ok) throw new Error(`API read failed: ${resp.status}`);
  return resp.json();
};

const apiWrite = async (instance, account, data) => {
  const token = await getAccessToken(instance, account);
  const resp = await fetch(`${API_URL}/api/write`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!resp.ok) throw new Error(`API write failed: ${resp.status}`);
  return resp.json();
};

const STORAGE_KEY = "harnesstrack-data";

// ─── Theme System ───────────────────────────────────────────────────────────
const THEMES = {
  dark: {
    name: "Dark", bgBase: "#0a0e1a", bgCard: "#0f172a", bgNav: "#0f1629",
    border: "#1e293b", borderHover: "#334155",
    textMuted: "#475569", textSecondary: "#64748b", textTertiary: "#94a3b8",
    textBody: "#cbd5e1", textPrimary: "#e2e8f0", textBright: "#f1f5f9",
    inputBg: "#0a0e1a", selectOptionBg: "#0f172a",
    navText: "#64748b", navTextActive: "#f1f5f9", navActiveBg: "#1e293b",
  },
  light: {
    name: "Light", bgBase: "#f1f5f9", bgCard: "#ffffff", bgNav: "#1e3a5f",
    border: "#e2e8f0", borderHover: "#cbd5e1",
    textMuted: "#94a3b8", textSecondary: "#64748b", textTertiary: "#475569",
    textBody: "#334155", textPrimary: "#1e293b", textBright: "#0f172a",
    inputBg: "#f8fafc", selectOptionBg: "#ffffff",
    navText: "#94b8db", navTextActive: "#ffffff", navActiveBg: "#2d4f7a",
  },
};
const themeCSS = (t) => `:root {
  --bg-base:${t.bgBase}; --bg-card:${t.bgCard}; --bg-nav:${t.bgNav};
  --border:${t.border}; --border-hover:${t.borderHover};
  --text-muted:${t.textMuted}; --text-secondary:${t.textSecondary}; --text-tertiary:${t.textTertiary};
  --text-body:${t.textBody}; --text-primary:${t.textPrimary}; --text-bright:${t.textBright};
  --input-bg:${t.inputBg}; --select-option-bg:${t.selectOptionBg};
  --nav-text:${t.navText}; --nav-text-active:${t.navTextActive}; --nav-active-bg:${t.navActiveBg};
}`;
const SEGMENT_CADENCE = { Diamond: "Monthly", Platinum: "Quarterly", Gold: "Semi-annual", Silver: "Annual", Prospect: "Quarterly" };
const CADENCE_DAYS = { Monthly: 30, Quarterly: 90, "Semi-annual": 180, Annual: 365, Never: 0 };
const SEGMENT_WEIGHT = { Diamond: 4, Platinum: 3, Gold: 2, Silver: 1, Prospect: 0 };
const SEGMENT_COLORS = { Diamond: "#60a5fa", Platinum: "#a78bfa", Gold: "#fbbf24", Silver: "var(--text-tertiary)", Prospect: "#f97316" };
const COLOR_OPTIONS = ["#3b82f6","#10b981","#f59e0b","#f43f5e","#8b5cf6","#06b6d4","#f97316","#14b8a6","#ec4899","#84cc16","#6366f1","var(--text-secondary)"];
const ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const defaultData = () => ({
  parties: ["Our Team","Client","Client Tax Counsel","Client Legal Counsel","Issuing Carrier","Contra Firm","Accepting Firm"],
  segments: ["Diamond","Platinum","Gold","Silver","Prospect"],
  occupations: ["Employed","Business Owner","Retired"],
  settings: { logoPath: "", logoData: "", theme: "dark", teamEmails: { primary: "", cc1: "", cc2: "" }, footerDisclosure: "" },
  templates: [
    { id:"t1", name:"Financial Planning", color:"#3b82f6", estDays:60, category:"Onboarding",
      steps:[
        {id:"s1",name:"Presentation of Plan",type:"single",assignee:"Our Team"},
        {id:"s2",name:"Preliminary Data Collection and Goal Setting",type:"single",assignee:"Our Team"},
        {id:"s3",name:"Presentation of Fees and Scope of Work",type:"single",assignee:"Our Team"},
        {id:"s4",name:"Acceptance of Fees",type:"single",assignee:"Client"},
        {id:"s5",name:"Payment of Fees Based on Terms",type:"single",assignee:"Client"},
        {id:"s6",name:"Creation of Client Upload Folder",type:"single",assignee:"Our Team",autoFolder:true},
        {id:"s7",name:"Data Collection",type:"group",assignee:"Client",children:[
          {id:"s7a",name:"Onboard Call (Rachel/Team)",type:"action",assignee:"Our Team"},
          {id:"s7b",name:"3Y Tax Returns",type:"document",assignee:"Client"},
          {id:"s7c",name:"Recent Account Statements",type:"document",assignee:"Client"},
          {id:"s7d",name:"Debt Structure or Statements",type:"document",assignee:"Client"},
          {id:"s7e",name:"Trust Paperwork",type:"document",assignee:"Client Legal Counsel"},
          {id:"s7f",name:"Business Continuation Documents",type:"document",assignee:"Client"},
        ]},
        {id:"s8",name:"Check-In Call to Review Data",type:"single",assignee:"Our Team"},
        {id:"s9",name:"Data Input",type:"group",assignee:"Our Team",children:[
          {id:"s9a",name:"Basic Details",type:"action",assignee:"Our Team"},
          {id:"s9b",name:"Goal Details",type:"action",assignee:"Our Team"},
          {id:"s9c",name:"Asset and Debt Input",type:"action",assignee:"Our Team"},
          {id:"s9d",name:"Special Liquidity Events",type:"action",assignee:"Our Team"},
          {id:"s9e",name:"Basic Output",type:"action",assignee:"Our Team"},
          {id:"s9f",name:"Scenario/Event Planning",type:"action",assignee:"Our Team"},
        ]},
        {id:"s10",name:"Recommendations Created",type:"single",assignee:"Our Team"},
        {id:"s11",name:"Upload Recommendations to Delivery Folder",type:"single",assignee:"Our Team",autoFolder:true},
        {id:"s12",name:"Delivery and Presentation Meeting",type:"single",assignee:"Our Team"},
        {id:"s13",name:"Final Fees Due",type:"single",assignee:"Client"},
      ]},
    { id:"t2", name:"Exit Planning", color:"#8b5cf6", estDays:90, category:"Onboarding",
      steps:[
        {id:"e1",name:"Presentation of Plan",type:"single",assignee:"Our Team"},
        {id:"e2",name:"Preliminary Data Collection and Goal Setting",type:"single",assignee:"Our Team"},
        {id:"e3",name:"Presentation of Fees and Scope of Work",type:"single",assignee:"Our Team"},
        {id:"e4",name:"Acceptance of Fees",type:"single",assignee:"Client"},
        {id:"e5",name:"Payment of Fees Based on Terms",type:"single",assignee:"Client"},
        {id:"e6",name:"Creation of Client Upload Folder",type:"single",assignee:"Our Team",autoFolder:true},
        {id:"e7",name:"Data Collection",type:"group",assignee:"Client",children:[
          {id:"e7a",name:"Onboard Call (Rachel/Team)",type:"action",assignee:"Our Team"},
          {id:"e7b",name:"3Y Business Tax Returns",type:"document",assignee:"Client Tax Counsel"},
          {id:"e7c",name:"3Y Personal Tax Returns",type:"document",assignee:"Client Tax Counsel"},
          {id:"e7d",name:"Recent Personal Account Statements",type:"document",assignee:"Client"},
          {id:"e7e",name:"Debt Structure or Statements",type:"document",assignee:"Client"},
          {id:"e7f",name:"Trust Paperwork",type:"document",assignee:"Client Legal Counsel"},
          {id:"e7g",name:"Business Continuation Documents",type:"document",assignee:"Client Legal Counsel"},
          {id:"e7h",name:"Census",type:"document",assignee:"Client"},
          {id:"e7i",name:"Balance Sheet",type:"document",assignee:"Client Tax Counsel"},
          {id:"e7j",name:"Income Statement",type:"document",assignee:"Client Tax Counsel"},
        ]},
        {id:"e8",name:"Survey & Add-Backs Discussion",type:"group",assignee:"Our Team",children:[
          {id:"e8a",name:"BERI",type:"action",assignee:"Our Team"},
          {id:"e8b",name:"ODI",type:"action",assignee:"Our Team"},
          {id:"e8c",name:"GPI",type:"action",assignee:"Our Team"},
          {id:"e8d",name:"ValuCompass",type:"action",assignee:"Our Team"},
        ]},
        {id:"e9",name:"Industry Assessment Delivery",type:"single",assignee:"Our Team"},
        {id:"e10",name:"Data Input",type:"group",assignee:"Our Team",children:[
          {id:"e10a",name:"Basic Entity",type:"action",assignee:"Our Team"},
          {id:"e10b",name:"Basic Personal",type:"action",assignee:"Our Team"},
          {id:"e10c",name:"Industry Norms",type:"action",assignee:"Our Team"},
        ]},
        {id:"e11",name:"Custom KPI Tracking & Value Acceleration Presentation",type:"single",assignee:"Our Team"},
        {id:"e12",name:"Social Security Bends Presentation",type:"single",assignee:"Our Team",conditional:true,conditionLabel:"SDE Focus + Employs Spouse"},
        {id:"e13",name:"M&A Advisor Conversation",type:"single",assignee:"Client",conditional:true,conditionLabel:"Client Opted In"},
        {id:"e14",name:"Recommendations Created",type:"single",assignee:"Our Team"},
        {id:"e15",name:"Upload to Delivery Folder",type:"single",assignee:"Our Team"},
        {id:"e16",name:"Delivery and Presentation Meeting",type:"single",assignee:"Our Team"},
        {id:"e17",name:"Final Fees Due",type:"single",assignee:"Client"},
      ]},
    { id:"t3", name:"Account Onboarding", color:"#f59e0b", estDays:30, category:"Onboarding",
      steps:[
        {id:"a1",name:"Data and Statement Collection",type:"single",assignee:"Client"},
        {id:"a2",name:"Account Application",type:"single",assignee:"Our Team"},
        {id:"a3",name:"Transfer Paperwork",type:"single",assignee:"Contra Firm"},
        {id:"a4",name:"Asset Delivery",type:"single",assignee:"Accepting Firm"},
      ]},
    { id:"t4", name:"Key Person Life Insurance", color:"#f43f5e", estDays:75, category:"Inventory",
      steps:[
        {id:"k1",name:"Data Collection & Proposal Delivery",type:"single",assignee:"Our Team"},
        {id:"k2",name:"Application Delivery",type:"single",assignee:"Our Team"},
        {id:"k3",name:"Application Review & IGO Status",type:"single",assignee:"Our Team"},
        {id:"k4",name:"Submission to Underwriting",type:"single",assignee:"Issuing Carrier"},
        {id:"k5",name:"Carrier Requirements",type:"group",assignee:"Issuing Carrier",children:[
          {id:"k5a",name:"Phone Interview",type:"action",assignee:"Client"},
          {id:"k5b",name:"Health Exam",type:"action",assignee:"Client"},
          {id:"k5c",name:"Medical Records",type:"document",assignee:"Issuing Carrier"},
        ]},
        {id:"k6",name:"Communication of Offer",type:"single",assignee:"Issuing Carrier"},
        {id:"k7",name:"Acceptance & Binding of Coverage",type:"single",assignee:"Client"},
        {id:"k8",name:"Policy Delivery",type:"single",assignee:"Issuing Carrier"},
      ]},
  ],
  clients: [],
  nextClientNum: 1,
  campaigns: [],
  nextCampaignNum: 1,
});

const uid = () => Math.random().toString(36).slice(2, 10);
const daysBetween = (d1, d2) => Math.floor((new Date(d2) - new Date(d1)) / 86400000);
const today = () => new Date().toISOString().slice(0, 10);

const getReviewHealth = (client) => {
  const freq = client.reviewFrequency || SEGMENT_CADENCE[client.segment] || "Annual";
  if (freq === "Never") return { status: "exempt", color: "#64748b", pct: 0, daysSince: null, daysUntil: null, freq, cycleDays: 0, label: "Exempt" };
  const cycleDays = CADENCE_DAYS[freq] || 365;
  if (!client.lastReview) return { status: "never", color: "#64748b", pct: 0, daysSince: null, daysUntil: null, freq, cycleDays, label: "Never reviewed" };
  const daysSince = daysBetween(client.lastReview, today());
  const daysUntil = cycleDays - daysSince;
  const pct = Math.min(daysSince / cycleDays, 1.5);
  if (daysUntil < 0) return { status: "overdue", color: "#f43f5e", pct, daysSince, daysUntil, freq, cycleDays, label: `${Math.abs(daysUntil)}d overdue` };
  if (daysUntil <= cycleDays * 0.2) return { status: "approaching", color: "#f59e0b", pct, daysSince, daysUntil, freq, cycleDays, label: `${daysUntil}d until due` };
  return { status: "healthy", color: "#22c55e", pct, daysSince, daysUntil, freq, cycleDays, label: `${daysUntil}d until due` };
};
const formatPhone = (v) => { const d = v.replace(/\D/g, "").slice(0, 10); if (d.length <= 3) return d; if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`; return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`; };
const formatCurrency = (v) => { const n = parseFloat(String(v).replace(/[^0-9.]/g, "")); if (isNaN(n)) return ""; return n.toLocaleString("en-US", { maximumFractionDigits: 0 }); };

// Migrate/normalize data shape (used on any loaded data, regardless of source)
const migrateData = (p) => {
  if (!p) return null;
  if (!p.segments) p.segments = ["Diamond","Platinum","Gold","Silver","Prospect"];
  if (!p.segments.includes("Prospect")) p.segments.push("Prospect");
  if (!p.occupations) p.occupations = ["Employed","Business Owner","Retired"];
  if (!p.settings) p.settings = { logoPath: "", logoData: "", theme: "dark", teamEmails: { primary: "", cc1: "", cc2: "" } };
  if (!p.settings.logoData) p.settings.logoData = "";
  if (!p.settings.theme) p.settings.theme = "dark";
  if (!p.campaigns) p.campaigns = [];
  if (!p.nextCampaignNum) p.nextCampaignNum = 1;
  if (!p.settings.teamEmails) p.settings.teamEmails = { primary: "", cc1: "", cc2: "" };
  if (p.settings.footerDisclosure === undefined) p.settings.footerDisclosure = "";
  p.clients = (p.clients || []).map(c => {
    const base = {
      relationshipName: "", segment: "Silver", occupation: "", phone: "", email: "", altEmail: "",
      dob1: "", name1: "", dob2: "", name2: "", anniversary: "", firmAnniversary: "", reviewFrequency: "",
      street: "", suite: "", city: "", state: "", zip: "",
      hobbies: "", debrief: "", dnaLinks: [], lastReview: "", lastNotes: "", activityLog: [], reminders: [],
      linkedAUM: "", fwd12Comp: "", archived: false, archiveReason: "", marketingOptOut: false, ...c,
    };
    base.relationshipName = c.relationshipName || c.refCode || "";
    base.workflows = (c.workflows || []).map(w => {
      const wBase = { wfStatus: "active", nickname: "", wfNotes: "", riskPremium: "", planningFees: "", f12Rev: w.f12 || "", ...w };
      wBase.transaction = w.transaction || w.aumNna || "";
      wBase.nna = w.nna || "";
      return wBase;
    });
    return base;
  });
  return p;
};

// Load from localStorage (fallback for local dev / migration)
const loadLocalData = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return migrateData(JSON.parse(raw));
  } catch (e) {}
  return null;
};

// Debounced API save — prevents rapid writes from hammering the API
let _saveTimer = null;
let _saveInstance = null;
let _saveAccount = null;
let _saveStatusCallback = null;
const setSaveAuth = (instance, account) => { _saveInstance = instance; _saveAccount = account; };
const setSaveStatusCallback = (cb) => { _saveStatusCallback = cb; };

const saveData = (d) => {
  // Always save to localStorage as local cache
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(d)); } catch(e) {}
  // Debounced API write
  if (_saveInstance && _saveAccount) {
    if (_saveStatusCallback) _saveStatusCallback("saving");
    clearTimeout(_saveTimer);
    _saveTimer = setTimeout(() => {
      apiWrite(_saveInstance, _saveAccount, d)
        .then(() => { if (_saveStatusCallback) { _saveStatusCallback("saved"); setTimeout(() => _saveStatusCallback(null), 2000); } })
        .catch(err => { console.error("API save failed:", err); if (_saveStatusCallback) { _saveStatusCallback("error"); setTimeout(() => _saveStatusCallback(null), 4000); } });
    }, 800);
  }
};

const PARTY_HUE_MAP = {};
const getPartyColor = (party) => {
  const h = ["#3b82f6","#f59e0b","#10b981","#14b8a6","#8b5cf6","#f43f5e","#06b6d4","#f97316","#ec4899","#84cc16","#6366f1","var(--text-secondary)"];
  if (!PARTY_HUE_MAP[party]) PARTY_HUE_MAP[party] = h[Object.keys(PARTY_HUE_MAP).length % h.length];
  return PARTY_HUE_MAP[party];
};
const assignPartyColors = (parties) => {
  const h = ["#3b82f6","#f59e0b","#10b981","#14b8a6","#8b5cf6","#f43f5e","#06b6d4","#f97316","#ec4899","#84cc16","#6366f1","var(--text-secondary)"];
  parties.forEach((p, i) => { PARTY_HUE_MAP[p] = h[i % h.length]; });
};

const calcProgress = (wf) => {
  let t = 0, d = 0;
  (wf.steps || []).forEach(s => {
    if (s.children && s.children.length) s.children.forEach(c => { t++; if (c.status === "complete") d++; });
    else if (!s.skipped) { t++; if (s.status === "complete") d++; }
  });
  return t === 0 ? 0 : d / t;
};

const getBlockedBy = (wf) => {
  for (const s of (wf.steps || [])) {
    if (s.children && s.children.length) { for (const c of s.children) if (c.status !== "complete" && !c.skipped) return c.assignee; }
    else if (s.status !== "complete" && !s.skipped) return s.assignee;
  }
  return null;
};

// ─── Dashboard logic: collect active Service + Inventory workflow cards ──────

const getActiveWorkflowCards = (data) => {
  const cards = [];
  const now = today();
  (data.clients || []).filter(c => !c.archived).forEach(client => {
    client.workflows.forEach(wf => {
      if (wf.wfStatus === "pending") return;
      const cat = wf.category || (data.templates.find(t => t.id === wf.templateId) || {}).category || "Onboarding";
      const prog = calcProgress(wf);
      if (prog >= 1) return; // completed, skip
      const elapsed = daysBetween(wf.startDate, now);
      const daysRemaining = wf.estDays - elapsed;
      const blocked = getBlockedBy(wf);
      const weight = SEGMENT_WEIGHT[client.segment] || 1;
      const outstanding = [];
      wf.steps.forEach(s => {
        if (s.children && s.children.length) {
          s.children.forEach(c => { if (c.status !== "complete" && !c.skipped) outstanding.push({ name: c.name, party: c.assignee, type: c.type }); });
        } else {
          if (s.status !== "complete" && !s.skipped) outstanding.push({ name: s.name, party: s.assignee, type: s.type });
        }
      });
      cards.push({
        wfId: wf.id, clientId: client.id, clientName: client.relationshipName,
        segment: client.segment, weight,
        wfName: wf.nickname || wf.name, wfTemplateName: wf.name, wfColor: wf.color, wfCategory: cat,
        progress: prog, blocked, elapsed, estDays: wf.estDays,
        daysRemaining, overdue: daysRemaining < 0, outstanding, startDate: wf.startDate,
        transaction: wf.transaction || "", nna: wf.nna || "", riskPremium: wf.riskPremium || "", planningFees: wf.planningFees || "", f12Rev: wf.f12Rev || "", wfNotes: wf.wfNotes || "",
      });
    });
  });
  cards.sort((a, b) => {
    if (a.overdue !== b.overdue) return a.overdue ? -1 : 1;
    if (a.weight !== b.weight) return b.weight - a.weight;
    return a.daysRemaining - b.daysRemaining;
  });
  return cards;
};

// ─── Micro Components ───────────────────────────────────────────────────────

const PB = ({ value, color = "#3b82f6", h = 6, label = false }) => (
  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
    <div style={{ flex:1, height:h, backgroundColor:"var(--border)", borderRadius:h/2, overflow:"hidden" }}>
      <div style={{ width:`${Math.round(value*100)}%`, height:"100%", backgroundColor:color, borderRadius:h/2, transition:"width 0.3s" }}/>
    </div>
    {label && <span style={{ fontSize:11, color:"var(--text-tertiary)", fontWeight:700, minWidth:36, fontFamily:"monospace" }}>{Math.round(value*100)}%</span>}
  </div>
);

const Dot = ({ status }) => {
  const c = { complete:"#22c55e", "in-progress":"#f59e0b", "not-started":"var(--text-tertiary)", skipped:"var(--text-muted)" };
  return <span style={{ display:"inline-block", width:10, height:10, borderRadius:"50%", backgroundColor:c[status]||"var(--text-tertiary)", flexShrink:0 }}/>;
};

const PBadge = ({ party, sm }) => {
  const c = getPartyColor(party);
  return <span style={{ display:"inline-flex", alignItems:"center", gap:3, fontSize:sm?9:10, fontWeight:700, padding:sm?"2px 6px":"3px 8px", borderRadius:4, backgroundColor:c+"18", color:c, border:`1px solid ${c}22`, whiteSpace:"nowrap" }}><span style={{ width:sm?4:5, height:sm?4:5, borderRadius:"50%", backgroundColor:c }}/>{party}</span>;
};

const SegBadge = ({ segment, active, onClick }) => {
  const c = SEGMENT_COLORS[segment] || "var(--text-secondary)";
  return <span onClick={onClick} style={{ display:"inline-block", fontSize:10, fontWeight:700, letterSpacing:"0.04em", padding:"3px 10px", borderRadius:4, backgroundColor:active?c+"33":c+"18", color:c, border:`1px solid ${active?c:c+"33"}`, cursor:onClick?"pointer":"default", transition:"all 0.15s" }}>{segment}</span>;
};

const Bdg = ({ children, color = "#3b82f6", outline }) => (
  <span style={{ display:"inline-block", fontSize:10, fontWeight:700, letterSpacing:"0.04em", textTransform:"uppercase", padding:"2px 7px", borderRadius:4, backgroundColor:outline?"transparent":color+"22", color, border:outline?`1px solid ${color}44`:"none" }}>{children}</span>
);

const Btn = ({ children, onClick, primary, small, style: s = {} }) => (
  <button onClick={onClick} style={{ padding:small?"5px 10px":"8px 16px", backgroundColor:primary?"#3b82f6":"var(--border)", color:primary?"#fff":"var(--text-primary)", border:primary?"none":"1px solid var(--border-hover)", borderRadius:6, fontSize:small?12:13, fontWeight:600, cursor:"pointer", ...s }}>{children}</button>
);

const Input = ({ value, onChange, placeholder, style: s = {}, type = "text" }) => (
  <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border-hover)", borderRadius:6, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", ...s }}/>
);

const Modal = ({ title, onClose, children, wide }) => (
  <div style={{ position:"fixed", inset:0, backgroundColor:"rgba(0,0,0,0.65)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }} onClick={onClose}>
    <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:12, padding:28, width:wide?680:480, maxHeight:"85vh", overflow:"auto" }} onClick={e => e.stopPropagation()}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:"var(--text-bright)" }}>{title}</h3>
        <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--text-secondary)", fontSize:18, cursor:"pointer" }}>✕</button>
      </div>
      {children}
    </div>
  </div>
);

const FieldRow = ({ label, children }) => (
  <div style={{ display:"flex", alignItems:"center", gap:12, padding:"6px 0" }}>
    <span style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em", minWidth:130, textAlign:"right" }}>{label}</span>
    <div style={{ flex:1 }}>{children}</div>
  </div>
);
const FieldInput = ({ value, onChange, placeholder, type = "text" }) => (
  <input type={type} value={value||""} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit" }}
    onFocus={e => e.target.style.borderColor="var(--border-hover)"} onBlur={e => e.target.style.borderColor="var(--border)"} />
);
const FieldSelect = ({ value, onChange, options, placeholder }) => (
  <select value={value||""} onChange={e => onChange(e.target.value)} style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", cursor:"pointer", fontFamily:"inherit" }}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => <option key={o} value={o}>{o}</option>)}
  </select>
);

const ReadField = ({ label, value, internal }) => (
  <div style={{ display:"flex", gap:8 }}>
    <span style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.04em", minWidth:90, textAlign:"right" }}>{label}</span>
    <span style={{ fontSize:13, color:internal?"#f59e0b":"var(--text-primary)" }}>{value}</span>
    {internal && <span style={{ fontSize:9, color:"var(--text-muted)", fontWeight:700 }}>INTERNAL</span>}
  </div>
);

// ─── Shared Styles ───────────────────────────────────────────────────────────
const labelStyle = { display:"block", fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6 };
const colHead = { fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" };
const selectFullStyle = { padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border-hover)", borderRadius:6, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", cursor:"pointer", fontFamily:"inherit" };
const dateInputStyle = { padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border-hover)", borderRadius:6, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit" };

// ─── New Client Modal ────────────────────────────────────────────────────────

const NewClientModal = ({ data, setData, onClose }) => {
  const [relationshipName, setRelationshipName] = useState("");
  const [segment, setSegment] = useState("Silver");
  const [occupation, setOccupation] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name1, setName1] = useState("");
  const [dob1, setDob1] = useState("");
  const [name2, setName2] = useState("");
  const [dob2, setDob2] = useState("");
  const [lead, setLead] = useState("Jason");
  const [folderPath, setFolderPath] = useState("");

  const create = () => {
    if (!relationshipName.trim()) return alert("Relationship Name is required.");
    const num = String(data.nextClientNum).padStart(3, "0");
    const refCode = `HF-${new Date().getFullYear()}-${num}`;
    const newClient = { id:uid(), refCode, lead, folderPath, workflows:[], createdDate:today(),
      relationshipName:relationshipName.trim(), segment, occupation, phone:formatPhone(phone), email,
      altEmail:"", name1:name1.trim(), dob1, name2:name2.trim(), dob2, anniversary:"", firmAnniversary:"",
      street:"", suite:"", city:"", state:"", zip:"",
      reviewFrequency:SEGMENT_CADENCE[segment]||"Annual", hobbies:"", debrief:"", dnaLinks:[], lastReview:"", lastNotes:"",
      activityLog:[], reminders:[], linkedAUM:"", fwd12Comp:"", archived:false, archiveReason:"", marketingOptOut:false };
    const updated = { ...data, clients:[...data.clients, newClient], nextClientNum:data.nextClientNum+1 };
    setData(updated); saveData(updated); onClose();
  };

  return (
    <Modal title="New Relationship" onClose={onClose} wide>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={labelStyle}>Relationship Name *</label>
          <Input value={relationshipName} onChange={setRelationshipName} placeholder='e.g. "Jason & Sarah Harmon" or "Harmon Industries, LLC"'/>
          <p style={{ fontSize:11, color:"var(--text-muted)", margin:"4px 0 0" }}>Individual, household, or entity</p>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Segment</label><select value={segment} onChange={e=>setSegment(e.target.value)} style={selectFullStyle}>{(data.segments||[]).map(s=><option key={s} value={s}>{s}</option>)}</select></div>
          <div><label style={labelStyle}>Occupation</label><select value={occupation} onChange={e=>setOccupation(e.target.value)} style={selectFullStyle}><option value="">Select...</option>{(data.occupations||[]).map(o=><option key={o} value={o}>{o}</option>)}</select></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Primary Name</label><Input value={name1} onChange={setName1} placeholder="First and last"/></div>
          <div><label style={labelStyle}>Primary DoB</label><Input type="date" value={dob1} onChange={setDob1}/></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Spouse / Partner</label><Input value={name2} onChange={setName2} placeholder="Optional"/></div>
          <div><label style={labelStyle}>Spouse / Partner DoB</label><Input type="date" value={dob2} onChange={setDob2}/></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Phone</label><Input value={phone} onChange={v=>setPhone(formatPhone(v))} placeholder="(555) 123-4567"/></div>
          <div><label style={labelStyle}>Email</label><Input value={email} onChange={setEmail} placeholder="email@example.com"/></div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Lead Advisor</label><Input value={lead} onChange={setLead}/></div>
          <div><label style={labelStyle}>Folder Path</label><Input value={folderPath} onChange={setFolderPath} placeholder="OneDrive path..."/></div>
        </div>
        <p style={{ fontSize:12, color:"var(--text-muted)", margin:"4px 0 0", fontStyle:"italic" }}>Workflows and inventory items are added after creation from the relationship detail view.</p>
        <Btn primary onClick={create} style={{ marginTop:8 }}>Create Relationship</Btn>
      </div>
    </Modal>
  );
};

// ─── Step Override Panel ─────────────────────────────────────────────────────

const StepOverridePanel = ({ step, onUpdate, parties, onClose }) => {
  const [status, setStatus] = useState(step.status);
  const [date, setDate] = useState(step.date || "");
  const [notes, setNotes] = useState(step.notes || "");
  const [assignee, setAssignee] = useState(step.assignee);
  const [skipped, setSkipped] = useState(step.skipped || false);
  const save = () => { onUpdate({ ...step, status, date:date||null, notes, assignee, skipped }); onClose(); };

  return (
    <Modal title={`Override: ${step.name}`} onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={labelStyle}>Status</label>
          <div style={{ display:"flex", gap:8 }}>
            {["not-started","in-progress","complete"].map(s => (
              <button key={s} onClick={()=>{ setStatus(s); if(s==="complete"&&!date)setDate(today()); }} style={{ flex:1, padding:"8px 12px", borderRadius:6, border:status===s?`2px solid ${s==="complete"?"#22c55e":s==="in-progress"?"#f59e0b":"var(--text-secondary)"}`:"1px solid var(--border-hover)", backgroundColor:status===s?"var(--border)":"var(--bg-base)", color:"var(--text-primary)", fontSize:12, fontWeight:600, cursor:"pointer", textTransform:"capitalize" }}>
                <Dot status={s}/> <span style={{ marginLeft:6 }}>{s.replace("-"," ")}</span>
              </button>
            ))}
          </div>
        </div>
        <label style={{ ...labelStyle, display:"flex", alignItems:"center", gap:8 }}><input type="checkbox" checked={skipped} onChange={e=>setSkipped(e.target.checked)} style={{ accentColor:"var(--text-secondary)" }}/> Skip (N/A)</label>
        <div><label style={labelStyle}>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={dateInputStyle}/></div>
        <div><label style={labelStyle}>Assigned To</label><select value={assignee} onChange={e=>setAssignee(e.target.value)} style={selectFullStyle}>{parties.map(p=><option key={p} value={p}>{p}</option>)}</select></div>
        <div><label style={labelStyle}>Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Optional..." style={{ padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border-hover)", borderRadius:6, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/></div>
        <div style={{ display:"flex", gap:8, marginTop:4 }}><Btn onClick={onClose} style={{ flex:1 }}>Cancel</Btn><Btn primary onClick={save} style={{ flex:1 }}>Save</Btn></div>
      </div>
    </Modal>
  );
};

// ─── Activity Log Modal ─────────────────────────────────────────────────────

const ActivityLogModal = ({ client, onSave, onClose }) => {
  const [type, setType] = useState("Telephone Call");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [handler, setHandler] = useState("Jason");
  const [isReview, setIsReview] = useState(false);
  const save = () => { if(!note.trim())return; onSave({ id:uid(), type, date, note:note.trim(), handler, isReview, createdAt:new Date().toISOString() }); onClose(); };

  const types = ["Correspondence","Telephone Call","Video Conference","In-Person"];

  return (
    <Modal title={`Log Activity — ${client.relationshipName}`} onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={labelStyle}>Activity Type</label>
          <div style={{ display:"flex", gap:8 }}>
            {types.map(t => (
              <button key={t} onClick={()=>setType(t)} style={{ flex:1, padding:"8px 12px", borderRadius:6, border:type===t?"2px solid #3b82f6":"1px solid var(--border-hover)", backgroundColor:type===t?"var(--border)":"var(--bg-base)", color:"var(--text-primary)", fontSize:12, fontWeight:600, cursor:"pointer" }}>{t}</button>
            ))}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={dateInputStyle}/></div>
          <div><label style={labelStyle}>Handled By</label><Input value={handler} onChange={setHandler}/></div>
        </div>
        <div><label style={labelStyle}>Note</label><textarea value={note} onChange={e=>setNote(e.target.value)} rows={4} placeholder="What was discussed..." style={{ padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border-hover)", borderRadius:6, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/></div>
        {/* Review toggle */}
        <div style={{ padding:"12px 14px", backgroundColor:isReview?"#22c55e0a":"var(--bg-base)", border:isReview?"2px solid #22c55e":"1px solid var(--border)", borderRadius:6, cursor:"pointer", transition:"all 0.15s" }} onClick={()=>setIsReview(!isReview)}>
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
            <input type="checkbox" checked={isReview} onChange={e=>setIsReview(e.target.checked)} style={{ accentColor:"#22c55e", width:18, height:18 }}/>
            <div>
              <span style={{ fontSize:13, fontWeight:700, color:isReview?"#22c55e":"var(--text-secondary)" }}>Counts as Review</span>
              <p style={{ margin:"2px 0 0", fontSize:11, color:"var(--text-muted)" }}>Resets the review compliance clock for this relationship</p>
            </div>
          </label>
        </div>
        <Btn primary onClick={save}>Log Activity</Btn>
      </div>
    </Modal>
  );
};

const EditActivityModal = ({ entry, onSave, onDelete, onClose }) => {
  const [type, setType] = useState(entry.type || "Telephone Call");
  const [date, setDate] = useState(entry.date || today());
  const [note, setNote] = useState(entry.note || "");
  const [handler, setHandler] = useState(entry.handler || "Jason");
  const [isReview, setIsReview] = useState(entry.isReview || false);
  const save = () => { if(!note.trim())return; onSave({ ...entry, type, date, note:note.trim(), handler, isReview }); };

  const types = ["Correspondence","Telephone Call","Video Conference","In-Person"];

  return (
    <Modal title="Edit Activity" onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={labelStyle}>Activity Type</label>
          <div style={{ display:"flex", gap:8 }}>
            {types.map(t => (
              <button key={t} onClick={()=>setType(t)} style={{ flex:1, padding:"8px 12px", borderRadius:6, border:type===t?"2px solid #3b82f6":"1px solid var(--border-hover)", backgroundColor:type===t?"var(--border)":"var(--bg-base)", color:"var(--text-primary)", fontSize:12, fontWeight:600, cursor:"pointer" }}>{t}</button>
            ))}
          </div>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Date</label><input type="date" value={date} onChange={e=>setDate(e.target.value)} style={dateInputStyle}/></div>
          <div><label style={labelStyle}>Handled By</label><Input value={handler} onChange={setHandler}/></div>
        </div>
        <div><label style={labelStyle}>Note</label><textarea value={note} onChange={e=>setNote(e.target.value)} rows={4} style={{ padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border-hover)", borderRadius:6, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/></div>
        <div style={{ padding:"12px 14px", backgroundColor:isReview?"#22c55e0a":"var(--bg-base)", border:isReview?"2px solid #22c55e":"1px solid var(--border)", borderRadius:6, cursor:"pointer", transition:"all 0.15s" }} onClick={()=>setIsReview(!isReview)}>
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
            <input type="checkbox" checked={isReview} onChange={e=>setIsReview(e.target.checked)} style={{ accentColor:"#22c55e", width:18, height:18 }}/>
            <div>
              <span style={{ fontSize:13, fontWeight:700, color:isReview?"#22c55e":"var(--text-secondary)" }}>Counts as Review</span>
              <p style={{ margin:"2px 0 0", fontSize:11, color:"var(--text-muted)" }}>Resets the review compliance clock for this relationship</p>
            </div>
          </label>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn primary onClick={save} style={{ flex:1 }}>Save Changes</Btn>
          <Btn onClick={()=>onDelete(entry.id)} style={{ borderColor:"#f43f5e44", color:"#f43f5e" }}>Delete</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ─── Helper: instantiate a workflow from a template ─────────────────────────

const instantiateWorkflow = (tmpl, startDate, wfStatus = "active", nickname = "", wfNotes = "") => ({
  id: uid(), templateId: tmpl.id, name: tmpl.name, color: tmpl.color,
  estDays: tmpl.estDays, category: tmpl.category || "Onboarding",
  startDate, wfStatus, nickname, wfNotes,
  aumNna: "", transaction: "", nna: "", riskPremium: "", planningFees: "", f12Rev: "",
  steps: tmpl.steps.map(s => ({
    ...s, id: uid(), status: "not-started", date: null, notes: "", skipped: false,
    children: s.children ? s.children.map(c => ({
      ...c, id: uid(), status: "not-started", date: null, notes: "", skipped: false,
    })) : undefined,
  })),
});

// ─── Start Workflow Modal (Onboarding / Service) ────────────────────────────

const StartWorkflowModal = ({ data, client, onAdd, onClose }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [nickname, setNickname] = useState("");
  const [notes, setNotes] = useState("");
  const available = data.templates;

  const start = () => {
    if (!selectedId) return;
    const tmpl = data.templates.find(t => t.id === selectedId);
    onAdd(instantiateWorkflow(tmpl, today(), "active", nickname.trim(), notes.trim()));
    onClose();
  };

  return (
    <Modal title="Start Workflow" onClose={onClose} wide>
      <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "0 0 16px" }}>Select a template to begin tracking. Give it a nickname to distinguish it from other instances.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
        {available.map(t => {
          const sel = selectedId === t.id;
          return (
            <div key={t.id} onClick={() => setSelectedId(sel ? null : t.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                backgroundColor: sel ? "var(--border)" : "var(--bg-base)", borderRadius: 6,
                border: sel ? `2px solid ${t.color}` : "1px solid var(--border)",
                cursor: "pointer" }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: t.color }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary)", fontWeight: sel ? 700 : 400 }}>{t.name}</span>
              <Bdg color="var(--text-muted)" outline>{t.category || "Onboarding"}</Bdg>
              <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{t.estDays}d · {t.steps.length} steps</span>
            </div>
          );
        })}
      </div>
      {selectedId && (
        <div style={{ padding: 16, backgroundColor: "var(--bg-base)", borderRadius: 6, border: "1px solid var(--border)", marginBottom: 16 }}>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Nickname (optional)</label>
            <Input value={nickname} onChange={setNickname} placeholder='e.g. "Harmon 401k Rollover" or "Smith Annuity"'/>
            <p style={{ fontSize: 11, color: "var(--text-muted)", margin: "4px 0 0" }}>Displayed on dashboard cards. Defaults to template name if blank.</p>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional context..." style={{ padding: "6px 10px", backgroundColor: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text-primary)", fontSize: 13, outline: "none", width: "100%", fontFamily: "inherit", resize: "vertical" }} />
          </div>
        </div>
      )}
      {available.length === 0 && <p style={{ fontSize: 13, color: "var(--text-muted)" }}>No templates defined. Create one in the Templates tab.</p>}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn onClick={onClose} style={{ flex: 1 }}>Cancel</Btn>
        <Btn primary onClick={start} style={{ flex: 1 }}>Start Workflow</Btn>
      </div>
    </Modal>
  );
};

// ─── Add Inventory Item Modal (delayed template) ────────────────────────────

const AddInventoryModal = ({ data, client, onAdd, onClose }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [startNow, setStartNow] = useState(false);
  const [scheduledDate, setScheduledDate] = useState("");
  const [caseName, setCaseName] = useState("");
  const [transaction, setTransaction] = useState("");
  const [nna, setNna] = useState("");
  const [riskPremium, setRiskPremium] = useState("");
  const [planningFees, setPlanningFees] = useState("");
  const [f12Rev, setF12Rev] = useState("");
  const available = data.templates;

  const add = () => {
    if (!selectedId) return;
    const tmpl = data.templates.find(t => t.id === selectedId);
    const status = startNow ? "active" : "pending";
    const sd = startNow ? today() : (scheduledDate || "");
    const wf = instantiateWorkflow(tmpl, sd, status, caseName.trim(), "");
    wf.category = "Inventory";
    wf.transaction = transaction; wf.nna = nna; wf.riskPremium = riskPremium; wf.planningFees = planningFees; wf.f12Rev = f12Rev;
    if (!startNow) wf.scheduledDate = scheduledDate || "";
    onAdd(wf);
    onClose();
  };

  return (
    <Modal title="Add Inventory Item" onClose={onClose} wide>
      <p style={{ fontSize: 12, color: "var(--text-tertiary)", margin: "0 0 16px" }}>Select a template and enter the case details.</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
        {available.map(t => {
          const sel = selectedId === t.id;
          return (
            <div key={t.id} onClick={() => setSelectedId(sel ? null : t.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px",
                backgroundColor: sel ? "var(--border)" : "var(--bg-base)", borderRadius: 6,
                border: sel ? `2px solid ${t.color}` : "1px solid var(--border)", cursor: "pointer" }}>
              <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: t.color }} />
              <span style={{ flex: 1, fontSize: 13, color: "var(--text-primary)", fontWeight: sel ? 700 : 400 }}>{t.name}</span>
              <Bdg color="var(--text-muted)" outline>{t.category || "Onboarding"}</Bdg>
              <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>{t.estDays}d · {t.steps.length} steps</span>
            </div>
          );
        })}
      </div>
      {available.length === 0 && <p style={{ fontSize: 13, color: "var(--text-muted)" }}>No templates defined. Create one in the Templates tab.</p>}
      {selectedId && (
        <div style={{ padding: 16, backgroundColor: "var(--bg-base)", borderRadius: 6, border: "1px solid var(--border)", marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <label style={labelStyle}>Case Name / Nickname *</label>
              <Input value={caseName} onChange={setCaseName} placeholder='e.g. "Smith 401k Rollover" or "Jones Term Life 2M"'/>
            </div>
            <div>
              <label style={labelStyle}>Transaction</label>
              <Input value={transaction} onChange={setTransaction} placeholder="e.g. 500000"/>
            </div>
            <div>
              <label style={labelStyle}>NNA (Net New Assets)</label>
              <Input value={nna} onChange={setNna} placeholder="e.g. 250000 or -50000"/>
            </div>
            <div>
              <label style={labelStyle}>Risk Mgmt Premium</label>
              <Input value={riskPremium} onChange={setRiskPremium} placeholder="e.g. 3200"/>
            </div>
            <div>
              <label style={labelStyle}>Planning Fees</label>
              <Input value={planningFees} onChange={setPlanningFees} placeholder="e.g. 5000"/>
            </div>
            <div>
              <label style={labelStyle}>F12 Revenue</label>
              <Input value={f12Rev} onChange={setF12Rev} placeholder="e.g. 12000"/>
            </div>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={() => setStartNow(true)} style={{ flex: 1, padding: "10px 12px", borderRadius: 6, border: startNow ? "2px solid #22c55e" : "1px solid var(--border-hover)", backgroundColor: startNow ? "var(--border)" : "var(--bg-base)", color: "var(--text-primary)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              <span style={{ color: "#22c55e" }}>●</span> Start Now
            </button>
            <button onClick={() => setStartNow(false)} style={{ flex: 1, padding: "10px 12px", borderRadius: 6, border: !startNow ? "2px solid #f59e0b" : "1px solid var(--border-hover)", backgroundColor: !startNow ? "var(--border)" : "var(--bg-base)", color: "var(--text-primary)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              <span style={{ color: "#f59e0b" }}>●</span> Queue for Later
            </button>
          </div>
          {!startNow && (
            <div style={{ marginTop: 10 }}>
              <label style={labelStyle}>Target Start Date (optional)</label>
              <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} style={dateInputStyle} />
            </div>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn onClick={onClose} style={{ flex: 1 }}>Cancel</Btn>
        <Btn primary onClick={add} style={{ flex: 1 }}>{startNow ? "Start Item" : "Queue Item"}</Btn>
      </div>
    </Modal>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// DASHBOARD — Priority Activity View
// ═════════════════════════════════════════════════════════════════════════════

const Dashboard = ({ data, onSelectClient }) => {
  const cards = getActiveWorkflowCards(data);
  const overdueCards = cards.filter(c => c.overdue);
  const activeCards = cards.filter(c => !c.overdue);

  const WfCard = ({ card }) => {
    const isOD = card.overdue;
    const borderColor = isOD ? "#f43f5e55" : `${card.wfColor}33`;
    const timeLabel = isOD ? `Overdue by ${Math.abs(card.daysRemaining)}d` : card.daysRemaining === 0 ? "Due today" : `${card.daysRemaining}d remaining`;

    return (
      <div onClick={() => onSelectClient(card.clientId)}
        style={{ backgroundColor:"var(--bg-card)", border:`1px solid ${borderColor}`, borderRadius:8, padding:18, cursor:"pointer", transition:"border-color 0.15s", borderLeft:`4px solid ${card.wfColor}` }}
        onMouseEnter={e => e.currentTarget.style.borderColor = isOD ? "#f43f5e" : card.wfColor}
        onMouseLeave={e => e.currentTarget.style.borderColor = borderColor}>
        {/* Header */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
          <div>
            <div style={{ fontWeight:700, color:card.wfColor, fontSize:15, marginBottom:2 }}>{card.wfName}</div>
            {card.wfName !== card.wfTemplateName && <div style={{ fontSize:11, color:"var(--text-muted)", marginBottom:4 }}>Template: {card.wfTemplateName}</div>}
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <Bdg color={card.wfColor}>{card.wfCategory}</Bdg>
              <span style={{ fontSize:11, color:"var(--text-secondary)" }}>for</span>
              <span style={{ fontSize:12, fontWeight:600, color:"var(--text-bright)" }}>{card.clientName}</span>
              <SegBadge segment={card.segment}/>
            </div>
          </div>
          <span style={{ fontSize:11, fontWeight:700, fontFamily:"monospace", color:isOD?"#f43f5e":"#f59e0b", backgroundColor:isOD?"#f43f5e14":"#f59e0b14", padding:"3px 8px", borderRadius:4, whiteSpace:"nowrap" }}>{timeLabel}</span>
        </div>
        {/* Notes */}
        {card.wfNotes && <p style={{ fontSize:12, color:"var(--text-tertiary)", margin:"0 0 10px", lineHeight:1.4, fontStyle:"italic" }}>{card.wfNotes}</p>}
        {/* Progress */}
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
          <div style={{ flex:1 }}><PB value={card.progress} color={card.wfColor} h={6}/></div>
          <span style={{ fontSize:11, fontWeight:700, fontFamily:"monospace", color:"var(--text-tertiary)" }}>{Math.round(card.progress * 100)}%</span>
          <span style={{ fontSize:11, color:"var(--text-muted)" }}>{card.elapsed}d / {card.estDays}d</span>
        </div>
        {/* Blocked */}
        {card.blocked && (
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10 }}>
            <span style={{ fontSize:10, fontWeight:700, color:"#f43f5e", textTransform:"uppercase", letterSpacing:"0.06em" }}>Blocked by:</span>
            <PBadge party={card.blocked} sm/>
          </div>
        )}
        {/* Outstanding steps */}
        {card.outstanding.length > 0 && (
          <div style={{ borderTop:"1px solid var(--border)", paddingTop:10 }}>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:6, display:"block" }}>Outstanding ({card.outstanding.length})</span>
            <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:120, overflow:"auto" }}>
              {card.outstanding.slice(0, 6).map((step, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12 }}>
                  <Dot status="not-started"/>
                  <span style={{ flex:1, color:"var(--text-body)" }}>{step.name}</span>
                  <PBadge party={step.party} sm/>
                </div>
              ))}
              {card.outstanding.length > 6 && <span style={{ fontSize:11, color:"var(--text-muted)", fontStyle:"italic" }}>+{card.outstanding.length - 6} more</span>}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Dashboard</h2>
        <p style={{ margin:"4px 0 0", fontSize:13, color:"var(--text-secondary)" }}>Active workflow and inventory items across all relationships</p>
      </div>
      {/* Summary */}
      <div style={{ display:"flex", gap:16, marginBottom:24 }}>
        {[
          { label:"Overdue", val:overdueCards.length, color:overdueCards.length>0?"#f43f5e":"#22c55e" },
          { label:"Active Items", val:cards.length, color:"#3b82f6" },
        ].map((c,i) => (
          <div key={i} style={{ flex:1, padding:"14px 18px", backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em" }}>{c.label}</span>
            <div style={{ fontSize:24, fontWeight:800, color:c.color, fontFamily:"monospace", marginTop:2 }}>{c.val}</div>
          </div>
        ))}
      </div>
      {/* Overdue */}
      {overdueCards.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <h3 style={{ margin:"0 0 14px", fontSize:14, fontWeight:700, color:"#f43f5e", display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", backgroundColor:"#f43f5e" }}/>Overdue
          </h3>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {overdueCards.map(card => <WfCard key={card.wfId} card={card}/>)}
          </div>
        </div>
      )}
      {/* Active */}
      {activeCards.length > 0 && (
        <div style={{ marginBottom:28 }}>
          <h3 style={{ margin:"0 0 14px", fontSize:14, fontWeight:700, color:"#3b82f6", display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", backgroundColor:"#3b82f6" }}/>Active
          </h3>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {activeCards.map(card => <WfCard key={card.wfId} card={card}/>)}
          </div>
        </div>
      )}
      {cards.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--text-muted)" }}>
          <p style={{ fontSize:28, marginBottom:8 }}>✓</p>
          <p style={{ fontSize:15, color:"var(--text-tertiary)", marginBottom:4 }}>All clear</p>
          <p style={{ fontSize:13 }}>No active items. Start a workflow from any relationship card.</p>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// RELATIONSHIPS TAB — A-Z + Segment Filter + Contact Cards
// ═════════════════════════════════════════════════════════════════════════════

const RelationshipsTab = ({ data, setData, onSelectClient, letterFilter, setLetterFilter, segmentFilter, setSegmentFilter, search, setSearch }) => {
  const [wfModalClient, setWfModalClient] = useState(null);
  const [invModalClient, setInvModalClient] = useState(null);
  const activeClients = (data.clients || []).filter(c => !c.archived);
  let filtered = activeClients;
  if (letterFilter) filtered = filtered.filter(c => (c.relationshipName||"").toUpperCase().startsWith(letterFilter));
  if (segmentFilter) filtered = filtered.filter(c => c.segment === segmentFilter);
  if (search.trim()) {
    const q = search.toLowerCase().trim();
    filtered = filtered.filter(c =>
      (c.relationshipName||"").toLowerCase().includes(q) ||
      (c.name1||"").toLowerCase().includes(q) ||
      (c.name2||"").toLowerCase().includes(q) ||
      (c.email||"").toLowerCase().includes(q) ||
      (c.phone||"").toLowerCase().includes(q) ||
      (c.occupation||"").toLowerCase().includes(q)
    );
  }
  filtered.sort((a, b) => (a.relationshipName||"").localeCompare(b.relationshipName||""));
  const activeLetters = new Set(activeClients.map(c => (c.relationshipName||" ")[0].toUpperCase()));

  const deleteClient = (cid) => {
    if (!window.confirm("Delete this relationship?")) return;
    const updated = { ...data, clients:data.clients.filter(c=>c.id!==cid) };
    setData(updated); saveData(updated);
  };

  const addWorkflowToClient = (clientId, wf) => {
    const u = JSON.parse(JSON.stringify(data));
    u.clients.find(c => c.id === clientId).workflows.push(wf);
    setData(u); saveData(u);
  };

  return (
    <div>
      {wfModalClient && <StartWorkflowModal data={data} client={wfModalClient} onAdd={wf => { addWorkflowToClient(wfModalClient.id, wf); setWfModalClient(null); }} onClose={() => setWfModalClient(null)} />}
      {invModalClient && <AddInventoryModal data={data} client={invModalClient} onAdd={wf => { addWorkflowToClient(invModalClient.id, wf); setInvModalClient(null); }} onClose={() => setInvModalClient(null)} />}

      <div style={{ marginBottom:20 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Relationships</h2>
        <p style={{ margin:"4px 0 0", fontSize:13, color:"var(--text-secondary)" }}>{activeClients.length} total · {filtered.length} shown</p>
      </div>

      {/* A-Z row + Search */}
      <div style={{ display:"flex", gap:2, marginBottom:10, flexWrap:"wrap", alignItems:"center" }}>
        <button onClick={()=>setLetterFilter(null)} style={{ padding:"4px 10px", fontSize:11, fontWeight:letterFilter===null?800:500, color:letterFilter===null?"var(--text-bright)":"var(--text-muted)", backgroundColor:letterFilter===null?"#3b82f6":"transparent", border:letterFilter===null?"none":"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>All</button>
        {ALPHA.map(l => {
          const has = activeLetters.has(l);
          const act = letterFilter===l;
          return <button key={l} onClick={()=>has&&setLetterFilter(act?null:l)} style={{ padding:"4px 8px", fontSize:11, fontWeight:act?800:600, color:act?"var(--text-bright)":has?"var(--text-tertiary)":"var(--border)", backgroundColor:act?"#3b82f6":"transparent", border:act?"none":"1px solid transparent", borderRadius:4, cursor:has?"pointer":"default", minWidth:26, textAlign:"center" }}>{l}</button>;
        })}
        <div style={{ marginLeft:16, position:"relative", flex:"0 0 200px" }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search relationships..."
            style={{ padding:"5px 10px 5px 28px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:6, color:"var(--text-primary)", fontSize:12, outline:"none", width:"100%", fontFamily:"inherit" }}/>
          <span style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", fontSize:12, color:"var(--text-muted)", pointerEvents:"none" }}>🔍</span>
          {search && <button onClick={() => setSearch("")} style={{ position:"absolute", right:6, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"var(--text-muted)", fontSize:12, cursor:"pointer", padding:0 }}>✕</button>}
        </div>
      </div>

      {/* Segment row */}
      <div style={{ display:"flex", gap:6, marginBottom:24, alignItems:"center" }}>
        <button onClick={()=>setSegmentFilter(null)} style={{ padding:"4px 12px", fontSize:11, fontWeight:segmentFilter===null?700:500, color:segmentFilter===null?"var(--text-bright)":"var(--text-secondary)", backgroundColor:segmentFilter===null?"var(--border-hover)":"transparent", border:segmentFilter===null?"none":"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>All Segments</button>
        {(data.segments||[]).map(seg => <SegBadge key={seg} segment={seg} active={segmentFilter===seg} onClick={()=>setSegmentFilter(segmentFilter===seg?null:seg)}/>)}
      </div>

      {filtered.length === 0 && <div style={{ textAlign:"center", padding:"50px 20px", color:"var(--text-muted)" }}><p style={{ fontSize:15 }}>No relationships match current filters</p></div>}

      {/* Contact cards grid */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        {filtered.map(client => {
          const sc = SEGMENT_COLORS[client.segment] || "var(--text-secondary)";
          const activeWfs = client.workflows.filter(w=>(w.wfStatus||"active")==="active");
          const pendingWfs = client.workflows.filter(w=>w.wfStatus==="pending");
          const rh = getReviewHealth(client);
          return (
            <div key={client.id} style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, padding:18, transition:"border-color 0.15s", borderTop:`3px solid ${sc}44` }}
              onMouseEnter={e=>e.currentTarget.style.borderColor="var(--border-hover)"} onMouseLeave={e=>e.currentTarget.style.borderColor="var(--border)"}>
              {/* Clickable header area */}
              <div onClick={()=>onSelectClient(client.id)} style={{ cursor:"pointer" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                  <div>
                    <div style={{ fontWeight:700, color:"var(--text-bright)", fontSize:15, marginBottom:4 }}>{client.relationshipName}</div>
                    <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                      <SegBadge segment={client.segment}/>{client.occupation && <Bdg color="var(--text-secondary)" outline>{client.occupation}</Bdg>}
                      {client.marketingOptOut && <Bdg color="#f43f5e" outline>Opt-Out</Bdg>}
                    </div>
                  </div>
                  <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                    {/* Review health pill */}
                    <div title={`${rh.freq} review · ${rh.label}${rh.daysSince !== null ? ` · Last: ${rh.daysSince}d ago` : ""}`}
                      style={{ display:"flex", alignItems:"center", gap:5, padding:"3px 8px", borderRadius:4, backgroundColor:`${rh.color}14`, border:`1px solid ${rh.color}33` }}>
                      <span style={{ width:6, height:6, borderRadius:"50%", backgroundColor:rh.color }}/>
                      <span style={{ fontSize:10, fontWeight:700, color:rh.color, whiteSpace:"nowrap" }}>{rh.label}</span>
                    </div>
                    <button onClick={e=>{e.stopPropagation();deleteClient(client.id);}} style={{ background:"none", border:"1px solid var(--border-hover)", borderRadius:4, color:"var(--text-muted)", fontSize:11, padding:"3px 8px", cursor:"pointer" }}>✕</button>
                  </div>
                </div>
                {/* Review cycle bar */}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <div style={{ flex:1, height:3, backgroundColor:"var(--border)", borderRadius:2, overflow:"hidden" }}>
                    <div style={{ width:`${Math.min(rh.pct * 100, 100)}%`, height:"100%", backgroundColor:rh.color, borderRadius:2, transition:"width 0.3s" }}/>
                  </div>
                  <span style={{ fontSize:9, fontWeight:700, color:"var(--text-muted)", whiteSpace:"nowrap" }}>{rh.freq}</span>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:4, fontSize:12, color:"var(--text-tertiary)", marginBottom:10 }}>
                  {client.name1 && <div>Primary: {client.name1}</div>}
                  {client.name2 && <div>Spouse: {client.name2}</div>}
                  {client.phone && <div>{client.phone}</div>}
                  {client.email && <div style={{ color:"#60a5fa" }}>{client.email}</div>}
                </div>
                {/* DNA Links */}
                {client.dnaLinks&&client.dnaLinks.length>0 && (
                  <div style={{ display:"flex", gap:4, alignItems:"center", marginBottom:10, flexWrap:"wrap" }}>
                    <span style={{ fontSize:9, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Linked:</span>
                    {client.dnaLinks.map(lid => {
                      const lk = data.clients.find(c => c.id === lid);
                      if (!lk) return null;
                      const lkColor = SEGMENT_COLORS[lk.segment] || "var(--text-secondary)";
                      return (
                        <span key={lid} onClick={e => { e.stopPropagation(); onSelectClient(lid); }}
                          style={{ display:"inline-flex", alignItems:"center", gap:3, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, backgroundColor:`${lkColor}14`, color:lkColor, border:`1px solid ${lkColor}33`, cursor:"pointer" }}>
                          <span style={{ width:4, height:4, borderRadius:"50%", backgroundColor:lkColor }}/>{lk.relationshipName}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Workflow indicators */}
              {(activeWfs.length > 0 || pendingWfs.length > 0) && (
                <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:10, flexWrap:"wrap" }}>
                  {activeWfs.map(wf => (
                    <span key={wf.id} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, backgroundColor:wf.color+"18", color:wf.color, border:`1px solid ${wf.color}33` }}>
                      <span style={{ width:5, height:5, borderRadius:"50%", backgroundColor:wf.color }}/>{wf.nickname || wf.name}
                    </span>
                  ))}
                  {pendingWfs.map(wf => (
                    <span key={wf.id} style={{ display:"inline-flex", alignItems:"center", gap:4, fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, backgroundColor:"#f59e0b14", color:"#f59e0b", border:"1px solid #f59e0b33" }}>
                      ⏳ {wf.nickname || wf.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Action buttons + footer */}
              <div style={{ display:"flex", alignItems:"center", gap:6, paddingTop:10, borderTop:"1px solid var(--border)" }}>
                <button onClick={e=>{e.stopPropagation();setWfModalClient(client);}} style={{ padding:"4px 10px", fontSize:10, fontWeight:700, backgroundColor:"#10b981", color:"#fff", border:"none", borderRadius:4, cursor:"pointer" }}>+ Workflow</button>
                <button onClick={e=>{e.stopPropagation();setInvModalClient(client);}} style={{ padding:"4px 10px", fontSize:10, fontWeight:700, backgroundColor:"#f59e0b22", color:"#f59e0b", border:"1px solid #f59e0b44", borderRadius:4, cursor:"pointer" }}>+ Inventory</button>
                <span style={{ marginLeft:"auto", fontFamily:"monospace", fontSize:10, color:"var(--text-muted)" }}>{client.refCode}</span>
                <span style={{ fontSize:11, color:"var(--text-secondary)" }}>{client.lead}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// CONTACT CARD (in Client Detail)
// ═════════════════════════════════════════════════════════════════════════════

const ContactCard = ({ client, data, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...client });
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const save = () => { onUpdate(form); setEditing(false); };
  const cancel = () => { setForm({ ...client }); setEditing(false); };

  if (!editing) return (
    <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, padding:20, marginBottom:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:"var(--text-bright)" }}>{client.relationshipName}</h3>
          <SegBadge segment={client.segment}/>{client.occupation && <Bdg color="var(--text-secondary)" outline>{client.occupation}</Bdg>}
          {client.marketingOptOut && <Bdg color="#f43f5e" outline>Mktg Opt-Out</Bdg>}
        </div>
        <Btn small onClick={()=>setEditing(true)}>Edit Contact</Btn>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0, fontSize:13 }}>
        <div style={{ display:"flex", flexDirection:"column", gap:6, paddingRight:24, borderRight:"1px solid var(--border)18" }}>
          {client.name1 && <ReadField label="Primary" value={`${client.name1}${client.dob1?` · ${client.dob1}`:""}`}/>}
          {client.name2 && <ReadField label="Spouse" value={`${client.name2}${client.dob2?` · ${client.dob2}`:""}`}/>}
          {client.anniversary && <ReadField label="Anniversary" value={client.anniversary}/>}
          {client.firmAnniversary && <ReadField label="Firm Anniversary" value={client.firmAnniversary}/>}
          {client.phone ? (
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <ReadField label="Phone" value={client.phone}/>
              <button onClick={() => window.open(`zoomphonecall://${client.phone.replace(/\D/g,"")}`)} title="Call via Zoom Phone"
                style={{ background:"none", border:"1px solid #2D8CFF33", borderRadius:4, color:"#2D8CFF", fontSize:12, padding:"2px 6px", cursor:"pointer", flexShrink:0, lineHeight:1 }}>📞</button>
            </div>
          ) : <ReadField label="Phone" value="—"/>}
          <ReadField label="Email" value={client.email||"—"}/>
          {client.altEmail && <ReadField label="Alt Email" value={client.altEmail}/>}
          {(client.street || client.city) && <ReadField label="Address" value={[client.street, client.suite, [client.city, client.state, client.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ")}/>}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:6, paddingLeft:24 }}>
          {(() => {
            const rh = getReviewHealth(client);
            return (
              <div>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:4 }}>
                  <span style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Review Cycle</span>
                  <span style={{ fontSize:10, fontWeight:700, color:rh.color }}>{rh.label}</span>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
                  <div style={{ flex:1, height:6, backgroundColor:"var(--border)", borderRadius:3, overflow:"hidden" }}>
                    <div style={{ width:`${Math.min(rh.pct * 100, 100)}%`, height:"100%", backgroundColor:rh.color, borderRadius:3 }}/>
                  </div>
                  <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", whiteSpace:"nowrap" }}>{rh.freq}</span>
                </div>
                <div style={{ fontSize:11, color:"var(--text-tertiary)", marginTop:2 }}>
                  Last: {client.lastReview || "Never"}{rh.daysSince !== null && ` (${rh.daysSince}d ago)`}
                </div>
              </div>
            );
          })()}
          {client.hobbies && <ReadField label="Hobbies" value={client.hobbies}/>}
        </div>
      </div>
      {/* Debrief / Family Notes */}
      {client.debrief && (
        <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid var(--border)" }}>
          <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Debrief / Family Notes</span>
          <div style={{ fontSize:13, color:"var(--text-primary)", marginTop:4, lineHeight:1.5, whiteSpace:"pre-wrap" }}>{client.debrief}</div>
        </div>
      )}
      {client.dnaLinks&&client.dnaLinks.length>0 && (
        <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid var(--border)", display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Linked Relationships</span>
          {client.dnaLinks.map((lid) => {
            const lk = data.clients.find(c => c.id === lid);
            if (!lk) return null;
            const lkColor = SEGMENT_COLORS[lk.segment] || "var(--text-secondary)";
            return (
              <span key={lid} style={{ display:"inline-flex", alignItems:"center", gap:5, fontSize:12, fontWeight:600, padding:"3px 10px", borderRadius:6, backgroundColor:`${lkColor}14`, color:lkColor, border:`1px solid ${lkColor}33`, cursor:"pointer" }}
                title={`${lk.segment} · ${lk.occupation || ""}`}>
                <span style={{ width:5, height:5, borderRadius:"50%", backgroundColor:lkColor }}/>{lk.relationshipName}
              </span>
            );
          })}
        </div>
      )}
      {client.lastNotes && <div style={{ marginTop:12, paddingTop:12, borderTop:"1px solid var(--border)" }}><span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase" }}>Last Notes: </span><span style={{ fontSize:12, color:"var(--text-tertiary)" }}>{client.lastNotes}</span></div>}
    </div>
  );

  return (
    <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid #3b82f644", borderRadius:8, padding:20, marginBottom:20 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:"#3b82f6" }}>Editing Contact Card</h3>
        <div style={{ display:"flex", gap:8 }}><Btn small onClick={cancel}>Cancel</Btn><Btn small primary onClick={save}>Save</Btn></div>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <div style={{ gridColumn:"1/-1" }}><FieldRow label="Relationship Name"><FieldInput value={form.relationshipName} onChange={v=>set("relationshipName",v)}/></FieldRow></div>
        <FieldRow label="Segment"><FieldSelect value={form.segment} onChange={v=>set("segment",v)} options={data.segments||[]}/></FieldRow>
        <FieldRow label="Occupation"><FieldSelect value={form.occupation} onChange={v=>set("occupation",v)} options={data.occupations||[]} placeholder="Select..."/></FieldRow>
        <FieldRow label="Primary Name"><FieldInput value={form.name1} onChange={v=>set("name1",v)}/></FieldRow>
        <FieldRow label="Primary DoB"><FieldInput type="date" value={form.dob1} onChange={v=>set("dob1",v)}/></FieldRow>
        <FieldRow label="Spouse Name"><FieldInput value={form.name2} onChange={v=>set("name2",v)}/></FieldRow>
        <FieldRow label="Spouse DoB"><FieldInput type="date" value={form.dob2} onChange={v=>set("dob2",v)}/></FieldRow>
        <FieldRow label="Anniversary"><FieldInput type="date" value={form.anniversary} onChange={v=>set("anniversary",v)}/></FieldRow>
        <FieldRow label="Firm Anniversary"><FieldInput type="date" value={form.firmAnniversary} onChange={v=>set("firmAnniversary",v)}/></FieldRow>
        <FieldRow label="Review Freq"><FieldSelect value={form.reviewFrequency} onChange={v=>set("reviewFrequency",v)} options={["Monthly","Quarterly","Semi-annual","Annual","Never"]}/></FieldRow>
        <FieldRow label="Phone"><FieldInput value={form.phone} onChange={v=>set("phone",formatPhone(v))}/></FieldRow>
        <FieldRow label="Email"><FieldInput value={form.email} onChange={v=>set("email",v)}/></FieldRow>
        <FieldRow label="Alt Email"><FieldInput value={form.altEmail} onChange={v=>set("altEmail",v)}/></FieldRow>
        <FieldRow label="Lead"><FieldInput value={form.lead} onChange={v=>set("lead",v)}/></FieldRow>
        <div style={{ display:"flex", alignItems:"center", gap:8, paddingTop:12 }}>
          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:12, fontWeight:600, color:form.marketingOptOut?"#f43f5e":"var(--text-secondary)", cursor:"pointer" }}>
            <input type="checkbox" checked={form.marketingOptOut||false} onChange={e=>set("marketingOptOut",e.target.checked)} style={{ accentColor:"#f43f5e", width:16, height:16 }}/>
            Marketing Opt-Out
          </label>
        </div>
        {/* Address */}
        <div style={{ gridColumn:"1/-1", marginTop:4 }}><label style={{ ...labelStyle, fontSize:10, color:"var(--text-muted)" }}>Address</label></div>
        <div style={{ gridColumn:"1/-1" }}><FieldRow label="Street"><FieldInput value={form.street} onChange={v=>set("street",v)} placeholder="123 Main St"/></FieldRow></div>
        <FieldRow label="Suite / Unit"><FieldInput value={form.suite} onChange={v=>set("suite",v)} placeholder="Suite 200"/></FieldRow>
        <FieldRow label="City"><FieldInput value={form.city} onChange={v=>set("city",v)}/></FieldRow>
        <FieldRow label="State"><FieldInput value={form.state} onChange={v=>set("state",v.toUpperCase().slice(0,2))} placeholder="MO"/></FieldRow>
        <FieldRow label="ZIP"><FieldInput value={form.zip} onChange={v=>set("zip",v.replace(/\D/g,"").slice(0,5))} placeholder="63101"/></FieldRow>
        <div style={{ gridColumn:"1/-1" }}><FieldRow label="Hobbies"><textarea value={form.hobbies||""} onChange={e=>set("hobbies",e.target.value)} rows={2} style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/></FieldRow></div>
        <div style={{ gridColumn:"1/-1" }}><FieldRow label="Debrief / Family Notes"><textarea value={form.debrief||""} onChange={e=>set("debrief",e.target.value)} rows={4} placeholder="Children, extended family, important context..." style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/></FieldRow></div>
        {/* DNA Links editor */}
        <div style={{ gridColumn:"1/-1" }}>
          <label style={labelStyle}>Linked Relationships</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:8 }}>
            {(form.dnaLinks||[]).map(lid => {
              const lk = data.clients.find(c => c.id === lid);
              if (!lk) return null;
              return (
                <span key={lid} style={{ display:"inline-flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, padding:"4px 10px", borderRadius:6, backgroundColor:"#3b82f614", color:"#3b82f6", border:"1px solid #3b82f633" }}>
                  {lk.relationshipName}
                  <button onClick={() => set("dnaLinks", (form.dnaLinks||[]).filter(id => id !== lid))} style={{ background:"none", border:"none", color:"#3b82f6", fontSize:12, cursor:"pointer", padding:0, lineHeight:1 }}>✕</button>
                </span>
              );
            })}
            {(!form.dnaLinks || form.dnaLinks.length === 0) && <span style={{ fontSize:12, color:"var(--text-muted)", fontStyle:"italic" }}>No linked relationships</span>}
          </div>
          <select onChange={e => {
            if (!e.target.value) return;
            const links = [...(form.dnaLinks||[])];
            if (!links.includes(e.target.value)) links.push(e.target.value);
            set("dnaLinks", links);
            e.target.value = "";
          }} value="" style={selectFullStyle}>
            <option value="">+ Link a relationship...</option>
            {data.clients.filter(c => c.id !== client.id && !(form.dnaLinks||[]).includes(c.id) && !c.archived).sort((a,b) => a.relationshipName.localeCompare(b.relationshipName)).map(c => (
              <option key={c.id} value={c.id}>{c.relationshipName}</option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

// ─── Progress Report Generator ──────────────────────────────────────────────

const buildProgressReportHtml = (client, data, { forEmail = false } = {}) => {
  const logo = data.settings?.logoData || "";
  const activeWfs = client.workflows.filter(w => (w.wfStatus || "active") === "active");
  const now = today();

  // Collect "Items Needed From You" — steps assigned to Client that are incomplete
  const clientItems = [];
  activeWfs.forEach(wf => {
    wf.steps.forEach(s => {
      const check = (step) => {
        if (step.status !== "complete" && !step.skipped && (step.assignee || "").toLowerCase().includes("client")) {
          clientItems.push({ wfName: wf.nickname || wf.name, wfColor: wf.color, stepName: step.name });
        }
      };
      if (s.children && s.children.length) s.children.forEach(check);
      else check(s);
    });
  });

  const statusColor = (s) => s === "complete" ? "#16a34a" : s === "in-progress" ? "#d97706" : "#94a3b8";
  const statusLabel = (s, skipped) => skipped ? "Skipped" : s === "complete" ? "Complete" : s === "in-progress" ? "In Progress" : "Not Started";

  const wfSections = activeWfs.map(wf => {
    const prog = calcProgress(wf);
    const elapsed = daysBetween(wf.startDate, now);
    const pct = Math.round(prog * 100);

    const stepRows = [];
    wf.steps.forEach((s, i) => {
      const renderRow = (step, num) => `
        <tr style="border-bottom:1px solid #e2e8f0;">
          <td style="padding:8px 10px;font-size:12px;color:#475569;font-family:monospace;">${num}</td>
          <td style="padding:8px 10px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${statusColor(step.skipped?"skipped":step.status)};margin-right:6px;vertical-align:middle;"></span><span style="font-size:13px;color:${step.status==='complete'||step.skipped?'#94a3b8':'#1e293b'};${step.status==='complete'||step.skipped?'text-decoration:line-through;':''}">${step.name}</span></td>
          <td style="padding:8px 10px;font-size:12px;color:#475569;">${step.assignee||'—'}</td>
          <td style="padding:8px 10px;font-size:12px;color:#475569;text-align:center;"><span style="display:inline-block;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;background:${statusColor(step.skipped?'skipped':step.status)}22;color:${statusColor(step.skipped?'skipped':step.status)};">${statusLabel(step.status,step.skipped)}</span></td>
          <td style="padding:8px 10px;font-size:11px;color:#475569;">${step.date||'—'}</td>
        </tr>`;
      if (s.children && s.children.length) {
        stepRows.push(renderRow(s, String(i+1)));
        s.children.forEach((c, ci) => stepRows.push(renderRow(c, `${i+1}.${ci+1}`)));
      } else {
        stepRows.push(renderRow(s, String(i+1)));
      }
    });

    return `
      <div style="margin-bottom:32px;page-break-inside:avoid;">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px;">
          <span style="display:inline-block;width:12px;height:12px;border-radius:50%;background:${wf.color};"></span>
          <h3 style="margin:0;font-size:16px;font-weight:700;color:${wf.color};">${wf.nickname||wf.name}</h3>
          <span style="font-size:11px;font-weight:700;padding:2px 8px;border-radius:4px;background:${wf.color}18;color:${wf.color};text-transform:uppercase;">${wf.category||'Onboarding'}</span>
          ${wf.nickname && wf.nickname !== wf.name ? `<span style="font-size:11px;color:#94a3b8;">Template: ${wf.name}</span>` : ''}
        </div>
        ${wf.wfNotes ? `<p style="font-size:12px;color:#64748b;margin:0 0 12px;font-style:italic;">${wf.wfNotes}</p>` : ''}
        <div style="display:flex;align-items:center;gap:12px;margin-bottom:16px;">
          <div style="flex:1;height:10px;background:#e2e8f0;border-radius:5px;overflow:hidden;">
            <div style="width:${pct}%;height:100%;background:${wf.color};border-radius:5px;"></div>
          </div>
          <span style="font-size:13px;font-weight:700;color:${wf.color};font-family:monospace;">${pct}%</span>
          <span style="font-size:12px;color:#64748b;">${elapsed}d / ${wf.estDays}d</span>
        </div>
        <table style="width:100%;border-collapse:collapse;border:1px solid #e2e8f0;border-radius:6px;">
          <thead><tr style="background:#f8fafc;">
            <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;width:50px;">#</th>
            <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;">Step</th>
            <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;width:120px;">Assigned</th>
            <th style="padding:8px 10px;text-align:center;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;width:100px;">Status</th>
            <th style="padding:8px 10px;text-align:left;font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;width:90px;">Date</th>
          </tr></thead>
          <tbody>${stepRows.join('')}</tbody>
        </table>
      </div>`;
  }).join('');

  const clientItemsSection = clientItems.length > 0 ? `
    <div style="margin:32px 0;padding:20px;background:#fef3c7;border:2px solid #f59e0b;border-radius:8px;page-break-inside:avoid;">
      <h3 style="margin:0 0 12px;font-size:15px;font-weight:700;color:#92400e;">⚠ Items Needed From You</h3>
      <p style="font-size:12px;color:#78350f;margin:0 0 12px;">The following items require your action to keep things moving:</p>
      <ul style="margin:0;padding-left:20px;">${clientItems.map(ci =>
        `<li style="font-size:13px;color:#1e293b;margin-bottom:6px;"><strong>${ci.stepName}</strong> <span style="font-size:11px;color:#64748b;">— ${ci.wfName}</span></li>`
      ).join('')}</ul>
    </div>` : '';

  const html = `<!DOCTYPE html><html><head><title>Progress Report — ${client.relationshipName}</title>
    <style>
      * { box-sizing:border-box; margin:0; padding:0; }
      body { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif; color:#1e293b; padding:0; }
      @media print { body { padding:0; } .no-print { display:none !important; } @page { margin:0.6in; } }
    </style></head><body>
    ${forEmail ? '' : `<div class="no-print" style="padding:12px 24px;background:#f1f5f9;text-align:right;">
      <button onclick="window.print()" style="padding:8px 20px;background:#1e3a5f;color:#fff;border:none;border-radius:6px;font-size:13px;font-weight:600;cursor:pointer;">Print / Save as PDF</button>
    </div>`}
    <!-- Navy Header -->
    <div style="background:#1e3a5f;padding:28px 36px;display:flex;justify-content:space-between;align-items:center;">
      <div style="display:flex;align-items:center;gap:20px;">
        ${logo ? `<img src="${logo}" alt="Logo" style="max-height:48px;max-width:180px;" onerror="this.style.display='none'"/>` : ''}
        <div>
          <h1 style="color:#fff;font-size:22px;font-weight:700;margin:0 0 4px;">Progress Report</h1>
          <p style="color:#94b8db;font-size:13px;margin:0;">${client.relationshipName}</p>
        </div>
      </div>
    </div>
    <!-- Client Info Bar -->
    <div style="padding:16px 36px;background:#f8fafc;border-bottom:1px solid #e2e8f0;display:flex;gap:32px;flex-wrap:wrap;">
      <div><span style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;">Ref Code</span><div style="font-size:13px;font-weight:600;color:#1e293b;font-family:monospace;">${client.refCode}</div></div>
      <div><span style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;">Lead Advisor</span><div style="font-size:13px;font-weight:600;color:#1e293b;">${client.lead}</div></div>
      <div><span style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;">Report Date</span><div style="font-size:13px;font-weight:600;color:#1e293b;">${now}</div></div>
      ${client.name1 ? `<div><span style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;">Primary</span><div style="font-size:13px;font-weight:600;color:#1e293b;">${client.name1}</div></div>` : ''}
      ${client.name2 ? `<div><span style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:0.06em;">Spouse</span><div style="font-size:13px;font-weight:600;color:#1e293b;">${client.name2}</div></div>` : ''}
    </div>
    <!-- Body -->
    <div style="padding:28px 36px;">
      ${clientItemsSection}
      ${activeWfs.length > 0 ? `<h2 style="font-size:17px;font-weight:700;color:#1e293b;margin:0 0 20px;padding-bottom:10px;border-bottom:2px solid #e2e8f0;">Workflow Progress</h2>` : ''}
      ${wfSections}
      ${activeWfs.length === 0 ? '<p style="font-size:14px;color:#64748b;padding:40px 0;text-align:center;">No active workflows.</p>' : ''}
    </div>
    <!-- Footer -->
    <div style="padding:16px 36px;border-top:1px solid #e2e8f0;text-align:center;">
      <p style="font-size:11px;color:#94a3b8;">Generated by HarnessTrack · ${now} · Confidential</p>
    </div>
  </body></html>`;

  return html;
};

const generateProgressReport = (client, data) => {
  const html = buildProgressReportHtml(client, data);
  const w = window.open('', '_blank');
  if (w) { w.document.write(html); w.document.close(); }
  else alert('Please allow popups to generate the report.');
};

const emailProgressReport = (client, data) => {
  const emails = data.settings?.teamEmails || {};
  const clientEmail = client.email;
  if (!clientEmail) return alert("This relationship has no email address on file.");

  const cc = [emails.cc1, emails.cc2].filter(Boolean).join(", ");
  const dateStr = new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" });
  const subject = `Current Progress Report ${dateStr}`;
  const htmlBody = buildProgressReportHtml(client, data, { forEmail: true });

  let eml = "";
  eml += `From: ${emails.primary || clientEmail}\r\n`;
  eml += `To: ${clientEmail}\r\n`;
  if (cc) eml += `Cc: ${cc}\r\n`;
  eml += `Subject: ${subject}\r\n`;
  eml += `Date: ${new Date().toUTCString()}\r\n`;
  eml += `MIME-Version: 1.0\r\n`;
  eml += `X-Unsent: 1\r\n`;
  eml += `Content-Type: text/html; charset="UTF-8"\r\n`;
  eml += `Content-Transfer-Encoding: 7bit\r\n\r\n`;
  eml += htmlBody;

  const blob = new Blob([eml], { type: "message/rfc822" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Progress-Report-${client.relationshipName.replace(/[^a-zA-Z0-9]/g, "-")}-${today()}.eml`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Workflow Detail Panel (inline editing) ─────────────────────────────────

const WfDetailPanel = ({ wf, onUpdate, onClose }) => {
  const [nickname, setNickname] = useState(wf.nickname || "");
  const [wfNotes, setWfNotes] = useState(wf.wfNotes || "");
  const [transaction, setTransaction] = useState(wf.transaction || "");
  const [nna, setNna] = useState(wf.nna || "");
  const [riskPremium, setRiskPremium] = useState(wf.riskPremium || "");
  const [planningFees, setPlanningFees] = useState(wf.planningFees || "");
  const [f12Rev, setF12Rev] = useState(wf.f12Rev || "");

  const save = () => {
    onUpdate({ nickname: nickname.trim(), wfNotes: wfNotes.trim(), transaction, nna, riskPremium, planningFees, f12Rev });
    onClose();
  };

  const cat = wf.category || "Onboarding";
  const isInv = cat === "Inventory";

  return (
    <div style={{ marginBottom: 16, padding: 16, backgroundColor: "var(--bg-card)", borderRadius: 6, border: `1px solid ${wf.color}33` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <h4 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: wf.color }}>Workflow Details</h4>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn small onClick={onClose}>Cancel</Btn>
          <Btn small primary onClick={save}>Save</Btn>
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1/-1" }}>
          <label style={labelStyle}>{isInv ? "Case Name" : "Nickname"}</label>
          <Input value={nickname} onChange={setNickname} placeholder={`Defaults to "${wf.name}" if blank`} />
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <label style={labelStyle}>Notes</label>
          <textarea value={wfNotes} onChange={e => setWfNotes(e.target.value)} rows={2} placeholder="Context, details, special instructions..."
            style={{ padding: "6px 10px", backgroundColor: "var(--bg-base)", border: "1px solid var(--border)", borderRadius: 4, color: "var(--text-primary)", fontSize: 13, outline: "none", width: "100%", fontFamily: "inherit", resize: "vertical" }} />
        </div>
        {isInv && (<>
          <div>
            <label style={labelStyle}>Transaction</label>
            <Input value={transaction} onChange={setTransaction} placeholder="e.g. 500000" />
          </div>
          <div>
            <label style={labelStyle}>NNA (Net New Assets)</label>
            <Input value={nna} onChange={setNna} placeholder="e.g. 250000 or -50000" />
          </div>
          <div>
            <label style={labelStyle}>Risk Mgmt Premium</label>
            <Input value={riskPremium} onChange={setRiskPremium} placeholder="e.g. 3200" />
          </div>
          <div>
            <label style={labelStyle}>Planning Fees</label>
            <Input value={planningFees} onChange={setPlanningFees} placeholder="e.g. 5000" />
          </div>
          <div>
            <label style={labelStyle}>F12 Revenue</label>
            <Input value={f12Rev} onChange={setF12Rev} placeholder="e.g. 12000" />
          </div>
        </>)}
      </div>
      <div style={{ display: "flex", gap: 16, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Template: <span style={{ color: "var(--text-tertiary)" }}>{wf.name}</span></span>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Started: <span style={{ color: "var(--text-tertiary)" }}>{wf.startDate}</span></span>
        <span style={{ fontSize: 11, color: "var(--text-muted)" }}>Est: <span style={{ color: "var(--text-tertiary)" }}>{wf.estDays}d</span></span>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// CLIENT DETAIL
// ═════════════════════════════════════════════════════════════════════════════

const ClientDetail = ({ clientId, data, setData, onBack }) => {
  const client = data.clients.find(c => c.id === clientId);
  const [activeWf, setActiveWf] = useState(0);
  const [editingStep, setEditingStep] = useState(null);
  const [editFolder, setEditFolder] = useState(false);
  const [folderVal, setFolderVal] = useState(client?.folderPath || "");
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [showStartWf, setShowStartWf] = useState(false);
  const [showAddInv, setShowAddInv] = useState(false);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [editingWfDetail, setEditingWfDetail] = useState(null); // wfId being edited
  if (!client) return <div style={{ color:"var(--text-secondary)", padding:40 }}>Not found. <Btn onClick={onBack}>Back</Btn></div>;

  const activeWorkflows = client.workflows.filter(w => (w.wfStatus || "active") === "active");
  const pendingWorkflows = client.workflows.filter(w => w.wfStatus === "pending");
  const wf = activeWorkflows[activeWf];

  const updateStep = (wfIdx, stepIdx, childIdx, us) => {
    const realWf = activeWorkflows[wfIdx];
    const realIdx = client.workflows.findIndex(w => w.id === realWf.id);
    const u = JSON.parse(JSON.stringify(data)); const cl = u.clients.find(c=>c.id===clientId);
    if (childIdx!==undefined) {
      cl.workflows[realIdx].steps[stepIdx].children[childIdx]=us;
    } else {
      const ch = cl.workflows[realIdx].steps[stepIdx].children;
      cl.workflows[realIdx].steps[stepIdx]={...us,children:ch};
      // Cascade: if parent marked complete, complete all children too
      if (us.status === "complete" && ch && ch.length > 0) {
        cl.workflows[realIdx].steps[stepIdx].children = ch.map(c =>
          c.status !== "complete" && !c.skipped ? { ...c, status: "complete", date: c.date || us.date || today() } : c
        );
      }
    }
    setData(u); saveData(u);
  };
  const updateClient = (uf) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    const oldLinks = cl.dnaLinks || [];
    const newLinks = uf.dnaLinks || oldLinks;
    Object.assign(cl, uf);
    // Bidirectional DNA: sync linked relationships
    const added = newLinks.filter(id => !oldLinks.includes(id));
    const removed = oldLinks.filter(id => !newLinks.includes(id));
    added.forEach(lid => {
      const lk = u.clients.find(c => c.id === lid);
      if (lk) { if (!lk.dnaLinks) lk.dnaLinks = []; if (!lk.dnaLinks.includes(clientId)) lk.dnaLinks.push(clientId); }
    });
    removed.forEach(lid => {
      const lk = u.clients.find(c => c.id === lid);
      if (lk && lk.dnaLinks) lk.dnaLinks = lk.dnaLinks.filter(id => id !== clientId);
    });
    setData(u); saveData(u);
  };
  const addActivity = (entry) => { const u=JSON.parse(JSON.stringify(data)); const cl=u.clients.find(c=>c.id===clientId); cl.activityLog=[entry,...(cl.activityLog||[])]; cl.lastNotes=entry.note; if(entry.isReview) cl.lastReview=entry.date; setData(u); saveData(u); };
  const updateActivity = (updated) => {
    const u=JSON.parse(JSON.stringify(data)); const cl=u.clients.find(c=>c.id===clientId);
    cl.activityLog = (cl.activityLog||[]).map(e => e.id === updated.id ? updated : e);
    // Recalculate lastReview from all review entries
    const reviews = cl.activityLog.filter(e => e.isReview).sort((a,b) => b.date.localeCompare(a.date));
    cl.lastReview = reviews.length > 0 ? reviews[0].date : "";
    setData(u); saveData(u); setEditingActivity(null);
  };
  const deleteActivity = (entryId) => {
    if (!window.confirm("Delete this activity entry?")) return;
    const u=JSON.parse(JSON.stringify(data)); const cl=u.clients.find(c=>c.id===clientId);
    cl.activityLog = (cl.activityLog||[]).filter(e => e.id !== entryId);
    const reviews = cl.activityLog.filter(e => e.isReview).sort((a,b) => b.date.localeCompare(a.date));
    cl.lastReview = reviews.length > 0 ? reviews[0].date : "";
    setData(u); saveData(u); setEditingActivity(null);
  };
  const saveFolderPath = () => { updateClient({ folderPath:folderVal }); setEditFolder(false); };

  const updateWorkflowField = (wfId, fields) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    const w = cl.workflows.find(w => w.id === wfId);
    if (w) Object.assign(w, fields);
    setData(u); saveData(u);
  };

  const addWorkflow = (wf) => {
    const u = JSON.parse(JSON.stringify(data));
    u.clients.find(c => c.id === clientId).workflows.push(wf);
    setData(u); saveData(u);
  };

  const removeWorkflow = (wfId) => {
    if (!window.confirm("Remove this workflow from this relationship?")) return;
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    cl.workflows = cl.workflows.filter(w => w.id !== wfId);
    setData(u); saveData(u);
    setActiveWf(0);
  };

  const activateWorkflow = (wfId) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    const wf = cl.workflows.find(w => w.id === wfId);
    wf.wfStatus = "active";
    wf.startDate = today();
    setData(u); saveData(u);
  };

  const actIcons = { "Correspondence":"✉️", "Telephone Call":"📞", "Video Conference":"💻", "In-Person":"🤝", call:"📞", Zoom:"💻", "in-person":"🤝", email:"✉️" };

  return (
    <div>
      {editingStep && <StepOverridePanel step={editingStep.step} parties={data.parties} onUpdate={s=>updateStep(editingStep.wfIdx,editingStep.stepIdx,editingStep.childIdx,s)} onClose={()=>setEditingStep(null)}/>}
      {showActivityLog && <ActivityLogModal client={client} onSave={addActivity} onClose={()=>setShowActivityLog(false)}/>}
      {editingActivity && <EditActivityModal entry={editingActivity} onSave={updateActivity} onDelete={deleteActivity} onClose={()=>setEditingActivity(null)}/>}
      {showStartWf && <StartWorkflowModal data={data} client={client} onAdd={addWorkflow} onClose={()=>setShowStartWf(false)}/>}
      {showAddInv && <AddInventoryModal data={data} client={client} onAdd={addWorkflow} onClose={()=>setShowAddInv(false)}/>}
      {showAddReminder && <AddReminderModal data={data} clientId={clientId} onClose={()=>setShowAddReminder(false)} onSave={(r) => {
        const u=JSON.parse(JSON.stringify(data)); const cl=u.clients.find(c=>c.id===clientId);
        if(!cl.reminders) cl.reminders=[];
        const idx=cl.reminders.findIndex(x=>x.id===r.id);
        if(idx>=0) cl.reminders[idx]=r; else cl.reminders.push(r);
        setData(u); saveData(u);
      }}/>}

      <Btn onClick={onBack} small style={{ marginBottom:16 }}>← Back</Btn>
      <ContactCard client={client} data={data} onUpdate={updateClient}/>

      {/* Quick Actions */}
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
        <Btn small primary onClick={()=>setShowActivityLog(true)}>+ Log Activity</Btn>
        <Btn small onClick={()=>setShowStartWf(true)} style={{ backgroundColor:"#10b981", color:"#fff", border:"none" }}>+ Start Workflow</Btn>
        <Btn small onClick={()=>setShowAddInv(true)} style={{ backgroundColor:"#f59e0b22", color:"#f59e0b", border:"1px solid #f59e0b44" }}>+ Add Inventory Item</Btn>
        <Btn small onClick={()=>setShowAddReminder(true)} style={{ backgroundColor:"#a78bfa22", color:"#a78bfa", border:"1px solid #a78bfa44" }}>+ Reminder</Btn>
        <Btn small onClick={()=>generateProgressReport(client, data)} style={{ backgroundColor:"#1e3a5f", color:"#fff", border:"none" }}>📄 Progress Report</Btn>
        <Btn small onClick={()=>emailProgressReport(client, data)} style={{ backgroundColor:"#1e3a5f", color:"#fff", border:"none" }}>📧 Email Report</Btn>
      </div>

      {/* Activity History */}
      {client.activityLog&&client.activityLog.length>0 && (
        <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, padding:16, marginBottom:20 }}>
          <h4 style={{ margin:"0 0 12px", fontSize:13, fontWeight:700, color:"var(--text-tertiary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Activity History</h4>
          <div style={{ display:"flex", flexDirection:"column", gap:8, maxHeight:200, overflow:"auto" }}>
            {client.activityLog.slice(0,10).map(e => (
              <div key={e.id} style={{ display:"flex", gap:10, alignItems:"flex-start", padding:"8px 10px", backgroundColor:e.isReview?"#22c55e08":"var(--bg-base)", borderRadius:6, border:e.isReview?"1px solid #22c55e22":"1px solid transparent" }}>
                <span style={{ fontSize:16, lineHeight:1 }}>{actIcons[e.type]||"📋"}</span>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:3 }}>
                    <span style={{ fontSize:11, fontWeight:700, color:"var(--text-tertiary)" }}>{e.type}</span>
                    {e.isReview && <span style={{ fontSize:9, fontWeight:800, padding:"1px 6px", borderRadius:3, backgroundColor:"#22c55e22", color:"#22c55e", textTransform:"uppercase", letterSpacing:"0.06em" }}>Review</span>}
                    <span style={{ fontSize:11, color:"var(--text-muted)" }}>{e.date}</span>
                    <span style={{ fontSize:11, color:"var(--text-muted)" }}>by {e.handler}</span>
                  </div>
                  <p style={{ margin:0, fontSize:12, color:"var(--text-body)", lineHeight:1.4 }}>{e.note}</p>
                </div>
                <button onClick={()=>setEditingActivity(e)} style={{ background:"none", border:"none", color:"var(--text-muted)", fontSize:13, cursor:"pointer", padding:"2px 6px", borderRadius:4, flexShrink:0 }} title="Edit">✎</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Folder path */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20, padding:"10px 14px", backgroundColor:"var(--bg-card)", borderRadius:6, border:"1px solid var(--border)" }}>
        <span style={{ fontSize:12, color:"var(--text-secondary)", fontWeight:600 }}>📁 Upload Folder:</span>
        {editFolder ? (
          <div style={{ display:"flex", flex:1, gap:8 }}>
            <Input value={folderVal} onChange={setFolderVal} placeholder="Path..." style={{ flex:1 }}/>
            <Btn small primary onClick={saveFolderPath}>Save</Btn>
            <Btn small onClick={()=>{setEditFolder(false);setFolderVal(client.folderPath||"");}}>Cancel</Btn>
          </div>
        ) : (<><span style={{ flex:1, fontSize:12, fontFamily:"monospace", color:client.folderPath?"var(--text-primary)":"var(--text-muted)" }}>{client.folderPath||"No folder path set"}</span><Btn small onClick={()=>setEditFolder(true)}>Edit</Btn></>)}
      </div>

      {/* Pending Inventory Items */}
      {pendingWorkflows.length > 0 && (
        <div style={{ marginBottom:20, padding:16, backgroundColor:"var(--bg-card)", border:"1px solid #f59e0b33", borderRadius:8 }}>
          <h4 style={{ margin:"0 0 12px", fontSize:13, fontWeight:700, color:"#f59e0b", textTransform:"uppercase", letterSpacing:"0.06em", display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:8, height:8, borderRadius:"50%", backgroundColor:"#f59e0b" }}/>Pending Inventory Items
          </h4>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {pendingWorkflows.map(pw => (
              <div key={pw.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 14px", backgroundColor:"var(--bg-base)", borderRadius:6, border:"1px solid var(--border)" }}>
                <span style={{ width:10, height:10, borderRadius:"50%", backgroundColor:pw.color }}/>
                <span style={{ flex:1, fontSize:13, color:"var(--text-primary)", fontWeight:600 }}>{pw.nickname || pw.name}</span>
                <Bdg color={pw.color}>Inventory</Bdg>
                <span style={{ fontSize:11, color:"var(--text-secondary)" }}>{pw.estDays}d · {pw.steps.length} steps</span>
                {pw.scheduledDate && <span style={{ fontSize:11, color:"var(--text-muted)", fontFamily:"monospace" }}>Target: {pw.scheduledDate}</span>}
                <Bdg color="#f59e0b" outline>Pending</Bdg>
                <Btn small primary onClick={()=>activateWorkflow(pw.id)} style={{ backgroundColor:"#22c55e", fontSize:11, padding:"4px 10px" }}>Activate</Btn>
                <button onClick={()=>removeWorkflow(pw.id)} style={{ background:"none", border:"1px solid var(--border-hover)", borderRadius:4, color:"var(--text-muted)", fontSize:11, padding:"3px 8px", cursor:"pointer" }}>✕</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Workflows */}
      {activeWorkflows.length>0 && (<>
        <div style={{ display:"flex", alignItems:"center", gap:0, marginBottom:20, borderBottom:"1px solid var(--border)" }}>
          {activeWorkflows.map((w,i) => (
            <button key={w.id} onClick={()=>{setActiveWf(i);setEditingWfDetail(null);}} style={{ padding:"10px 18px", backgroundColor:"transparent", color:activeWf===i?w.color:"var(--text-secondary)", border:"none", borderBottom:activeWf===i?`2px solid ${w.color}`:"2px solid transparent", fontSize:13, fontWeight:activeWf===i?700:500, cursor:"pointer", display:"flex", alignItems:"center", gap:6 }}>
              {w.nickname || w.name}
              <Bdg color="var(--text-muted)" outline>{w.category||"Onboarding"}</Bdg>
            </button>
          ))}
        </div>
        {wf && (<>
          {/* Progress + actions row */}
          <div style={{ marginBottom:16, padding:"12px 16px", backgroundColor:"var(--bg-card)", borderRadius:6, border:"1px solid var(--border)", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1 }}><PB value={calcProgress(wf)} color={wf.color} h={8} label/></div>
            <span style={{ fontSize:12, color:"var(--text-secondary)" }}>{daysBetween(wf.startDate,today())}d / {wf.estDays}d</span>
            <button onClick={()=>setEditingWfDetail(editingWfDetail===wf.id?null:wf.id)} style={{ background:"none", border:`1px solid ${editingWfDetail===wf.id?wf.color+"66":"var(--border-hover)"}`, borderRadius:4, color:editingWfDetail===wf.id?wf.color:"var(--text-tertiary)", fontSize:10, padding:"3px 8px", cursor:"pointer" }}>Details</button>
            <button onClick={()=>removeWorkflow(wf.id)} style={{ background:"none", border:"1px solid #f43f5e33", borderRadius:4, color:"#f43f5e", fontSize:10, padding:"3px 8px", cursor:"pointer" }} title="Remove workflow">Remove</button>
          </div>

          {/* Inline workflow detail editor */}
          {editingWfDetail === wf.id && <WfDetailPanel wf={wf} onUpdate={fields => updateWorkflowField(wf.id, fields)} onClose={() => setEditingWfDetail(null)} />}

          <div style={{ display:"flex", alignItems:"center", gap:8, padding:"0 12px 8px", borderBottom:"1px solid var(--border)", marginBottom:4 }}>
            <span style={colHead}>#</span><span style={{ ...colHead, width:18 }}></span><span style={{ ...colHead, flex:1 }}>Step</span>
            <span style={{ ...colHead, minWidth:110, textAlign:"center" }}>Assigned</span><span style={{ ...colHead, minWidth:75, textAlign:"right" }}>Date</span><span style={{ ...colHead, minWidth:50, textAlign:"center" }}>Edit</span>
          </div>
          {wf.steps.map((step,sIdx) => <StepRowLive key={step.id} step={step} index={sIdx} wfIdx={activeWf} wfColor={wf.color} onEdit={childIdx=>setEditingStep({ wfIdx:activeWf, stepIdx:sIdx, childIdx, step:childIdx!==undefined?step.children[childIdx]:step })}/>)}
        </>)}
      </>)}

      {/* Empty state */}
      {activeWorkflows.length === 0 && pendingWorkflows.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", backgroundColor:"var(--bg-card)", borderRadius:8, border:"1px solid var(--border)" }}>
          <p style={{ fontSize:14, color:"var(--text-tertiary)", marginBottom:4 }}>No workflows or inventory items yet</p>
          <p style={{ fontSize:12, color:"var(--text-muted)", marginBottom:16 }}>Start a workflow or add an inventory item to begin tracking progress for this relationship.</p>
          <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
            <Btn small onClick={()=>setShowStartWf(true)} style={{ backgroundColor:"#10b981", color:"#fff", border:"none" }}>Start Workflow</Btn>
            <Btn small onClick={()=>setShowAddInv(true)} style={{ backgroundColor:"#f59e0b22", color:"#f59e0b", border:"1px solid #f59e0b44" }}>Add Inventory Item</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

const StepRowLive = ({ step, index, wfIdx, wfColor, onEdit }) => {
  const [expanded, setExpanded] = useState(step.status==="in-progress"||(step.children&&step.children.some(c=>c.status==="in-progress")));
  const hasKids = step.children&&step.children.length>0;
  const isDone = step.status==="complete"; const isSkipped = step.skipped;

  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 12px", backgroundColor:step.status==="in-progress"?"var(--border)":"var(--bg-card)", borderRadius:6, border:step.status==="in-progress"?`1px solid ${wfColor}33`:"1px solid transparent", opacity:isSkipped?0.4:1, cursor:hasKids?"pointer":"default" }}
        onClick={()=>hasKids&&setExpanded(!expanded)}>
        <span style={{ fontSize:11, color:"var(--text-muted)", fontWeight:700, minWidth:22, fontFamily:"monospace" }}>{String(index+1).padStart(2,"0")}</span>
        <Dot status={isSkipped?"skipped":step.status}/>
        <span style={{ flex:1, fontSize:13, color:isDone||isSkipped?"var(--text-secondary)":"var(--text-primary)", fontWeight:step.status==="in-progress"?600:400, textDecoration:isDone||isSkipped?"line-through":"none" }}>
          {step.name}{step.notes&&<span style={{ marginLeft:6, fontSize:10, color:"var(--text-muted)" }} title={step.notes}>💬</span>}
        </span>
        {step.conditional&&<Bdg color="#f59e0b" outline>IF: {step.conditionLabel}</Bdg>}
        {step.autoFolder&&<Bdg color="#8b5cf6" outline>Auto</Bdg>}
        {isSkipped&&<Bdg color="var(--text-muted)" outline>Skipped</Bdg>}
        <div style={{ minWidth:110, display:"flex", justifyContent:"center" }}><PBadge party={step.assignee} sm/></div>
        {hasKids&&<span style={{ fontSize:11, color:"var(--text-muted)" }}>{step.children.filter(c=>c.status==="complete").length}/{step.children.length}</span>}
        <span style={{ fontSize:11, color:"var(--text-muted)", minWidth:75, textAlign:"right" }}>{step.date||"—"}</span>
        <button onClick={e=>{e.stopPropagation();onEdit();}} style={{ background:"none", border:"1px solid var(--border-hover)", borderRadius:4, color:"var(--text-secondary)", fontSize:10, padding:"2px 8px", cursor:"pointer", minWidth:50, textAlign:"center" }}>Edit</button>
        {hasKids&&<span style={{ fontSize:10, color:"var(--text-muted)", transform:expanded?"rotate(180deg)":"rotate(0)", transition:"transform 0.2s" }}>▼</span>}
      </div>
      {hasKids&&expanded&&(
        <div style={{ marginLeft:44, borderLeft:`2px solid ${wfColor}33`, paddingLeft:14, marginTop:2, marginBottom:4 }}>
          {step.children.map((child,cIdx) => {
            const cD=child.status==="complete"; const cS=child.skipped;
            return (
              <div key={child.id} style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", fontSize:12, opacity:cS?0.4:1 }}>
                <Dot status={cS?"skipped":child.status}/>
                <span style={{ flex:1, color:cD||cS?"var(--text-muted)":"var(--text-body)", textDecoration:cD||cS?"line-through":"none" }}>{child.name}{child.notes&&<span style={{ marginLeft:4, fontSize:10, color:"var(--text-muted)" }} title={child.notes}>💬</span>}</span>
                {cS&&<Bdg color="var(--text-muted)" outline>Skip</Bdg>}
                <PBadge party={child.assignee} sm/>
                <span style={{ fontSize:11, color:"var(--text-muted)", minWidth:70, textAlign:"right" }}>{child.date||"—"}</span>
                <button onClick={()=>onEdit(cIdx)} style={{ background:"none", border:"1px solid var(--border-hover)", borderRadius:4, color:"var(--text-secondary)", fontSize:10, padding:"2px 8px", cursor:"pointer" }}>Edit</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TEMPLATE EDITOR
// ═════════════════════════════════════════════════════════════════════════════

const TemplateEditor = ({ data, setData }) => {
  const [selIdx, setSelIdx] = useState(0);
  const [showParties, setShowParties] = useState(false);
  const tmpl = data.templates[selIdx];
  const updateTmpl = (f, v) => { const u=JSON.parse(JSON.stringify(data)); u.templates[selIdx][f]=v; setData(u); saveData(u); };
  const updateStepAssignee = (sIdx, val, cIdx) => { const u=JSON.parse(JSON.stringify(data)); if(cIdx!==undefined) u.templates[selIdx].steps[sIdx].children[cIdx].assignee=val; else u.templates[selIdx].steps[sIdx].assignee=val; setData(u); saveData(u); };
  const updateParties = (np) => { const u={...data,parties:np}; setData(u); saveData(u); assignPartyColors(np); };

  const addTemplate = () => {
    const u = JSON.parse(JSON.stringify(data));
    const newTmpl = {
      id: uid(), name: "New Template", color: "#3b82f6", category: "Service", estDays: 30,
      steps: [{ id: uid(), name: "Step 1", type: "task", assignee: data.parties[0] || "Advisor" }],
    };
    u.templates.push(newTmpl);
    setData(u); saveData(u);
    setSelIdx(u.templates.length - 1);
  };

  const deleteTemplate = () => {
    if (data.templates.length <= 1) return alert("You must keep at least one template.");
    if (!window.confirm(`Delete template "${tmpl.name}"? This won't affect existing workflows.`)) return;
    const u = JSON.parse(JSON.stringify(data));
    u.templates.splice(selIdx, 1);
    setData(u); saveData(u);
    setSelIdx(Math.max(0, selIdx - 1));
  };

  const addStep = (type = "task") => {
    const u = JSON.parse(JSON.stringify(data));
    const newStep = { id: uid(), name: "New Step", type, assignee: data.parties[0] || "Advisor" };
    if (type === "group") newStep.children = [{ id: uid(), name: "Sub-step 1", type: "task", assignee: data.parties[0] || "Advisor" }];
    u.templates[selIdx].steps.push(newStep);
    setData(u); saveData(u);
  };
  const removeStep = (sIdx) => {
    const u = JSON.parse(JSON.stringify(data));
    u.templates[selIdx].steps.splice(sIdx, 1);
    setData(u); saveData(u);
  };
  const updateStepField = (sIdx, field, val, cIdx) => {
    const u = JSON.parse(JSON.stringify(data));
    if (cIdx !== undefined) u.templates[selIdx].steps[sIdx].children[cIdx][field] = val;
    else u.templates[selIdx].steps[sIdx][field] = val;
    setData(u); saveData(u);
  };
  const moveStep = (sIdx, dir) => {
    const nIdx = sIdx + dir;
    if (nIdx < 0 || nIdx >= data.templates[selIdx].steps.length) return;
    const u = JSON.parse(JSON.stringify(data));
    const steps = u.templates[selIdx].steps;
    [steps[sIdx], steps[nIdx]] = [steps[nIdx], steps[sIdx]];
    setData(u); saveData(u);
  };
  const addChild = (sIdx) => {
    const u = JSON.parse(JSON.stringify(data));
    const step = u.templates[selIdx].steps[sIdx];
    if (!step.children) step.children = [];
    step.type = "group";
    step.children.push({ id: uid(), name: "New Sub-step", type: "task", assignee: data.parties[0] || "Advisor" });
    setData(u); saveData(u);
  };
  const removeChild = (sIdx, cIdx) => {
    const u = JSON.parse(JSON.stringify(data));
    const step = u.templates[selIdx].steps[sIdx];
    step.children.splice(cIdx, 1);
    if (step.children.length === 0) { step.type = "task"; delete step.children; }
    setData(u); saveData(u);
  };
  const moveChild = (sIdx, cIdx, dir) => {
    const nIdx = cIdx + dir;
    const kids = data.templates[selIdx].steps[sIdx].children;
    if (nIdx < 0 || nIdx >= kids.length) return;
    const u = JSON.parse(JSON.stringify(data));
    const arr = u.templates[selIdx].steps[sIdx].children;
    [arr[cIdx], arr[nIdx]] = [arr[nIdx], arr[cIdx]];
    setData(u); saveData(u);
  };
  const convertToGroup = (sIdx) => {
    const u = JSON.parse(JSON.stringify(data));
    const step = u.templates[selIdx].steps[sIdx];
    step.type = "group";
    step.children = [{ id: uid(), name: "Sub-step 1", type: "task", assignee: step.assignee }];
    setData(u); saveData(u);
  };

  const partySelect = (val, onChange, color) => (
    <select value={val} onChange={e => onChange(e.target.value)} style={{ padding:"3px 8px", backgroundColor:getPartyColor(val)+"18", color:getPartyColor(val), border:`1px solid ${getPartyColor(val)}33`, borderRadius:4, fontSize:10, fontWeight:700, cursor:"pointer", outline:"none" }}>
      {data.parties.map(p => <option key={p} value={p}>{p}</option>)}
    </select>
  );
  const typeSelect = (val, onChange) => (
    <select value={val} onChange={e => onChange(e.target.value)} style={{ padding:"3px 8px", backgroundColor:"var(--bg-base)", color:"var(--text-tertiary)", border:"1px solid var(--border)", borderRadius:4, fontSize:10, cursor:"pointer", outline:"none" }}>
      <option value="task">Task</option>
      <option value="document">Document</option>
    </select>
  );
  const arrowBtn = (onClick, label, disabled) => (
    <button onClick={onClick} disabled={disabled} style={{ background:"none", border:"none", color:disabled?"var(--border)":"var(--text-muted)", cursor:disabled?"default":"pointer", fontSize:11, padding:"0 2px", lineHeight:1 }} title={label}>{label === "up" ? "▲" : "▼"}</button>
  );
  const xBtn = (onClick) => (
    <button onClick={onClick} style={{ background:"none", border:"1px solid var(--border-hover)", borderRadius:4, color:"var(--text-muted)", fontSize:10, padding:"2px 6px", cursor:"pointer", lineHeight:1 }} title="Remove">✕</button>
  );

  return (
    <div>
      {showParties&&<Modal title="Manage Parties" onClose={()=>setShowParties(false)}><PartiesInner parties={data.parties} setParties={updateParties}/></Modal>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
        <div><h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Workflow Templates</h2><p style={{ margin:"4px 0 0", fontSize:13, color:"var(--text-secondary)" }}>Categories, timelines, and party assignments</p></div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn primary small onClick={addTemplate}>+ Add Template</Btn>
          <Btn onClick={()=>setShowParties(true)}>⚙ Manage Parties</Btn>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:24, flexWrap:"wrap" }}>
        {data.templates.map((t,i) => <button key={t.id} onClick={()=>setSelIdx(i)} style={{ padding:"8px 16px", backgroundColor:selIdx===i?t.color+"14":"transparent", color:selIdx===i?t.color:"var(--text-secondary)", border:selIdx===i?`1px solid ${t.color}44`:"1px solid transparent", borderRadius:6, fontSize:13, fontWeight:selIdx===i?700:500, cursor:"pointer" }}><span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", backgroundColor:t.color, marginRight:8, verticalAlign:"middle" }}/>{t.name}</button>)}
      </div>
      <div style={{ backgroundColor:"var(--bg-card)", border:`1px solid ${tmpl.color}33`, borderRadius:8, padding:24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:14, color:"var(--text-muted)" }}>✏️</span>
            <input value={tmpl.name} onChange={e => updateTmpl("name", e.target.value)}
              style={{ background:"transparent", border:"none", borderBottom:`2px solid var(--border-hover)`, outline:"none", fontSize:18, fontWeight:700, color:tmpl.color, fontFamily:"inherit", padding:"4px 2px", minWidth:220 }}
              onFocus={e => e.target.style.borderBottomColor = tmpl.color}
              onBlur={e => e.target.style.borderBottomColor = "var(--border-hover)"} />
            <Bdg color={tmpl.color}>{tmpl.category||"Onboarding"}</Bdg>
          </div>
          <button onClick={deleteTemplate} style={{ background:"none", border:"1px solid #f43f5e33", borderRadius:4, color:"#f43f5e", fontSize:10, padding:"4px 10px", cursor:"pointer" }}>Delete Template</button>
        </div>
        <p style={{ margin:"0 0 20px", fontSize:12, color:"var(--text-secondary)" }}>{tmpl.steps.length} steps · {tmpl.steps.filter(s=>s.type==="group").length} groups</p>
        <div style={{ display:"flex", gap:24, marginBottom:24, padding:16, backgroundColor:"var(--bg-base)", borderRadius:8, border:"1px solid var(--border)", flexWrap:"wrap" }}>
          <div style={{ minWidth:200 }}>
            <label style={labelStyle}>Color</label>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>{COLOR_OPTIONS.map(c=><button key={c} onClick={()=>updateTmpl("color",c)} style={{ width:24, height:24, borderRadius:6, backgroundColor:c, border:tmpl.color===c?"2px solid #f1f5f9":"2px solid transparent", cursor:"pointer", opacity:tmpl.color===c?1:0.5 }}/>)}</div>
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <select value={tmpl.category||"Onboarding"} onChange={e=>updateTmpl("category",e.target.value)} style={selectFullStyle}><option value="Onboarding">Onboarding</option><option value="Service">Service</option><option value="Inventory">Inventory</option></select>
          </div>
          <div style={{ flex:1, minWidth:250 }}>
            <label style={labelStyle}>Est. Days: <strong style={{ color:"var(--text-primary)" }}>{tmpl.estDays}</strong></label>
            <input type="range" min="0" max="180" step="1" value={tmpl.estDays} onChange={e=>updateTmpl("estDays",Number(e.target.value))} style={{ width:"100%", accentColor:tmpl.color, cursor:"pointer" }}/>
          </div>
        </div>

        {/* Step list header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:12 }}>
          <div style={{ display:"flex", gap:6, alignItems:"center" }}>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Steps</span>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em", marginLeft:8 }}>Parties:</span>
            {data.parties.map(p=><PBadge key={p} party={p} sm/>)}
          </div>
          <div style={{ display:"flex", gap:6 }}>
            <Btn small onClick={() => addStep("task")} style={{ fontSize:11, padding:"4px 10px" }}>+ Step</Btn>
            <Btn small onClick={() => addStep("group")} style={{ fontSize:11, padding:"4px 10px" }}>+ Group</Btn>
          </div>
        </div>

        {/* Steps */}
        {tmpl.steps.map((step, sIdx) => (
          <div key={step.id}>
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 10px", backgroundColor:step.type==="group"?`${tmpl.color}08`:"transparent", borderBottom:"1px solid var(--border)18", borderRadius:step.type==="group"?"6px 6px 0 0":0 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                {arrowBtn(() => moveStep(sIdx, -1), "up", sIdx === 0)}
                {arrowBtn(() => moveStep(sIdx, 1), "down", sIdx === tmpl.steps.length - 1)}
              </div>
              <span style={{ fontSize:11, color:"var(--text-muted)", fontWeight:700, minWidth:22, fontFamily:"monospace" }}>{String(sIdx+1).padStart(2,"0")}</span>
              <span style={{ width:6, height:6, borderRadius:"50%", backgroundColor:step.type==="group"?tmpl.color:step.type==="document"?"#22c55e":"var(--text-secondary)", flexShrink:0 }}/>
              <input value={step.name} onChange={e => updateStepField(sIdx, "name", e.target.value)}
                style={{ flex:1, background:"transparent", border:"none", borderBottom:"1px solid transparent", outline:"none", fontSize:13, color:"var(--text-primary)", fontFamily:"inherit", padding:"2px 0" }}
                onFocus={e => e.target.style.borderBottomColor = tmpl.color}
                onBlur={e => e.target.style.borderBottomColor = "transparent"} />
              {step.type !== "group" && typeSelect(step.type, v => updateStepField(sIdx, "type", v))}
              {partySelect(step.assignee, v => updateStepAssignee(sIdx, v))}
              {step.type==="group" && <Bdg color={tmpl.color}>Group · {step.children.length}</Bdg>}
              {step.type !== "group" && <button onClick={() => convertToGroup(sIdx)} style={{ background:"none", border:"1px solid var(--border-hover)", borderRadius:4, color:"var(--text-muted)", fontSize:9, padding:"2px 6px", cursor:"pointer" }} title="Convert to group">▶ Group</button>}
              {xBtn(() => removeStep(sIdx))}
            </div>
            {step.type==="group" && (
              <div style={{ marginLeft:44, borderLeft:`2px solid ${tmpl.color}33`, paddingLeft:12, marginBottom:8, paddingBottom:4, backgroundColor:`${tmpl.color}04`, borderRadius:"0 0 0 6px" }}>
                {step.children.map((child, cIdx) => (
                  <div key={child.id} style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 8px", fontSize:12 }}>
                    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                      {arrowBtn(() => moveChild(sIdx, cIdx, -1), "up", cIdx === 0)}
                      {arrowBtn(() => moveChild(sIdx, cIdx, 1), "down", cIdx === step.children.length - 1)}
                    </div>
                    <span style={{ width:5, height:5, borderRadius:"50%", backgroundColor:child.type==="document"?"#22c55e":"var(--text-muted)", flexShrink:0 }}/>
                    <input value={child.name} onChange={e => updateStepField(sIdx, "name", e.target.value, cIdx)}
                      style={{ flex:1, background:"transparent", border:"none", borderBottom:"1px solid transparent", outline:"none", fontSize:12, color:"var(--text-tertiary)", fontFamily:"inherit", padding:"2px 0" }}
                      onFocus={e => e.target.style.borderBottomColor = tmpl.color}
                      onBlur={e => e.target.style.borderBottomColor = "transparent"} />
                    {typeSelect(child.type, v => updateStepField(sIdx, "type", v, cIdx))}
                    {partySelect(child.assignee, v => updateStepAssignee(sIdx, v, cIdx))}
                    {xBtn(() => removeChild(sIdx, cIdx))}
                  </div>
                ))}
                <button onClick={() => addChild(sIdx)} style={{ background:"none", border:"1px dashed #334155", borderRadius:4, color:"var(--text-muted)", fontSize:11, padding:"4px 10px", cursor:"pointer", marginTop:4, marginLeft:20 }}>+ Add Sub-step</button>
              </div>
            )}
          </div>
        ))}

        {tmpl.steps.length === 0 && (
          <div style={{ textAlign:"center", padding:"24px", color:"var(--text-muted)", fontSize:13 }}>
            No steps yet. Add a step or group to get started.
          </div>
        )}
      </div>
    </div>
  );
};

const PartiesInner = ({ parties, setParties }) => {
  const [val, setVal] = useState("");
  return (
    <div>
      <div style={{ display:"flex", flexDirection:"column", gap:6, marginBottom:16 }}>
        {parties.map((p,i) => (
          <div key={p} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 12px", backgroundColor:"var(--bg-base)", borderRadius:6, border:"1px solid var(--border)" }}>
            <span style={{ width:8, height:8, borderRadius:"50%", backgroundColor:getPartyColor(p) }}/><span style={{ flex:1, fontSize:13, color:"var(--text-primary)" }}>{p}</span>
            <button onClick={()=>setParties(parties.filter((_,j)=>j!==i))} style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer" }}>✕</button>
          </div>
        ))}
      </div>
      <div style={{ display:"flex", gap:8 }}><Input value={val} onChange={setVal} placeholder="New party..."/><Btn primary onClick={()=>{if(val.trim()&&!parties.includes(val.trim())){setParties([...parties,val.trim()]);setVal("");}}}>Add</Btn></div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// INVENTORY TAB
// ═════════════════════════════════════════════════════════════════════════════

const EditInventoryModal = ({ wf, onSave, onClose }) => {
  const [caseName, setCaseName] = useState(wf.nickname || "");
  const [transaction, setTransaction] = useState(wf.transaction || "");
  const [nna, setNna] = useState(wf.nna || "");
  const [riskPremium, setRiskPremium] = useState(wf.riskPremium || "");
  const [planningFees, setPlanningFees] = useState(wf.planningFees || "");
  const [f12Rev, setF12Rev] = useState(wf.f12Rev || "");
  const [wfNotes, setWfNotes] = useState(wf.wfNotes || "");

  const save = () => {
    onSave({ nickname: caseName.trim(), transaction, nna, riskPremium, planningFees, f12Rev, wfNotes: wfNotes.trim() });
  };

  return (
    <Modal title="Edit Inventory Item" onClose={onClose} wide>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <div style={{ gridColumn:"1/-1" }}>
          <label style={labelStyle}>Case Name</label>
          <Input value={caseName} onChange={setCaseName} placeholder={wf.name}/>
        </div>
        <div>
          <label style={labelStyle}>Transaction</label>
          <Input value={transaction} onChange={setTransaction} placeholder="e.g. 500000"/>
        </div>
        <div>
          <label style={labelStyle}>NNA (Net New Assets)</label>
          <Input value={nna} onChange={setNna} placeholder="e.g. 250000 or -50000"/>
        </div>
        <div>
          <label style={labelStyle}>Risk Mgmt Premium</label>
          <Input value={riskPremium} onChange={setRiskPremium} placeholder="e.g. 3200"/>
        </div>
        <div>
          <label style={labelStyle}>Planning Fees</label>
          <Input value={planningFees} onChange={setPlanningFees} placeholder="e.g. 5000"/>
        </div>
        <div>
          <label style={labelStyle}>F12 Revenue</label>
          <Input value={f12Rev} onChange={setF12Rev} placeholder="e.g. 12000"/>
        </div>
        <div style={{ gridColumn:"1/-1" }}>
          <label style={labelStyle}>Notes</label>
          <textarea value={wfNotes} onChange={e => setWfNotes(e.target.value)} rows={2} placeholder="Context, details..."
            style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/>
        </div>
      </div>
      <div style={{ display:"flex", gap:16, marginTop:12, paddingTop:10, borderTop:"1px solid var(--border)" }}>
        <span style={{ fontSize:11, color:"var(--text-muted)" }}>Template: <span style={{ color:"var(--text-tertiary)" }}>{wf.name}</span></span>
        <span style={{ fontSize:11, color:"var(--text-muted)" }}>Started: <span style={{ color:"var(--text-tertiary)" }}>{wf.startDate || "—"}</span></span>
        <span style={{ fontSize:11, color:"var(--text-muted)" }}>Status: <span style={{ color:wf.wfStatus==="active"?"#22c55e":"#f59e0b" }}>{wf.wfStatus || "active"}</span></span>
      </div>
      <div style={{ display:"flex", gap:8, marginTop:16 }}>
        <Btn onClick={onClose} style={{ flex:1 }}>Cancel</Btn>
        <Btn primary onClick={save} style={{ flex:1 }}>Save Changes</Btn>
      </div>
    </Modal>
  );
};

const InventoryTab = ({ data, setData, onSelectClient }) => {
  const parseNum = (v) => parseFloat(String(v).replace(/[^0-9.]/g, "")) || 0;
  const [editingItem, setEditingItem] = useState(null); // { clientId, wfId }

  // Collect all inventory workflow instances (active + pending) across all relationships
  const rows = [];
  (data.clients || []).filter(c => !c.archived).forEach(client => {
    client.workflows.forEach(wf => {
      const cat = wf.category || (data.templates.find(t => t.id === wf.templateId) || {}).category || "Onboarding";
      if (cat !== "Inventory") return;
      rows.push({
        clientId: client.id,
        wfId: wf.id,
        clientName: client.relationshipName,
        caseName: wf.nickname || wf.name,
        transaction: wf.transaction || "",
        nna: wf.nna || "",
        riskPremium: wf.riskPremium || "",
        planningFees: wf.planningFees || "",
        f12Rev: wf.f12Rev || "",
        wfColor: wf.color,
        pending: wf.wfStatus === "pending",
        progress: calcProgress(wf),
      });
    });
  });

  const totalTxn = rows.reduce((s, r) => s + parseNum(r.transaction), 0);
  const totalNna = rows.reduce((s, r) => s + parseNum(r.nna), 0);
  const totalRisk = rows.reduce((s, r) => s + parseNum(r.riskPremium), 0);
  const totalPlan = rows.reduce((s, r) => s + parseNum(r.planningFees), 0);
  const totalF12 = rows.reduce((s, r) => s + parseNum(r.f12Rev), 0);

  const saveEdit = (fields) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === editingItem.clientId);
    const wf = cl.workflows.find(w => w.id === editingItem.wfId);
    Object.assign(wf, fields);
    setData(u); saveData(u); setEditingItem(null);
  };

  const thStyle = { padding:"10px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em", borderBottom:"2px solid var(--border)" };
  const tdStyle = { padding:"10px 14px", fontSize:13, borderBottom:"1px solid var(--border)" };
  const moneyTd = { ...tdStyle, fontFamily:"monospace", fontWeight:600, textAlign:"right" };

  return (
    <div>
      {editingItem && (() => {
        const cl = data.clients.find(c => c.id === editingItem.clientId);
        const wf = cl?.workflows.find(w => w.id === editingItem.wfId);
        if (!wf) return null;
        return <EditInventoryModal wf={wf} onSave={saveEdit} onClose={() => setEditingItem(null)} />;
      })()}
      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Inventory</h2>
        <p style={{ margin:"4px 0 0", fontSize:13, color:"var(--text-secondary)" }}>Pipeline of all inventory items across relationships</p>
      </div>

      {/* Summary cards */}
      <div style={{ display:"flex", gap:16, marginBottom:24, flexWrap:"wrap" }}>
        {[
          { label:"Transaction", val:`$${totalTxn>0?formatCurrency(totalTxn):"0"}`, color:"#60a5fa" },
          { label:"NNA", val:`$${totalNna>0?formatCurrency(totalNna):"0"}`, color:"#38bdf8" },
          { label:"Risk Mgmt Premium", val:`$${totalRisk>0?formatCurrency(totalRisk):"0"}`, color:"#f59e0b" },
          { label:"Planning Fees", val:`$${totalPlan>0?formatCurrency(totalPlan):"0"}`, color:"#10b981" },
          { label:"F12 Revenue", val:`$${totalF12>0?formatCurrency(totalF12):"0"}`, color:"#a78bfa" },
        ].map((c,i) => (
          <div key={i} style={{ flex:1, minWidth:140, padding:"14px 18px", backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em" }}>{c.label}</span>
            <div style={{ fontSize:22, fontWeight:800, color:c.color, fontFamily:"monospace", marginTop:2 }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* Inventory table */}
      {rows.length > 0 ? (
        <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, overflow:"hidden" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr style={{ backgroundColor:"var(--bg-base)" }}>
                <th style={thStyle}>Relationship</th>
                <th style={thStyle}>Case Name</th>
                <th style={{ ...thStyle, textAlign:"right" }}>Transaction</th>
                <th style={{ ...thStyle, textAlign:"right" }}>NNA</th>
                <th style={{ ...thStyle, textAlign:"right" }}>Risk Mgmt Premium</th>
                <th style={{ ...thStyle, textAlign:"right" }}>Planning Fees</th>
                <th style={{ ...thStyle, textAlign:"center", width:40 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}
                  style={{ cursor:"pointer", transition:"background 0.1s" }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor="var(--border)"}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor="transparent"}>
                  <td style={tdStyle} onClick={() => onSelectClient(r.clientId)}>
                    <span style={{ color:"var(--text-bright)", fontWeight:600 }}>{r.clientName}</span>
                  </td>
                  <td style={tdStyle} onClick={() => onSelectClient(r.clientId)}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ width:6, height:6, borderRadius:"50%", backgroundColor:r.wfColor }}/>
                      <span style={{ color:"var(--text-primary)" }}>{r.caseName}</span>
                      {r.pending && <Bdg color="#f59e0b" outline>Pending</Bdg>}
                      {!r.pending && r.progress >= 1 && <Bdg color="#22c55e" outline>Complete</Bdg>}
                    </div>
                  </td>
                  <td style={moneyTd}>
                    <span style={{ color:r.transaction?"#60a5fa":"var(--border-hover)" }}>{r.transaction ? `$${formatCurrency(r.transaction)}` : "—"}</span>
                  </td>
                  <td style={moneyTd}>
                    <span style={{ color:r.nna?"#38bdf8":"var(--border-hover)" }}>{r.nna ? `$${formatCurrency(r.nna)}` : "—"}</span>
                  </td>
                  <td style={moneyTd}>
                    <span style={{ color:r.riskPremium?"#f59e0b":"var(--border-hover)" }}>{r.riskPremium ? `$${formatCurrency(r.riskPremium)}` : "—"}</span>
                  </td>
                  <td style={moneyTd}>
                    <span style={{ color:r.planningFees?"#10b981":"var(--border-hover)" }}>{r.planningFees ? `$${formatCurrency(r.planningFees)}` : "—"}</span>
                  </td>
                  <td style={{ ...tdStyle, textAlign:"center" }}>
                    <button onClick={e => { e.stopPropagation(); setEditingItem({ clientId: r.clientId, wfId: r.wfId }); }}
                      style={{ background:"none", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-secondary)", fontSize:11, padding:"3px 8px", cursor:"pointer" }}
                      onMouseEnter={e=>e.target.style.color="#3b82f6"} onMouseLeave={e=>e.target.style.color="var(--text-secondary)"}>✎</button>
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ backgroundColor:"var(--bg-base)" }}>
                <td style={{ ...tdStyle, fontWeight:700, color:"var(--text-tertiary)", borderBottom:"none" }} colSpan={2}>Totals ({rows.length} items)</td>
                <td style={{ ...moneyTd, fontWeight:700, color:"#60a5fa", borderBottom:"none" }}>${formatCurrency(totalTxn)}</td>
                <td style={{ ...moneyTd, fontWeight:700, color:"#38bdf8", borderBottom:"none" }}>${formatCurrency(totalNna)}</td>
                <td style={{ ...moneyTd, fontWeight:700, color:"#f59e0b", borderBottom:"none" }}>${formatCurrency(totalRisk)}</td>
                <td style={{ ...moneyTd, fontWeight:700, color:"#10b981", borderBottom:"none" }}>${formatCurrency(totalPlan)}</td>
                <td style={{ borderBottom:"none" }}/>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--text-muted)" }}>
          <p style={{ fontSize:15, color:"var(--text-tertiary)", marginBottom:4 }}>No inventory items yet</p>
          <p style={{ fontSize:13 }}>Add inventory items from the Relationships tab by clicking "+ Inventory" on any relationship card.</p>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// REMINDERS TAB
// ═════════════════════════════════════════════════════════════════════════════

const REMINDER_TYPES = [
  { value: "review", label: "Review Due", color: "#3b82f6", icon: "📋" },
  { value: "premium", label: "Premium Payment", color: "#f59e0b", icon: "💰" },
  { value: "birthday", label: "Birthday", color: "#ec4899", icon: "🎂" },
  { value: "anniversary", label: "Anniversary", color: "#a78bfa", icon: "💍" },
  { value: "custom", label: "Custom", color: "#10b981", icon: "📌" },
];
const RECURRENCE = [
  { value: "none", label: "One-time" },
  { value: "monthly", label: "Monthly", days: 30 },
  { value: "quarterly", label: "Quarterly", days: 90 },
  { value: "semi-annual", label: "Semi-annual", days: 182 },
  { value: "annual", label: "Annual", days: 365 },
];

const AddReminderModal = ({ data, clientId, onClose, onSave, editing }) => {
  const [rType, setRType] = useState(editing?.rType || "review");
  const [title, setTitle] = useState(editing?.title || "");
  const [dueDate, setDueDate] = useState(editing?.dueDate || "");
  const [recurrence, setRecurrence] = useState(editing?.recurrence || "none");
  const [amount, setAmount] = useState(editing?.amount || "");
  const [handler, setHandler] = useState(editing?.handler || "");
  const [notes, setNotes] = useState(editing?.notes || "");

  const save = () => {
    if (!dueDate) return alert("Due date is required.");
    const typeInfo = REMINDER_TYPES.find(t => t.value === rType);
    const reminder = {
      id: editing?.id || uid(),
      rType, title: title.trim() || typeInfo.label,
      dueDate, recurrence, amount, handler, notes: notes.trim(),
      completed: false, snoozedTo: null,
    };
    onSave(reminder);
    onClose();
  };

  return (
    <Modal title={editing ? "Edit Reminder" : "Add Reminder"} onClose={onClose}>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div>
          <label style={labelStyle}>Type</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {REMINDER_TYPES.map(t => (
              <button key={t.value} onClick={() => { setRType(t.value); if (!title.trim()) setTitle(""); }}
                style={{ padding:"6px 12px", borderRadius:6, fontSize:12, fontWeight:rType===t.value?700:500,
                  backgroundColor:rType===t.value?t.color+"18":"transparent",
                  color:rType===t.value?t.color:"var(--text-secondary)",
                  border:rType===t.value?`1px solid ${t.color}44`:"1px solid var(--border)",
                  cursor:"pointer" }}>{t.icon} {t.label}</button>
            ))}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Title</label>
          <Input value={title} onChange={setTitle} placeholder={REMINDER_TYPES.find(t=>t.value===rType)?.label}/>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
          <div>
            <label style={labelStyle}>Due Date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={dateInputStyle}/>
          </div>
          <div>
            <label style={labelStyle}>Recurrence</label>
            <select value={recurrence} onChange={e => setRecurrence(e.target.value)} style={selectFullStyle}>
              {RECURRENCE.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
        </div>
        {(rType === "premium") && (
          <div>
            <label style={labelStyle}>Amount</label>
            <Input value={amount} onChange={setAmount} placeholder="e.g. 3200"/>
          </div>
        )}
        <div>
          <label style={labelStyle}>Assigned To</label>
          <Input value={handler} onChange={setHandler} placeholder="e.g. Jeremy, Rachel"/>
        </div>
        <div>
          <label style={labelStyle}>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} placeholder="Optional details..."
            style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/>
        </div>
      </div>
      <div style={{ display:"flex", gap:8, marginTop:16 }}>
        <Btn onClick={onClose} style={{ flex:1 }}>Cancel</Btn>
        <Btn primary onClick={save} style={{ flex:1 }}>{editing ? "Update" : "Add Reminder"}</Btn>
      </div>
    </Modal>
  );
};

const RemindersTab = ({ data, setData, onSelectClient }) => {
  const [showAdd, setShowAdd] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterHandler, setFilterHandler] = useState(null);
  const [filterSegment, setFilterSegment] = useState(null);
  const [showCompliance, setShowCompliance] = useState(true);
  const now = today();
  const nowD = new Date(now);
  const endOfWeek = new Date(nowD); endOfWeek.setDate(endOfWeek.getDate() + 7);
  const endOfMonth = new Date(nowD); endOfMonth.setDate(endOfMonth.getDate() + 30);
  const ewStr = endOfWeek.toISOString().slice(0,10);
  const emStr = endOfMonth.toISOString().slice(0,10);

  // Collect all reminders across relationships
  const allReminders = [];
  (data.clients || []).filter(c => !c.archived).forEach(client => {
    (client.reminders || []).forEach(r => {
      if (r.completed) return;
      const effectiveDate = r.snoozedTo || r.dueDate;
      allReminders.push({ ...r, effectiveDate, clientId: client.id, clientName: client.relationshipName, clientSegment: client.segment });
    });
  });

  // Filter
  let filtered = allReminders;
  if (filterType) filtered = filtered.filter(r => r.rType === filterType);
  if (filterHandler) filtered = filtered.filter(r => r.handler && r.handler.toLowerCase().includes(filterHandler.toLowerCase()));
  if (filterSegment) filtered = filtered.filter(r => r.clientSegment === filterSegment);

  // Sort: date first, then segment weight descending (Diamond first)
  const segSort = (a, b) => {
    const cmp = a.effectiveDate.localeCompare(b.effectiveDate);
    if (cmp !== 0) return cmp;
    return (SEGMENT_WEIGHT[b.clientSegment] || 0) - (SEGMENT_WEIGHT[a.clientSegment] || 0);
  };

  // Bucket
  const overdue = filtered.filter(r => r.effectiveDate < now).sort(segSort);
  const thisWeek = filtered.filter(r => r.effectiveDate >= now && r.effectiveDate <= ewStr).sort(segSort);
  const thisMonth = filtered.filter(r => r.effectiveDate > ewStr && r.effectiveDate <= emStr).sort(segSort);
  const upcoming = filtered.filter(r => r.effectiveDate > emStr).sort(segSort);

  // Handlers list for filter
  const handlers = [...new Set(allReminders.map(r => r.handler).filter(Boolean))];

  const saveReminder = (clientId, reminder) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    const idx = cl.reminders.findIndex(r => r.id === reminder.id);
    if (idx >= 0) cl.reminders[idx] = reminder;
    else cl.reminders.push(reminder);
    setData(u); saveData(u);
  };

  const completeReminder = (clientId, reminderId) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    const r = cl.reminders.find(r => r.id === reminderId);
    if (!r) return;
    if (r.recurrence !== "none") {
      // Advance to next occurrence
      const rec = RECURRENCE.find(rc => rc.value === r.recurrence);
      const d = new Date(r.dueDate);
      d.setDate(d.getDate() + (rec?.days || 365));
      r.dueDate = d.toISOString().slice(0, 10);
      r.snoozedTo = null;
    } else {
      r.completed = true;
    }
    setData(u); saveData(u);
  };

  const snoozeReminder = (clientId, reminderId, days) => {
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    const r = cl.reminders.find(r => r.id === reminderId);
    if (!r) return;
    const d = new Date();
    d.setDate(d.getDate() + days);
    r.snoozedTo = d.toISOString().slice(0, 10);
    setData(u); saveData(u);
  };

  const deleteReminder = (clientId, reminderId) => {
    if (!window.confirm("Delete this reminder?")) return;
    const u = JSON.parse(JSON.stringify(data));
    const cl = u.clients.find(c => c.id === clientId);
    cl.reminders = cl.reminders.filter(r => r.id !== reminderId);
    setData(u); saveData(u);
  };

  const ReminderRow = ({ r }) => {
    const typeInfo = REMINDER_TYPES.find(t => t.value === r.rType) || REMINDER_TYPES[4];
    const isOverdue = r.effectiveDate < now;
    const daysAway = daysBetween(now, r.effectiveDate);
    const [showSnooze, setShowSnooze] = useState(false);

    return (
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 16px", backgroundColor:"var(--bg-card)",
        border:`1px solid ${isOverdue ? "#f43f5e33" : "var(--border)"}`, borderRadius:8, borderLeft:`4px solid ${typeInfo.color}`,
        transition:"border-color 0.15s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = isOverdue ? "#f43f5e" : typeInfo.color}
        onMouseLeave={e => e.currentTarget.style.borderColor = isOverdue ? "#f43f5e33" : "var(--border)"}>
        <span style={{ fontSize:20 }}>{typeInfo.icon}</span>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ fontSize:14, fontWeight:700, color:"var(--text-bright)" }}>{r.title}</span>
            {r.recurrence !== "none" && <Bdg color={typeInfo.color} outline>🔁 {RECURRENCE.find(rc=>rc.value===r.recurrence)?.label}</Bdg>}
            {r.amount && <span style={{ fontSize:12, fontWeight:700, color:"#f59e0b", fontFamily:"monospace" }}>${formatCurrency(r.amount)}</span>}
          </div>
          <div style={{ display:"flex", gap:12, marginTop:3 }}>
            <span onClick={() => onSelectClient(r.clientId)} style={{ fontSize:12, color:"#3b82f6", cursor:"pointer", fontWeight:600 }}>{r.clientName}</span>
            <SegBadge segment={r.clientSegment}/>
            {r.handler && <span style={{ fontSize:11, color:"var(--text-muted)" }}>→ {r.handler}</span>}
            {r.notes && <span style={{ fontSize:11, color:"var(--text-tertiary)", fontStyle:"italic" }}>{r.notes}</span>}
          </div>
        </div>
        <div style={{ textAlign:"right", minWidth:90 }}>
          <div style={{ fontSize:12, fontWeight:700, fontFamily:"monospace", color: isOverdue ? "#f43f5e" : daysAway <= 7 ? "#f59e0b" : "var(--text-tertiary)" }}>
            {r.effectiveDate}
          </div>
          <div style={{ fontSize:10, color: isOverdue ? "#f43f5e" : "var(--text-muted)" }}>
            {isOverdue ? `${Math.abs(daysAway)}d overdue` : daysAway === 0 ? "Today" : `in ${daysAway}d`}
          </div>
          {r.snoozedTo && <div style={{ fontSize:9, color:"#f59e0b" }}>snoozed</div>}
        </div>
        <div style={{ display:"flex", gap:4, position:"relative" }}>
          <button onClick={() => completeReminder(r.clientId, r.id)} title={r.recurrence !== "none" ? "Advance to next" : "Complete"} style={{ background:"none", border:"1px solid #22c55e33", borderRadius:4, color:"#22c55e", fontSize:13, padding:"4px 8px", cursor:"pointer" }}>✓</button>
          <button onClick={() => setShowSnooze(!showSnooze)} title="Snooze" style={{ background:"none", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-muted)", fontSize:11, padding:"4px 8px", cursor:"pointer" }}>⏰</button>
          <button onClick={() => setEditing({ ...r })} title="Edit" style={{ background:"none", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-muted)", fontSize:11, padding:"4px 8px", cursor:"pointer" }}>✎</button>
          <button onClick={() => deleteReminder(r.clientId, r.id)} title="Delete" style={{ background:"none", border:"1px solid #f43f5e33", borderRadius:4, color:"#f43f5e", fontSize:11, padding:"4px 8px", cursor:"pointer" }}>✕</button>
          {showSnooze && (
            <div style={{ position:"absolute", top:"100%", right:0, marginTop:4, backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:6, padding:6, zIndex:10, display:"flex", flexDirection:"column", gap:2, minWidth:100, boxShadow:"0 4px 12px rgba(0,0,0,0.3)" }}>
              {[1,3,7,14,30].map(d => (
                <button key={d} onClick={() => { snoozeReminder(r.clientId, r.id, d); setShowSnooze(false); }}
                  style={{ background:"none", border:"none", padding:"6px 10px", color:"var(--text-primary)", fontSize:12, cursor:"pointer", textAlign:"left", borderRadius:4 }}
                  onMouseEnter={e => e.target.style.backgroundColor="var(--bg-base)"}
                  onMouseLeave={e => e.target.style.backgroundColor="transparent"}>
                  {d}d
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const Section = ({ title, icon, color, items }) => {
    if (items.length === 0) return null;
    return (
      <div style={{ marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
          <span style={{ fontSize:14 }}>{icon}</span>
          <h3 style={{ margin:0, fontSize:14, fontWeight:700, color }}>{title}</h3>
          <span style={{ fontSize:12, fontWeight:700, color:"var(--text-muted)", fontFamily:"monospace" }}>({items.length})</span>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {items.map(r => <ReminderRow key={r.id} r={r}/>)}
        </div>
      </div>
    );
  };

  // Quick-add needs a client picker
  const clients = (data.clients || []).filter(c => !c.archived).sort((a,b) => a.relationshipName.localeCompare(b.relationshipName));

  return (
    <div>
      {showAdd && <AddReminderModal data={data} clientId={showAdd} onClose={() => setShowAdd(null)} onSave={(r) => saveReminder(showAdd, r)}/>}
      {editing && <AddReminderModal data={data} clientId={editing.clientId} editing={editing} onClose={() => setEditing(null)} onSave={(r) => saveReminder(editing.clientId, r)}/>}

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:24 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Reminders</h2>
          <p style={{ margin:"4px 0 0", fontSize:13, color:"var(--text-secondary)" }}>Reviews, premium payments, birthdays, and custom follow-ups</p>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <select onChange={e => setShowAdd(e.target.value || null)} value="" style={{ ...selectFullStyle, minWidth:200 }}>
            <option value="">+ Add Reminder to...</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.relationshipName}</option>)}
          </select>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap", alignItems:"center" }}>
        <button onClick={() => setFilterType(null)} style={{ padding:"5px 12px", fontSize:11, fontWeight:filterType===null?700:500, color:filterType===null?"var(--text-bright)":"var(--text-secondary)", backgroundColor:filterType===null?"var(--border-hover)":"transparent", border:filterType===null?"none":"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>All Types</button>
        {REMINDER_TYPES.map(t => (
          <button key={t.value} onClick={() => setFilterType(filterType===t.value?null:t.value)}
            style={{ padding:"5px 12px", fontSize:11, fontWeight:filterType===t.value?700:500, color:filterType===t.value?t.color:"var(--text-secondary)", backgroundColor:filterType===t.value?t.color+"18":"transparent", border:filterType===t.value?`1px solid ${t.color}44`:"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>{t.icon} {t.label}</button>
        ))}
        {handlers.length > 0 && <>
          <div style={{ width:1, height:20, backgroundColor:"var(--border)", margin:"0 4px" }}/>
          <button onClick={() => setFilterHandler(null)} style={{ padding:"5px 12px", fontSize:11, fontWeight:filterHandler===null?700:500, color:filterHandler===null?"var(--text-bright)":"var(--text-secondary)", backgroundColor:filterHandler===null?"var(--border-hover)":"transparent", border:filterHandler===null?"none":"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>All People</button>
          {handlers.map(h => (
            <button key={h} onClick={() => setFilterHandler(filterHandler===h?null:h)}
              style={{ padding:"5px 12px", fontSize:11, fontWeight:filterHandler===h?700:500, color:filterHandler===h?"var(--text-bright)":"var(--text-secondary)", backgroundColor:filterHandler===h?"var(--border-hover)":"transparent", border:filterHandler===h?"none":"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>{h}</button>
          ))}
        </>}
      </div>

      {/* Segment filters */}
      <div style={{ display:"flex", gap:6, marginBottom:20, alignItems:"center" }}>
        <button onClick={() => setFilterSegment(null)} style={{ padding:"5px 12px", fontSize:11, fontWeight:filterSegment===null?700:500, color:filterSegment===null?"var(--text-bright)":"var(--text-secondary)", backgroundColor:filterSegment===null?"var(--border-hover)":"transparent", border:filterSegment===null?"none":"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>All Segments</button>
        {(data.segments||[]).map(seg => {
          const sc = SEGMENT_COLORS[seg] || "var(--text-secondary)";
          const active = filterSegment === seg;
          return <button key={seg} onClick={() => setFilterSegment(active?null:seg)}
            style={{ padding:"5px 12px", fontSize:11, fontWeight:active?700:500, color:active?sc:"var(--text-secondary)", backgroundColor:active?`${sc}18`:"transparent", border:active?`1px solid ${sc}44`:"1px solid var(--border)", borderRadius:4, cursor:"pointer" }}>{seg}</button>;
        })}
      </div>

      {/* Summary */}
      <div style={{ display:"flex", gap:16, marginBottom:24 }}>
        {[
          { label:"Overdue", val:overdue.length, color:overdue.length>0?"#f43f5e":"#22c55e" },
          { label:"This Week", val:thisWeek.length, color:"#f59e0b" },
          { label:"This Month", val:thisMonth.length, color:"#3b82f6" },
          { label:"Total Active", val:filtered.length, color:"var(--text-tertiary)" },
        ].map((c,i) => (
          <div key={i} style={{ flex:1, padding:"14px 18px", backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.08em" }}>{c.label}</span>
            <div style={{ fontSize:24, fontWeight:800, color:c.color, fontFamily:"monospace", marginTop:2 }}>{c.val}</div>
          </div>
        ))}
      </div>

      {/* Review Compliance */}
      {(() => {
        let compClients = (data.clients || []).filter(c => !c.archived);
        if (filterSegment) compClients = compClients.filter(c => c.segment === filterSegment);
        const healthData = compClients.map(c => ({ ...getReviewHealth(c), client: c })).sort((a, b) => {
          const order = { overdue: 0, approaching: 1, never: 2, healthy: 3, exempt: 4 };
          if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
          // Secondary sort by segment weight
          const sw = (SEGMENT_WEIGHT[b.client.segment] || 0) - (SEGMENT_WEIGHT[a.client.segment] || 0);
          if (sw !== 0) return sw;
          if (a.daysUntil === null && b.daysUntil === null) return 0;
          if (a.daysUntil === null) return -1;
          if (b.daysUntil === null) return 1;
          return a.daysUntil - b.daysUntil;
        });
        const overdueCount = healthData.filter(h => h.status === "overdue").length;
        const approachingCount = healthData.filter(h => h.status === "approaching").length;
        const neverCount = healthData.filter(h => h.status === "never").length;
        const healthyCount = healthData.filter(h => h.status === "healthy").length;
        const exemptCount = healthData.filter(h => h.status === "exempt").length;

        return (
          <div style={{ marginBottom:24, backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, overflow:"hidden" }}>
            <div onClick={() => setShowCompliance(!showCompliance)}
              style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 18px", cursor:"pointer", borderBottom:showCompliance?"1px solid var(--border)":"none" }}>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:15 }}>📋</span>
                <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:"var(--text-bright)" }}>Review Compliance</h3>
                <div style={{ display:"flex", gap:6 }}>
                  {overdueCount > 0 && <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, backgroundColor:"#f43f5e18", color:"#f43f5e" }}>{overdueCount} overdue</span>}
                  {approachingCount > 0 && <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, backgroundColor:"#f59e0b18", color:"#f59e0b" }}>{approachingCount} approaching</span>}
                  {neverCount > 0 && <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, backgroundColor:"var(--border)", color:"var(--text-muted)" }}>{neverCount} never</span>}
                  {healthyCount > 0 && <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, backgroundColor:"#22c55e18", color:"#22c55e" }}>{healthyCount} on track</span>}
                  {exemptCount > 0 && <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, backgroundColor:"var(--border)", color:"var(--text-muted)" }}>{exemptCount} exempt</span>}
                </div>
              </div>
              <span style={{ fontSize:12, color:"var(--text-muted)", transform:showCompliance?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▼</span>
            </div>
            {showCompliance && (
              <div style={{ padding:"8px 12px" }}>
                {healthData.map(h => {
                  const c = h.client;
                  const barPct = h.status === "never" || h.status === "exempt" ? 0 : Math.min(h.pct * 100, 100);
                  return (
                    <div key={c.id} onClick={() => onSelectClient(c.id)}
                      style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 6px", borderRadius:4, cursor:"pointer", transition:"background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor="var(--bg-base)"}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor="transparent"}>
                      <span style={{ width:6, height:6, borderRadius:"50%", backgroundColor:h.color, flexShrink:0 }}/>
                      <span style={{ fontSize:13, fontWeight:600, color:"var(--text-bright)", minWidth:160 }}>{c.relationshipName}</span>
                      <SegBadge segment={c.segment}/>
                      <div style={{ flex:1, height:6, backgroundColor:"var(--border)", borderRadius:3, overflow:"hidden", position:"relative" }}>
                        <div style={{ width:`${barPct}%`, height:"100%", backgroundColor:h.color, borderRadius:3, transition:"width 0.3s" }}/>
                      </div>
                      <span style={{ fontSize:11, fontWeight:700, color:h.color, fontFamily:"monospace", minWidth:100, textAlign:"right" }}>{h.label}</span>
                      <span style={{ fontSize:10, color:"var(--text-muted)", minWidth:70, textAlign:"right" }}>{h.freq}</span>
                    </div>
                  );
                })}
                {compClients.length === 0 && <p style={{ padding:12, fontSize:13, color:"var(--text-muted)", textAlign:"center" }}>No active relationships{filterSegment ? ` in ${filterSegment}` : ""}</p>}
              </div>
            )}
          </div>
        );
      })()}

      {/* Sections */}
      <Section title="Overdue" icon="🔴" color="#f43f5e" items={overdue}/>
      <Section title="This Week" icon="🟡" color="#f59e0b" items={thisWeek}/>
      <Section title="This Month" icon="🔵" color="#3b82f6" items={thisMonth}/>
      <Section title="Upcoming" icon="⚪" color="var(--text-tertiary)" items={upcoming}/>

      {filtered.length === 0 && allReminders.length > 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", color:"var(--text-muted)" }}>
          <p style={{ fontSize:14, color:"var(--text-tertiary)" }}>No reminders match current filters</p>
        </div>
      )}

      {allReminders.length === 0 && (
        <div style={{ textAlign:"center", padding:"60px 20px", color:"var(--text-muted)" }}>
          <p style={{ fontSize:15, color:"var(--text-tertiary)", marginBottom:4 }}>No reminders yet</p>
          <p style={{ fontSize:13 }}>Use the dropdown above to add a reminder to any relationship.</p>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// DATA PANEL
// ═════════════════════════════════════════════════════════════════════════════

// Simple CSV parser handling quoted fields
const parseCSV = (text) => {
  const rows = []; let row = []; let cell = ""; let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], nx = text[i + 1];
    if (inQ) {
      if (ch === '"' && nx === '"') { cell += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cell += ch;
    } else {
      if (ch === '"') inQ = true;
      else if (ch === ',') { row.push(cell.trim()); cell = ""; }
      else if (ch === '\n' || (ch === '\r' && nx === '\n')) {
        row.push(cell.trim()); cell = ""; rows.push(row); row = [];
        if (ch === '\r') i++;
      } else cell += ch;
    }
  }
  if (cell || row.length) { row.push(cell.trim()); rows.push(row); }
  return rows;
};

const IMPORT_FIELDS = [
  { key: "relationshipName", label: "Relationship Name", required: true },
  { key: "segment", label: "Segment" },
  { key: "occupation", label: "Occupation" },
  { key: "name1", label: "Primary Name" },
  { key: "dob1", label: "Primary DoB" },
  { key: "name2", label: "Spouse / Partner" },
  { key: "dob2", label: "Spouse DoB" },
  { key: "phone", label: "Phone" },
  { key: "email", label: "Email" },
  { key: "altEmail", label: "Alt Email" },
  { key: "street", label: "Street" },
  { key: "suite", label: "Suite / Unit" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "lead", label: "Lead Advisor" },
  { key: "hobbies", label: "Hobbies" },
  { key: "debrief", label: "Debrief / Family Notes" },
  { key: "reviewFrequency", label: "Review Frequency" },
  { key: "anniversary", label: "Anniversary" },
  { key: "firmAnniversary", label: "Firm Anniversary" },
];

const FIELD_HINTS = {
  relationshipName: ["relationship","name","client","account"],
  segment: ["segment","tier","level","category"],
  occupation: ["occupation","job","employment"],
  name1: ["primary","first name","name1","contact"],
  dob1: ["dob","birth","birthday","dob1","primary dob"],
  name2: ["spouse","partner","name2","secondary"],
  dob2: ["spouse dob","dob2","partner dob"],
  phone: ["phone","tel","mobile","cell"],
  email: ["email","e-mail","mail"],
  altEmail: ["alt email","secondary email","email2"],
  street: ["street","address","addr"],
  suite: ["suite","unit","apt"],
  city: ["city","town"],
  state: ["state","st","province"],
  zip: ["zip","postal","postcode"],
  lead: ["lead","advisor","adviser","rep"],
  hobbies: ["hobbies","interests","hobby"],
  debrief: ["debrief","notes","family","family notes"],
  reviewFrequency: ["review","frequency","cadence"],
  anniversary: ["anniversary","wedding"],
  firmAnniversary: ["firm anniversary","client since","start date"],
};

const autoMapColumns = (headers) => {
  const mapping = {};
  const lower = headers.map(h => h.toLowerCase().trim());
  IMPORT_FIELDS.forEach(f => {
    const hints = FIELD_HINTS[f.key] || [f.key.toLowerCase()];
    let bestIdx = -1;
    // Exact match first
    for (let i = 0; i < lower.length; i++) {
      if (hints.some(h => lower[i] === h)) { bestIdx = i; break; }
    }
    // Contains match
    if (bestIdx === -1) {
      for (let i = 0; i < lower.length; i++) {
        if (hints.some(h => lower[i].includes(h) || h.includes(lower[i]))) { bestIdx = i; break; }
      }
    }
    if (bestIdx >= 0) mapping[f.key] = bestIdx;
  });
  return mapping;
};

const normalizeMatch = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const ContactImporter = ({ data, setData }) => {
  const [step, setStep] = useState("idle"); // idle, mapping, preview
  const [rawRows, setRawRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [previewRows, setPreviewRows] = useState([]);
  const [selected, setSelected] = useState({});
  const [mergeFields, setMergeFields] = useState({});
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => { setStep("idle"); setRawRows([]); setHeaders([]); setMapping({}); setPreviewRows([]); setSelected({}); setMergeFields({}); setResult(null); };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();

    if (ext === "csv" || ext === "tsv") {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const rows = parseCSV(ev.target.result);
        if (rows.length < 2) return alert("File appears empty or has no data rows.");
        setHeaders(rows[0]);
        setRawRows(rows.slice(1).filter(r => r.some(c => c)));
        setMapping(autoMapColumns(rows[0]));
        setStep("mapping");
      };
      reader.readAsText(file);
    } else if (ext === "xlsx" || ext === "xls") {
      alert("Please save your Excel file as CSV first (File → Save As → CSV). CSV is the supported import format.");
    } else {
      alert("Please upload a .csv or .xlsx file.");
    }
    e.target.value = "";
  };

  const setMap = (fieldKey, colIdx) => setMapping(p => ({ ...p, [fieldKey]: colIdx === "" ? undefined : parseInt(colIdx) }));

  const buildPreview = () => {
    if (mapping.relationshipName === undefined) return alert("Relationship Name must be mapped.");
    const existing = data.clients || [];
    const rows = rawRows.map((row, ri) => {
      const record = {};
      IMPORT_FIELDS.forEach(f => {
        if (mapping[f.key] !== undefined && row[mapping[f.key]] !== undefined) {
          record[f.key] = String(row[mapping[f.key]] || "").trim();
        }
      });

      if (!record.relationshipName) return { idx: ri, record, status: "error", reason: "Missing Relationship Name", match: null };

      // Find match
      const normName = normalizeMatch(record.relationshipName);
      const normEmail = normalizeMatch(record.email);
      let match = existing.find(c => normalizeMatch(c.relationshipName) === normName);
      if (!match && normEmail) match = existing.find(c => normalizeMatch(c.email) === normEmail);

      if (match) {
        // Check if truly identical
        const isDupe = IMPORT_FIELDS.every(f => !record[f.key] || record[f.key] === (match[f.key] || ""));
        if (isDupe) return { idx: ri, record, status: "duplicate", reason: "Exact duplicate", match };
        return { idx: ri, record, status: "match", reason: `Matches: ${match.relationshipName}`, match };
      }
      return { idx: ri, record, status: "new", reason: "New contact", match: null };
    });

    setPreviewRows(rows);
    // Auto-select new and match, deselect duplicate and error
    const sel = {};
    rows.forEach((r, i) => { sel[i] = r.status === "new" || r.status === "match"; });
    setSelected(sel);

    // For matches, default to overwrite non-empty incoming fields
    const mf = {};
    rows.forEach((r, i) => {
      if (r.status === "match" && r.match) {
        const fields = {};
        IMPORT_FIELDS.forEach(f => {
          if (r.record[f.key] && r.record[f.key] !== (r.match[f.key] || "")) {
            fields[f.key] = "incoming"; // default to incoming value
          }
        });
        if (Object.keys(fields).length) mf[i] = fields;
      }
    });
    setMergeFields(mf);
    setStep("preview");
  };

  const commitImport = () => {
    const u = JSON.parse(JSON.stringify(data));
    let created = 0, updated = 0, skipped = 0;

    previewRows.forEach((pr, i) => {
      if (!selected[i]) { skipped++; return; }

      if (pr.status === "new") {
        const num = String(u.nextClientNum || 1).padStart(3, "0");
        const refCode = `HF-${new Date().getFullYear()}-${num}`;
        const seg = (u.segments || []).includes(pr.record.segment) ? pr.record.segment : "Silver";
        const newClient = {
          id: uid(), refCode, createdDate: today(), workflows: [], activityLog: [], reminders: [], dnaLinks: [],
          relationshipName: pr.record.relationshipName || "", segment: seg,
          occupation: pr.record.occupation || "", name1: pr.record.name1 || "", dob1: pr.record.dob1 || "",
          name2: pr.record.name2 || "", dob2: pr.record.dob2 || "", phone: formatPhone(pr.record.phone || ""),
          email: pr.record.email || "", altEmail: pr.record.altEmail || "", lead: pr.record.lead || "Jason",
          street: pr.record.street || "", suite: pr.record.suite || "", city: pr.record.city || "",
          state: pr.record.state || "", zip: pr.record.zip || "", hobbies: pr.record.hobbies || "",
          debrief: pr.record.debrief || "", reviewFrequency: pr.record.reviewFrequency || SEGMENT_CADENCE[seg] || "Annual",
          anniversary: pr.record.anniversary || "", firmAnniversary: pr.record.firmAnniversary || "",
          lastReview: "", lastNotes: "", linkedAUM: "", fwd12Comp: "", folderPath: "",
          archived: false, archiveReason: "", marketingOptOut: false,
        };
        u.clients.push(newClient);
        u.nextClientNum = (u.nextClientNum || 1) + 1;
        created++;
      } else if (pr.status === "match" && pr.match) {
        const cl = u.clients.find(c => c.id === pr.match.id);
        if (!cl) { skipped++; return; }
        const fieldChoices = mergeFields[i] || {};
        Object.keys(fieldChoices).forEach(fk => {
          if (fieldChoices[fk] === "incoming") {
            cl[fk] = pr.record[fk] || cl[fk];
            if (fk === "phone") cl[fk] = formatPhone(pr.record[fk] || "");
          }
          // else "existing" — keep current
        });
        updated++;
      } else {
        skipped++;
      }
    });

    setData(u); saveData(u);
    setResult({ created, updated, skipped });
    setStep("idle");
  };

  const statusColors = { new: "#22c55e", match: "#f59e0b", duplicate: "#64748b", error: "#f43f5e" };
  const statusLabels = { new: "New", match: "Match", duplicate: "Duplicate", error: "Error" };

  return (
    <div style={{ padding:20, backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
        <h3 style={{ margin:0, fontSize:14, fontWeight:700, color:"var(--text-bright)" }}>Import Contacts</h3>
        {step !== "idle" && <Btn small onClick={reset}>Cancel Import</Btn>}
      </div>
      <p style={{ margin:"0 0 16px", fontSize:12, color:"var(--text-secondary)" }}>Upload a CSV file to import relationships. Column mapping, match detection, and merge controls before committing.</p>

      {/* Result toast */}
      {result && (
        <div style={{ padding:"10px 14px", backgroundColor:"#22c55e14", border:"1px solid #22c55e44", borderRadius:6, marginBottom:16, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontSize:13, fontWeight:600, color:"#22c55e" }}>Import complete — {result.created} created, {result.updated} updated, {result.skipped} skipped</span>
          <button onClick={() => setResult(null)} style={{ background:"none", border:"none", color:"var(--text-muted)", cursor:"pointer", fontSize:14 }}>✕</button>
        </div>
      )}

      {/* Step: IDLE — file picker */}
      {step === "idle" && (
        <div>
          <input ref={fileRef} type="file" accept=".csv,.tsv,.xlsx,.xls" onChange={handleFile} style={{ display:"none" }}/>
          <Btn primary onClick={() => fileRef.current?.click()}>📂 Upload CSV</Btn>
        </div>
      )}

      {/* Step: MAPPING */}
      {step === "mapping" && (
        <div>
          <div style={{ marginBottom:16, padding:12, backgroundColor:"var(--bg-base)", borderRadius:6, border:"1px solid var(--border)" }}>
            <span style={{ fontSize:12, fontWeight:600, color:"var(--text-tertiary)" }}>Found {headers.length} columns · {rawRows.length} data rows</span>
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
            {IMPORT_FIELDS.map(f => (
              <div key={f.key} style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:11, fontWeight:700, color:f.required?"var(--text-bright)":"var(--text-secondary)", minWidth:130 }}>
                  {f.label}{f.required?" *":""}
                </span>
                <select value={mapping[f.key] !== undefined ? mapping[f.key] : ""} onChange={e => setMap(f.key, e.target.value)}
                  style={{ flex:1, padding:"5px 8px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:12, fontFamily:"inherit" }}>
                  <option value="">— Skip —</option>
                  {headers.map((h, i) => <option key={i} value={i}>{h}</option>)}
                </select>
              </div>
            ))}
          </div>
          <Btn primary onClick={buildPreview}>Preview Import →</Btn>
        </div>
      )}

      {/* Step: PREVIEW */}
      {step === "preview" && (
        <div>
          {/* Summary */}
          <div style={{ display:"flex", gap:12, marginBottom:16 }}>
            {["new","match","duplicate","error"].map(s => {
              const count = previewRows.filter(r => r.status === s).length;
              if (!count) return null;
              const c = statusColors[s];
              return (
                <div key={s} style={{ padding:"8px 14px", backgroundColor:`${c}14`, border:`1px solid ${c}33`, borderRadius:6, textAlign:"center" }}>
                  <div style={{ fontSize:18, fontWeight:800, color:c, fontFamily:"monospace" }}>{count}</div>
                  <div style={{ fontSize:10, fontWeight:700, color:c, textTransform:"uppercase" }}>{statusLabels[s]}</div>
                </div>
              );
            })}
          </div>

          {/* Table */}
          <div style={{ maxHeight:400, overflow:"auto", border:"1px solid var(--border)", borderRadius:6, marginBottom:16 }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <thead>
                <tr style={{ backgroundColor:"var(--bg-base)", position:"sticky", top:0, zIndex:1 }}>
                  <th style={{ padding:"8px 10px", textAlign:"center", width:36 }}>
                    <input type="checkbox" checked={previewRows.every((_, i) => selected[i])}
                      onChange={e => { const s = {}; previewRows.forEach((_, i) => s[i] = e.target.checked); setSelected(s); }}
                      style={{ accentColor:"#3b82f6" }}/>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase" }}>Status</th>
                  <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase" }}>Relationship Name</th>
                  <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase" }}>Email</th>
                  <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase" }}>Segment</th>
                  <th style={{ padding:"8px 10px", textAlign:"left", fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((pr, i) => {
                  const c = statusColors[pr.status];
                  const hasMerge = pr.status === "match" && mergeFields[i] && Object.keys(mergeFields[i]).length > 0;
                  return (
                    <React.Fragment key={i}>
                      <tr style={{ borderBottom:"1px solid var(--border)", opacity: selected[i] ? 1 : 0.4 }}>
                        <td style={{ padding:"6px 10px", textAlign:"center" }}>
                          <input type="checkbox" checked={!!selected[i]} onChange={e => setSelected(p => ({ ...p, [i]: e.target.checked }))}
                            style={{ accentColor:"#3b82f6" }}/>
                        </td>
                        <td style={{ padding:"6px 10px" }}>
                          <span style={{ fontSize:10, fontWeight:800, padding:"2px 8px", borderRadius:4, backgroundColor:`${c}22`, color:c, textTransform:"uppercase" }}>{statusLabels[pr.status]}</span>
                        </td>
                        <td style={{ padding:"6px 10px", fontWeight:600, color:"var(--text-bright)" }}>{pr.record.relationshipName || "—"}</td>
                        <td style={{ padding:"6px 10px", color:"var(--text-secondary)" }}>{pr.record.email || "—"}</td>
                        <td style={{ padding:"6px 10px", color:"var(--text-secondary)" }}>{pr.record.segment || "—"}</td>
                        <td style={{ padding:"6px 10px", fontSize:11, color:"var(--text-muted)" }}>{pr.reason}</td>
                      </tr>
                      {/* Merge field controls for matched rows */}
                      {hasMerge && selected[i] && (
                        <tr style={{ borderBottom:"1px solid var(--border)", backgroundColor:"var(--bg-base)" }}>
                          <td colSpan={6} style={{ padding:"8px 10px 8px 46px" }}>
                            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                              {Object.keys(mergeFields[i]).map(fk => {
                                const field = IMPORT_FIELDS.find(f => f.key === fk);
                                const choice = mergeFields[i][fk];
                                return (
                                  <div key={fk} style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, padding:"3px 8px", backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:4 }}>
                                    <span style={{ fontWeight:700, color:"var(--text-secondary)", minWidth:70 }}>{field?.label || fk}:</span>
                                    <span style={{ color:"var(--text-muted)", textDecoration:choice==="incoming"?"none":"line-through", maxWidth:100, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                                      title={pr.record[fk]}>{pr.record[fk]}</span>
                                    <button onClick={() => setMergeFields(p => {
                                      const u = { ...p }; u[i] = { ...u[i], [fk]: u[i][fk] === "incoming" ? "existing" : "incoming" }; return u;
                                    })} style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, fontWeight:800,
                                      color: choice === "incoming" ? "#22c55e" : "#f43f5e", padding:"0 4px" }}>
                                      {choice === "incoming" ? "✓ Use" : "✗ Keep"}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display:"flex", gap:8 }}>
            <Btn onClick={() => setStep("mapping")}>← Back to Mapping</Btn>
            <Btn primary onClick={commitImport}>
              Import {previewRows.filter((_, i) => selected[i]).length} Contacts
            </Btn>
          </div>
        </div>
      )}
    </div>
  );
};

const DataPanel = ({ data, setData }) => {
  const exp = () => { const b=new Blob([JSON.stringify(data,null,2)],{type:"application/json"}); const u=URL.createObjectURL(b); const a=document.createElement("a"); a.href=u; a.download=`harnesstrack-backup-${today()}.json`; a.click(); URL.revokeObjectURL(u); };
  const imp = () => { const i=document.createElement("input"); i.type="file"; i.accept=".json"; i.onchange=e=>{ const f=e.target.files[0]; if(!f)return; const r=new FileReader(); r.onload=ev=>{try{ const d=JSON.parse(ev.target.result); if(d.templates&&d.clients){setData(d);saveData(d);alert("Imported.");} else alert("Invalid file."); }catch{alert("Parse error.");}}; r.readAsText(f); }; i.click(); };
  const rst = () => { if(window.confirm("Reset ALL data?")){const f=defaultData();setData(f);saveData(f);} };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      <div>
        <h2 style={{ margin:"0 0 8px", fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Data Management</h2>
        <p style={{ margin:"0 0 24px", fontSize:13, color:"var(--text-secondary)" }}>Export backups, import data, or reset</p>
        <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
          <Btn primary onClick={exp}>📤 Export Backup</Btn><Btn onClick={imp}>📥 Import Backup</Btn><Btn onClick={rst} style={{ borderColor:"#f43f5e44", color:"#f43f5e" }}>🗑 Reset All</Btn>
        </div>
        <div style={{ marginTop:24, padding:16, backgroundColor:"var(--bg-card)", borderRadius:8, border:"1px solid var(--border)" }}>
          <p style={{ fontSize:12, color:"var(--text-secondary)", margin:0 }}><strong style={{ color:"var(--text-tertiary)" }}>Storage:</strong> {data.clients.filter(c=>!c.archived).length} active · {data.clients.filter(c=>c.archived).length} archived · {data.templates.length} templates · {(data.campaigns||[]).length} campaigns</p>
        </div>
      </div>
      <ContactImporter data={data} setData={setData}/>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// CAMPAIGNS TAB
// ═════════════════════════════════════════════════════════════════════════════

const CAMPAIGN_STATUS_COLORS = { draft: "#64748b", active: "#22c55e", archived: "#f59e0b" };

const AddCampaignModal = ({ data, onSave, onClose }) => {
  const [internalTitle, setInternalTitle] = useState("");
  const [clientIntro, setClientIntro] = useState("");
  const [beginDate, setBeginDate] = useState(today());
  const [endDate, setEndDate] = useState("");
  const [segments, setSegments] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [medium, setMedium] = useState("digital");
  const [attachment, setAttachment] = useState("");
  const [attachmentName, setAttachmentName] = useState("");
  const fileRef = useRef(null);

  const toggleArr = (arr, setArr, val) => setArr(arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5000000) return alert("File too large. Max 5MB.");
    const reader = new FileReader();
    reader.onload = ev => { setAttachment(ev.target.result); setAttachmentName(file.name); };
    reader.readAsDataURL(file);
  };

  // Compute matching recipients (excluding marketing opt-outs)
  const matchingClients = (data.clients || []).filter(c => {
    if (c.archived) return false;
    if (c.marketingOptOut) return false;
    if (segments.length > 0 && !segments.includes(c.segment)) return false;
    if (occupations.length > 0 && !occupations.includes(c.occupation)) return false;
    return true;
  });

  const create = () => {
    if (!internalTitle.trim()) return alert("Internal Title is required.");
    if (!beginDate) return alert("Begin Date is required.");
    if (segments.length === 0) return alert("Select at least one segment.");
    const num = String(data.nextCampaignNum || 1).padStart(3, "0");
    const refCode = `HC-${new Date().getFullYear()}-${num}`;
    const campaign = {
      id: uid(), refCode, internalTitle: internalTitle.trim(), clientIntro: clientIntro.trim(),
      beginDate, endDate, segments, occupations, medium,
      attachment, attachmentName, status: "draft", createdDate: today(),
      recipients: matchingClients.map(c => ({ clientId: c.id, responded: null })),
    };
    onSave(campaign);
  };

  const chipBtn = (active, color) => ({
    padding: "5px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", transition: "all 0.15s",
    backgroundColor: active ? `${color}22` : "var(--bg-base)",
    color: active ? color : "var(--text-muted)",
    border: active ? `2px solid ${color}` : "1px solid var(--border)",
  });

  return (
    <Modal title="Create Campaign" onClose={onClose} wide>
      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
        <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:14 }}>
          <div>
            <label style={labelStyle}>Internal Title *</label>
            <Input value={internalTitle} onChange={setInternalTitle} placeholder='e.g. "Spring Client Appreciation Event"'/>
          </div>
          <div>
            <label style={labelStyle}>Reference</label>
            <div style={{ padding:"8px 12px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:6, color:"var(--text-muted)", fontSize:13, fontFamily:"monospace" }}>
              HC-{new Date().getFullYear()}-{String(data.nextCampaignNum || 1).padStart(3, "0")}
            </div>
          </div>
        </div>
        <div>
          <label style={labelStyle}>Client Introduction (visible to client)</label>
          <textarea value={clientIntro} onChange={e => setClientIntro(e.target.value)} rows={3} placeholder="The message or introduction your clients will see..."
            style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:13, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical" }}/>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
          <div><label style={labelStyle}>Begin Date *</label><input type="date" value={beginDate} onChange={e => setBeginDate(e.target.value)} style={dateInputStyle}/></div>
          <div><label style={labelStyle}>End Date</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={dateInputStyle}/></div>
        </div>

        {/* Segments */}
        <div>
          <label style={labelStyle}>Client Segments Included *</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {(data.segments || []).map(s => {
              const active = segments.includes(s);
              const c = SEGMENT_COLORS[s] || "var(--text-secondary)";
              return <button key={s} onClick={() => toggleArr(segments, setSegments, s)} style={chipBtn(active, c)}>{s}</button>;
            })}
          </div>
        </div>

        {/* Occupations */}
        <div>
          <label style={labelStyle}>Client Occupations Included</label>
          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            <button onClick={() => setOccupations([])} style={chipBtn(occupations.length === 0, "#94a3b8")}>All</button>
            {(data.occupations || []).map(o => {
              const active = occupations.includes(o);
              return <button key={o} onClick={() => toggleArr(occupations, setOccupations, o)} style={chipBtn(active, "#3b82f6")}>{o}</button>;
            })}
          </div>
        </div>

        {/* Medium */}
        <div>
          <label style={labelStyle}>Campaign Medium</label>
          <div style={{ display:"flex", gap:8 }}>
            {[["digital","📧","Digital"],["analogue","📬","Analogue"]].map(([val, icon, label]) => (
              <button key={val} onClick={() => setMedium(val)}
                style={{ flex:1, padding:"12px 14px", borderRadius:6, border:medium===val?`2px solid ${val==="digital"?"#3b82f6":"#f59e0b"}`:"1px solid var(--border)", backgroundColor:medium===val?"var(--border)":"var(--bg-base)", color:"var(--text-primary)", fontSize:13, fontWeight:600, cursor:"pointer" }}>
                {icon} {label}
              </button>
            ))}
          </div>
        </div>

        {/* Attachment */}
        <div>
          <label style={labelStyle}>{medium === "digital" ? "Marketing Email / Asset" : "Print Piece"} (optional)</label>
          <input ref={fileRef} type="file" onChange={handleFileUpload} style={{ display:"none" }}
            accept={medium === "digital" ? ".html,.pdf,.png,.jpg,.jpeg" : ".pdf,.png,.jpg,.jpeg,.docx"}/>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <Btn small onClick={() => fileRef.current?.click()}>Upload File</Btn>
            {attachmentName && <span style={{ fontSize:12, color:"var(--text-tertiary)" }}>📎 {attachmentName}</span>}
            {attachment && <button onClick={() => { setAttachment(""); setAttachmentName(""); }} style={{ background:"none", border:"none", color:"#f43f5e", fontSize:11, cursor:"pointer" }}>Remove</button>}
          </div>
        </div>

        {/* Preview: matching recipients */}
        <div style={{ padding:12, backgroundColor:"var(--bg-base)", borderRadius:6, border:"1px solid var(--border)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <span style={{ fontSize:11, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Matching Recipients</span>
            <span style={{ fontSize:13, fontWeight:800, color:"var(--text-bright)", fontFamily:"monospace" }}>{matchingClients.length}</span>
          </div>
          <div style={{ display:"flex", gap:4, flexWrap:"wrap", maxHeight:80, overflowY:"auto" }}>
            {matchingClients.length === 0 && <span style={{ fontSize:12, color:"var(--text-muted)", fontStyle:"italic" }}>No matches — adjust segment/occupation filters</span>}
            {matchingClients.map(c => {
              const sc = SEGMENT_COLORS[c.segment] || "var(--text-secondary)";
              return <span key={c.id} style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, backgroundColor:`${sc}14`, color:sc, border:`1px solid ${sc}33` }}>{c.relationshipName}</span>;
            })}
          </div>
        </div>

        <Btn primary onClick={create} style={{ marginTop:4 }}>Create Campaign</Btn>
      </div>
    </Modal>
  );
};

const generateCampaignReport = (campaigns, data) => {
  const active = campaigns.filter(c => c.status === "active");
  const logo = data.settings?.logoData || "";
  const now = today();
  const rows = active.map(c => {
    const total = c.recipients.length;
    const yesCount = c.recipients.filter(r => r.responded === "yes").length;
    const noCount = c.recipients.filter(r => r.responded === "no").length;
    const pending = total - yesCount - noCount;
    return { ...c, total, yesCount, noCount, pending };
  });

  const html = `<!DOCTYPE html><html><head><title>Campaign Progress Report</title>
  <style>
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin:0; padding:0; background:#fff; }
    .header { background:#1e3a5f; color:#fff; padding:24px 36px; display:flex; align-items:center; gap:16px; }
    .header img { height:40px; }
    .header h1 { margin:0; font-size:20px; font-weight:700; }
    .header .date { font-size:12px; opacity:0.8; margin-top:2px; }
    .section { padding:20px 36px; }
    .summary { display:flex; gap:16px; margin-bottom:20px; }
    .stat { flex:1; padding:12px 16px; background:#f8fafc; border:1px solid #e2e8f0; border-radius:6px; text-align:center; }
    .stat .num { font-size:24px; font-weight:800; font-family:monospace; }
    .stat .lbl { font-size:10px; text-transform:uppercase; color:#64748b; letter-spacing:0.06em; }
    table { width:100%; border-collapse:collapse; margin-top:12px; }
    th { text-align:left; padding:8px 12px; font-size:10px; text-transform:uppercase; color:#64748b; border-bottom:2px solid #e2e8f0; }
    td { padding:8px 12px; font-size:12px; border-bottom:1px solid #f1f5f9; }
    .bar-bg { height:8px; background:#e2e8f0; border-radius:4px; overflow:hidden; }
    .bar-fill { height:100%; border-radius:4px; }
    .badge { display:inline-block; padding:2px 8px; border-radius:4px; font-size:10px; font-weight:700; }
    .footer { padding:16px 36px; border-top:1px solid #e2e8f0; text-align:center; font-size:11px; color:#94a3b8; }
    @media print { body { margin:0; } .header { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
  </style></head><body>
    <div class="header">
      ${logo ? `<img src="${logo}"/>` : ""}
      <div><h1>Campaign Progress Report</h1><div class="date">${now}</div></div>
    </div>
    <div class="section">
      <div class="summary">
        <div class="stat"><div class="num">${active.length}</div><div class="lbl">Active Campaigns</div></div>
        <div class="stat"><div class="num" style="color:#22c55e">${rows.reduce((s,r) => s+r.yesCount,0)}</div><div class="lbl">Total Responded Yes</div></div>
        <div class="stat"><div class="num" style="color:#f43f5e">${rows.reduce((s,r) => s+r.noCount,0)}</div><div class="lbl">Total Responded No</div></div>
        <div class="stat"><div class="num" style="color:#64748b">${rows.reduce((s,r) => s+r.pending,0)}</div><div class="lbl">Awaiting Response</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Campaign</th><th>Ref</th><th>Medium</th><th>Dates</th><th style="text-align:center">Recipients</th><th style="text-align:center">Yes</th><th style="text-align:center">No</th><th style="text-align:center">Pending</th><th style="width:120px">Response Rate</th>
        </tr></thead>
        <tbody>${rows.map(r => {
          const rate = r.total > 0 ? Math.round((r.yesCount / r.total) * 100) : 0;
          return `<tr>
            <td><strong>${r.internalTitle}</strong></td>
            <td style="font-family:monospace;font-size:11px;color:#64748b">${r.refCode}</td>
            <td><span class="badge" style="background:${r.medium==="digital"?"#dbeafe;color:#3b82f6":"#fef3c7;color:#f59e0b"}">${r.medium==="digital"?"📧 Digital":"📬 Analogue"}</span></td>
            <td style="font-size:11px">${r.beginDate}${r.endDate?` → ${r.endDate}`:""}</td>
            <td style="text-align:center;font-weight:700">${r.total}</td>
            <td style="text-align:center;color:#22c55e;font-weight:700">${r.yesCount}</td>
            <td style="text-align:center;color:#f43f5e;font-weight:700">${r.noCount}</td>
            <td style="text-align:center;color:#64748b">${r.pending}</td>
            <td><div class="bar-bg"><div class="bar-fill" style="width:${rate}%;background:#22c55e"></div></div><div style="font-size:10px;text-align:center;margin-top:2px">${rate}%</div></td>
          </tr>`;
        }).join("")}</tbody>
      </table>
    </div>
    <div class="footer">Generated by HarnessTrack · ${now} · Confidential</div>
  </body></html>`;

  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
  else alert("Please allow popups to generate the report.");
};

const base64ToUint8Array = (dataUrl) => {
  const b64 = dataUrl.split(",")[1];
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

const getImageType = (dataUrl) => {
  if (dataUrl.includes("image/png")) return "png";
  if (dataUrl.includes("image/jpeg") || dataUrl.includes("image/jpg")) return "jpg";
  if (dataUrl.includes("image/gif")) return "gif";
  return "png";
};

const getImgDims = (dataUrl, maxW) => new Promise(resolve => {
  const img = new Image();
  img.onload = () => {
    let w = img.width, h = img.height;
    if (w > maxW) { h = Math.round(h * (maxW / w)); w = maxW; }
    resolve({ width: w, height: h });
  };
  img.onerror = () => resolve({ width: maxW, height: Math.round(maxW * 0.4) });
  img.src = dataUrl;
});

const generateAnaloguePrint = async (campaign, data) => {
  const logoData = data.settings?.logoData || "";
  const disclosure = data.settings?.footerDisclosure || "";
  const recipients = campaign.recipients.map(r => {
    const cl = data.clients.find(c => c.id === r.clientId);
    return cl || null;
  }).filter(Boolean);

  if (recipients.length === 0) return alert("No recipients in this campaign.");

  const FONT = "Segoe UI";
  const dateStr = new Date().toLocaleDateString("en-US", { year:"numeric", month:"long", day:"numeric" });

  // Prepare logo
  let logoBytes = null, logoDims = null, logoType = "png";
  if (logoData) {
    logoBytes = base64ToUint8Array(logoData);
    logoType = getImageType(logoData);
    logoDims = await getImgDims(logoData, 180);
  }

  // Prepare campaign image attachment
  let assetBytes = null, assetDims = null, assetType = "png";
  const hasImage = campaign.attachment && /\.(png|jpg|jpeg|gif|webp)$/i.test(campaign.attachmentName || "");
  if (hasImage) {
    assetBytes = base64ToUint8Array(campaign.attachment);
    assetType = getImageType(campaign.attachment);
    assetDims = await getImgDims(campaign.attachment, 500);
  }

  // Build one section per recipient
  const sections = recipients.map((cl, idx) => {
    const addr = [cl.street, cl.suite].filter(Boolean).join(", ");
    const cityLine = [cl.city, cl.state, cl.zip].filter(Boolean).join(" ");
    const greeting = cl.name1 || cl.relationshipName;

    // Fresh header per section
    const secHeader = [];
    if (logoBytes && logoDims) {
      secHeader.push(
        new Paragraph({
          children: [new ImageRun({ data: logoBytes, transformation: { ...logoDims }, type: logoType })],
          spacing: { after: 100 },
        })
      );
    } else {
      secHeader.push(new Paragraph({ children: [] }));
    }

    // Fresh footer per section
    const secFooter = [];
    if (disclosure) {
      disclosure.split("\n").forEach(line => {
        secFooter.push(
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: line, font: FONT, size: 14, color: "94a3b8", italics: true })],
            spacing: { before: 20, after: 20 },
          })
        );
      });
    } else {
      secFooter.push(new Paragraph({ children: [] }));
    }

    const children = [];

    // Date
    children.push(new Paragraph({
      children: [new TextRun({ text: dateStr, font: FONT, size: 22 })],
      spacing: { after: 300 },
    }));

    // Address block
    children.push(new Paragraph({
      children: [new TextRun({ text: cl.relationshipName, font: FONT, size: 22, bold: true })],
      spacing: { after: 40 },
    }));
    if (cl.name1 && cl.name1 !== cl.relationshipName) {
      children.push(new Paragraph({
        children: [new TextRun({ text: `Attn: ${cl.name1}`, font: FONT, size: 22 })],
        spacing: { after: 40 },
      }));
    }
    if (addr) {
      children.push(new Paragraph({
        children: [new TextRun({ text: addr, font: FONT, size: 22 })],
        spacing: { after: 40 },
      }));
    }
    if (cityLine) {
      children.push(new Paragraph({
        children: [new TextRun({ text: cityLine, font: FONT, size: 22 })],
        spacing: { after: 40 },
      }));
    }

    // Blank + Greeting
    children.push(new Paragraph({ children: [], spacing: { after: 200 } }));
    children.push(new Paragraph({
      children: [new TextRun({ text: `Dear ${greeting},`, font: FONT, size: 22 })],
      spacing: { after: 200 },
    }));

    // Client introduction body
    if (campaign.clientIntro) {
      campaign.clientIntro.split("\n").forEach(line => {
        children.push(new Paragraph({
          children: [new TextRun({ text: line, font: FONT, size: 22 })],
          spacing: { after: 160 },
        }));
      });
    }

    // Campaign image asset
    if (assetBytes && assetDims) {
      children.push(new Paragraph({ children: [], spacing: { after: 100 } }));
      children.push(new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [new ImageRun({ data: assetBytes, transformation: { ...assetDims }, type: assetType })],
        spacing: { after: 200 },
      }));
    }

    return {
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      headers: { default: new Header({ children: secHeader }) },
      footers: { default: new Footer({ children: secFooter }) },
      children,
    };
  });

  const doc = new Document({
    styles: {
      default: { document: { run: { font: FONT, size: 22 } } },
    },
    sections,
  });

  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${campaign.refCode}-${campaign.internalTitle.replace(/[^a-zA-Z0-9]/g, "-")}.docx`;
  a.click();
  URL.revokeObjectURL(url);
};

const launchDigitalCampaign = (campaign, data) => {
  const emails = data.settings?.teamEmails || {};
  const recipients = campaign.recipients.map(r => {
    const cl = data.clients.find(c => c.id === r.clientId);
    return cl?.email || null;
  }).filter(Boolean);

  if (!emails.primary) return alert("Please configure team emails in Settings before sending a digital campaign.");
  if (recipients.length === 0) return alert("No recipients have email addresses on file.");

  const boundary = "----=_HarnessTrack_" + uid();
  const to = emails.primary;
  const cc = [emails.cc1, emails.cc2].filter(Boolean).join(", ");
  const bcc = recipients.join(", ");
  const subject = campaign.internalTitle;
  const dateStr = new Date().toUTCString();

  // Determine if we have an inline image attachment
  const hasAttachment = campaign.attachment && campaign.attachmentName;
  const isImage = hasAttachment && /\.(png|jpg|jpeg|gif|webp)$/i.test(campaign.attachmentName);

  // Extract base64 data from data URL
  let attachB64 = "";
  let attachMime = "application/octet-stream";
  if (hasAttachment) {
    const parts = campaign.attachment.split(",");
    attachB64 = parts[1] || "";
    const mimeMatch = parts[0]?.match(/:(.*?);/);
    if (mimeMatch) attachMime = mimeMatch[1];
  }

  const cidName = "campaign-asset";

  // Build HTML body
  let htmlBody = `<html><head><style>body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; }</style></head><body>`;
  htmlBody += `<div style="max-width:640px;margin:0 auto;">`;
  if (campaign.clientIntro) {
    htmlBody += `<p style="font-size:14px;line-height:1.6;color:#1e293b;">${campaign.clientIntro.replace(/\n/g, "<br/>")}</p>`;
  }
  if (isImage) {
    htmlBody += `<div style="margin:16px 0;"><img src="cid:${cidName}" style="max-width:100%;border-radius:6px;" alt="${campaign.attachmentName}"/></div>`;
  }
  htmlBody += `</div></body></html>`;

  // Build EML
  let eml = "";
  eml += `From: ${to}\r\n`;
  eml += `To: ${to}\r\n`;
  if (cc) eml += `Cc: ${cc}\r\n`;
  eml += `Bcc: ${bcc}\r\n`;
  eml += `Subject: ${subject}\r\n`;
  eml += `Date: ${dateStr}\r\n`;
  eml += `MIME-Version: 1.0\r\n`;
  eml += `X-Unsent: 1\r\n`;

  if (hasAttachment) {
    // Multipart message
    const relBoundary = "----=_HT_rel_" + uid();

    if (isImage) {
      // Multipart/related for inline image
      eml += `Content-Type: multipart/related; boundary="${relBoundary}"\r\n\r\n`;
      // HTML part
      eml += `--${relBoundary}\r\n`;
      eml += `Content-Type: text/html; charset="UTF-8"\r\n`;
      eml += `Content-Transfer-Encoding: 7bit\r\n\r\n`;
      eml += htmlBody + `\r\n`;
      // Image part
      eml += `--${relBoundary}\r\n`;
      eml += `Content-Type: ${attachMime}; name="${campaign.attachmentName}"\r\n`;
      eml += `Content-Transfer-Encoding: base64\r\n`;
      eml += `Content-ID: <${cidName}>\r\n`;
      eml += `Content-Disposition: inline; filename="${campaign.attachmentName}"\r\n\r\n`;
      // Wrap base64 at 76 chars
      eml += attachB64.replace(/(.{76})/g, "$1\r\n") + `\r\n`;
      eml += `--${relBoundary}--\r\n`;
    } else {
      // Multipart/mixed for file attachment (PDF, docx, etc)
      eml += `Content-Type: multipart/mixed; boundary="${boundary}"\r\n\r\n`;
      // HTML part
      eml += `--${boundary}\r\n`;
      eml += `Content-Type: text/html; charset="UTF-8"\r\n`;
      eml += `Content-Transfer-Encoding: 7bit\r\n\r\n`;
      eml += htmlBody + `\r\n`;
      // Attachment part
      eml += `--${boundary}\r\n`;
      eml += `Content-Type: ${attachMime}; name="${campaign.attachmentName}"\r\n`;
      eml += `Content-Transfer-Encoding: base64\r\n`;
      eml += `Content-Disposition: attachment; filename="${campaign.attachmentName}"\r\n\r\n`;
      eml += attachB64.replace(/(.{76})/g, "$1\r\n") + `\r\n`;
      eml += `--${boundary}--\r\n`;
    }
  } else {
    // Simple HTML message, no attachment
    eml += `Content-Type: text/html; charset="UTF-8"\r\n`;
    eml += `Content-Transfer-Encoding: 7bit\r\n\r\n`;
    eml += htmlBody;
  }

  // Download as .eml
  const blob = new Blob([eml], { type: "message/rfc822" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${campaign.refCode}-${campaign.internalTitle.replace(/[^a-zA-Z0-9]/g, "-")}.eml`;
  a.click();
  URL.revokeObjectURL(url);
};

const CampaignCard = ({ campaign, data, setData }) => {
  const [expanded, setExpanded] = useState(false);
  const sc = CAMPAIGN_STATUS_COLORS[campaign.status] || "#64748b";

  const total = campaign.recipients.length;
  const yesCount = campaign.recipients.filter(r => r.responded === "yes").length;
  const noCount = campaign.recipients.filter(r => r.responded === "no").length;
  const pending = total - yesCount - noCount;
  const rate = total > 0 ? Math.round((yesCount / total) * 100) : 0;

  const updateCampaign = (fields) => {
    const u = JSON.parse(JSON.stringify(data));
    const c = u.campaigns.find(x => x.id === campaign.id);
    if (c) Object.assign(c, fields);
    setData(u); saveData(u);
  };

  const setResponse = (clientId, val) => {
    const newRecipients = campaign.recipients.map(r => r.clientId === clientId ? { ...r, responded: val } : r);
    updateCampaign({ recipients: newRecipients });
  };

  const activate = () => updateCampaign({ status: "active" });
  const archive = () => updateCampaign({ status: "archived" });
  const reactivate = () => updateCampaign({ status: "active" });

  return (
    <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, overflow:"hidden", borderLeft:`4px solid ${sc}` }}>
      {/* Card header */}
      <div style={{ padding:"16px 18px" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
          <div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
              <span style={{ fontWeight:700, fontSize:15, color:"var(--text-bright)" }}>{campaign.internalTitle}</span>
              <span style={{ fontSize:10, fontWeight:700, padding:"2px 8px", borderRadius:4, backgroundColor:`${sc}22`, color:sc, textTransform:"uppercase" }}>{campaign.status}</span>
              <span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:4, backgroundColor:campaign.medium==="digital"?"#3b82f618":"#f59e0b18", color:campaign.medium==="digital"?"#3b82f6":"#f59e0b" }}>
                {campaign.medium === "digital" ? "📧 Digital" : "📬 Analogue"}
              </span>
            </div>
            <div style={{ fontSize:11, fontFamily:"monospace", color:"var(--text-muted)", marginBottom:4 }}>{campaign.refCode}</div>
            {campaign.clientIntro && <p style={{ fontSize:12, color:"var(--text-tertiary)", margin:"4px 0 0", lineHeight:1.4, maxWidth:600 }}>{campaign.clientIntro.length > 120 ? campaign.clientIntro.slice(0,120)+"…" : campaign.clientIntro}</p>}
          </div>
          {/* Actions */}
          <div style={{ display:"flex", gap:6, flexShrink:0 }}>
            {campaign.status === "draft" && <Btn small primary onClick={activate}>▶ Activate</Btn>}
            {campaign.status === "active" && campaign.medium === "digital" && (
              <Btn small primary onClick={() => launchDigitalCampaign(campaign, data)}>📧 Download .eml</Btn>
            )}
            {campaign.status === "active" && campaign.medium === "analogue" && (
              <Btn small primary onClick={() => generateAnaloguePrint(campaign, data)}>📄 Download .docx</Btn>
            )}
            {campaign.status === "active" && <Btn small onClick={archive}>Archive</Btn>}
            {campaign.status === "archived" && <Btn small onClick={reactivate}>Reactivate</Btn>}
          </div>
        </div>

        {/* Campaign details */}
        <div style={{ display:"flex", gap:16, flexWrap:"wrap", fontSize:11, color:"var(--text-secondary)", marginBottom:10 }}>
          <span>📅 {campaign.beginDate}{campaign.endDate ? ` → ${campaign.endDate}` : ""}</span>
          <span>🎯 {campaign.segments.join(", ")}</span>
          {campaign.occupations.length > 0 && <span>👤 {campaign.occupations.join(", ")}</span>}
          {campaign.attachmentName && <span>📎 {campaign.attachmentName}</span>}
        </div>

        {/* Response stats bar */}
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          <div style={{ flex:1, height:8, backgroundColor:"var(--border)", borderRadius:4, overflow:"hidden", display:"flex" }}>
            {yesCount > 0 && <div style={{ width:`${(yesCount/total)*100}%`, height:"100%", backgroundColor:"#22c55e" }}/>}
            {noCount > 0 && <div style={{ width:`${(noCount/total)*100}%`, height:"100%", backgroundColor:"#f43f5e" }}/>}
          </div>
          <div style={{ display:"flex", gap:10, fontSize:11, fontWeight:700, flexShrink:0 }}>
            <span style={{ color:"#22c55e" }}>{yesCount} yes</span>
            <span style={{ color:"#f43f5e" }}>{noCount} no</span>
            <span style={{ color:"var(--text-muted)" }}>{pending} pending</span>
            <span style={{ color:"var(--text-tertiary)" }}>{rate}%</span>
          </div>
        </div>
      </div>

      {/* Expand/collapse recipients */}
      <div>
        <button onClick={() => setExpanded(!expanded)}
          style={{ width:"100%", padding:"10px 18px", backgroundColor:"var(--bg-base)", border:"none", borderTop:"1px solid var(--border)", color:"var(--text-secondary)", fontSize:12, fontWeight:600, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span>👥 {total} Recipients</span>
          <span style={{ transform:expanded?"rotate(180deg)":"none", transition:"transform 0.2s" }}>▼</span>
        </button>
        {expanded && (
          <div style={{ borderTop:"1px solid var(--border)" }}>
            {campaign.recipients.map(r => {
              const cl = data.clients.find(c => c.id === r.clientId);
              if (!cl) return null;
              const sc2 = SEGMENT_COLORS[cl.segment] || "var(--text-secondary)";
              return (
                <div key={r.clientId} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 18px", borderBottom:"1px solid var(--border)", fontSize:12 }}>
                  <span style={{ width:6, height:6, borderRadius:"50%", backgroundColor:sc2, flexShrink:0 }}/>
                  <span style={{ flex:1, fontWeight:600, color:"var(--text-bright)" }}>{cl.relationshipName}</span>
                  <span style={{ fontSize:11, color:"var(--text-muted)", minWidth:100 }}>{cl.email || "no email"}</span>
                  <div style={{ display:"flex", gap:4 }}>
                    {["yes","no"].map(val => {
                      const active2 = r.responded === val;
                      const c = val === "yes" ? "#22c55e" : "#f43f5e";
                      return (
                        <button key={val} onClick={() => setResponse(r.clientId, active2 ? null : val)}
                          style={{ padding:"3px 10px", borderRadius:4, fontSize:10, fontWeight:700, cursor:"pointer", textTransform:"uppercase",
                            backgroundColor: active2 ? `${c}22` : "var(--bg-base)",
                            color: active2 ? c : "var(--text-muted)",
                            border: active2 ? `2px solid ${c}` : "1px solid var(--border)" }}>
                          {val}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const CampaignsTab = ({ data, setData }) => {
  const [showAdd, setShowAdd] = useState(false);

  const saveCampaign = (campaign) => {
    const u = { ...data, campaigns: [...(data.campaigns || []), campaign], nextCampaignNum: (data.nextCampaignNum || 1) + 1 };
    setData(u); saveData(u); setShowAdd(false);
  };

  const campaigns = data.campaigns || [];
  const active = campaigns.filter(c => c.status === "active" || c.status === "draft");
  const archived = campaigns.filter(c => c.status === "archived");

  return (
    <div>
      {showAdd && <AddCampaignModal data={data} onSave={saveCampaign} onClose={() => setShowAdd(false)}/>}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div>
          <h2 style={{ margin:0, fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Campaigns</h2>
          <p style={{ margin:"4px 0 0", fontSize:13, color:"var(--text-secondary)" }}>Digital and analogue marketing campaigns, event invitations, and targeted outreach</p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Btn primary onClick={() => setShowAdd(true)}>+ Add Campaign</Btn>
          <Btn onClick={() => generateCampaignReport(campaigns, data)}>📊 Progress Report</Btn>
        </div>
      </div>

      {/* Active / Draft campaigns */}
      {active.length > 0 && (
        <div style={{ marginBottom:24 }}>
          <h3 style={{ margin:"0 0 12px", fontSize:13, fontWeight:700, color:"var(--text-secondary)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Active & Draft ({active.length})</h3>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {active.map(c => <CampaignCard key={c.id} campaign={c} data={data} setData={setData}/>)}
          </div>
        </div>
      )}

      {/* Archived campaigns */}
      {archived.length > 0 && (
        <div>
          <h3 style={{ margin:"0 0 12px", fontSize:13, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", letterSpacing:"0.06em" }}>Archived ({archived.length})</h3>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {archived.map(c => <CampaignCard key={c.id} campaign={c} data={data} setData={setData}/>)}
          </div>
        </div>
      )}

      {campaigns.length === 0 && (
        <div style={{ textAlign:"center", padding:"80px 20px", backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
          <div style={{ fontSize:40, marginBottom:12 }}>📣</div>
          <p style={{ fontSize:15, fontWeight:600, color:"var(--text-tertiary)", marginBottom:8 }}>No Campaigns Yet</p>
          <p style={{ fontSize:13, color:"var(--text-muted)", maxWidth:400, margin:"0 auto", lineHeight:1.6 }}>
            Create your first campaign to start targeting relationships by segment and occupation with digital emails or print mailers.
          </p>
        </div>
      )}
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// SETTINGS
// ═════════════════════════════════════════════════════════════════════════════

const SettingsPanel = ({ data, setData }) => {
  const settings = data.settings || { logoPath: "", logoData: "", theme: "dark", teamEmails: { primary: "", cc1: "", cc2: "" } };
  const [saved, setSaved] = useState(false);
  const fileRef = useRef(null);
  const currentThemeKey = settings.theme || "dark";

  const setTheme = (key) => {
    const u = { ...data, settings: { ...settings, theme: key } };
    setData(u); saveData(u);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return alert("Please select an image file.");
    if (file.size > 500000) return alert("Image too large. Please use an image under 500KB.");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const u = { ...data, settings: { ...settings, logoData: ev.target.result, logoPath: file.name } };
      setData(u); saveData(u);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    };
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    const u = { ...data, settings: { ...settings, logoData: "", logoPath: "" } };
    setData(u); saveData(u);
  };

  const logoData = settings.logoData || "";

  return (
    <div>
      <h2 style={{ margin:"0 0 8px", fontSize:20, fontWeight:700, color:"var(--text-bright)" }}>Settings</h2>
      <p style={{ margin:"0 0 28px", fontSize:13, color:"var(--text-secondary)" }}>Application preferences and configuration</p>

      {/* Theme */}
      <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, padding:24, marginBottom:20 }}>
        <h3 style={{ margin:"0 0 4px", fontSize:15, fontWeight:700, color:"var(--text-bright)" }}>Theme</h3>
        <p style={{ margin:"0 0 16px", fontSize:12, color:"var(--text-secondary)" }}>Switch between dark and light mode</p>
        <div style={{ display:"flex", gap:12 }}>
          {Object.entries(THEMES).map(([key, theme]) => {
            const active = key === currentThemeKey;
            return (
              <button key={key} onClick={() => setTheme(key)}
                style={{ flex:1, padding:16, borderRadius:8, cursor:"pointer",
                  border: active ? "2px solid #3b82f6" : "1px solid var(--border)",
                  backgroundColor: theme.bgBase, transition:"all 0.15s" }}>
                <div style={{ display:"flex", gap:6, marginBottom:10 }}>
                  <span style={{ width:16, height:16, borderRadius:4, backgroundColor:theme.bgBase, border:`1px solid ${theme.border}` }}/>
                  <span style={{ width:16, height:16, borderRadius:4, backgroundColor:theme.bgCard, border:`1px solid ${theme.border}` }}/>
                  <span style={{ width:16, height:16, borderRadius:4, backgroundColor:theme.border }}/>
                </div>
                <div style={{ fontSize:13, fontWeight:active?700:500, color:theme.textBright }}>{theme.name}</div>
                <div style={{ fontSize:11, color:theme.textSecondary, marginTop:2 }}>{key === "dark" ? "Navy + slate" : "Light gray + blue"}</div>
                {active && <div style={{ marginTop:8, fontSize:10, fontWeight:700, color:"#3b82f6" }}>● ACTIVE</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Logo */}
      <div style={{ backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8, padding:24 }}>
        <h3 style={{ margin:"0 0 4px", fontSize:15, fontWeight:700, color:"var(--text-bright)" }}>Company Logo</h3>
        <p style={{ margin:"0 0 16px", fontSize:12, color:"var(--text-secondary)" }}>Used in the header of Progress Reports. Upload a PNG, JPG, or SVG under 500KB.</p>
        <div style={{ display:"flex", gap:12, alignItems:"center" }}>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display:"none" }}/>
          <Btn primary onClick={() => fileRef.current.click()}>Upload Logo</Btn>
          {logoData && <Btn onClick={clearLogo} style={{ color:"#f43f5e", borderColor:"#f43f5e33" }}>Remove Logo</Btn>}
        </div>
        {saved && <div style={{ marginTop:12, padding:"8px 14px", backgroundColor:"#22c55e18", border:"1px solid #22c55e44", borderRadius:6, fontSize:12, color:"#22c55e", fontWeight:600 }}>✓ Logo saved</div>}
        {logoData && (
          <div style={{ marginTop:16, padding:16, backgroundColor:"var(--bg-base)", borderRadius:6, border:"1px solid var(--border)" }}>
            <span style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", textTransform:"uppercase", display:"block", marginBottom:8 }}>Preview</span>
            <div style={{ padding:16, backgroundColor:"#1e3a5f", borderRadius:6, display:"inline-block" }}>
              <img src={logoData} alt="Logo" style={{ maxHeight:48, maxWidth:200 }}/>
            </div>
            {settings.logoPath && <div style={{ marginTop:8, fontSize:11, fontFamily:"monospace", color:"var(--text-secondary)" }}>{settings.logoPath}</div>}
          </div>
        )}
      </div>

      {/* Team Emails (for campaigns) */}
      <div style={{ padding:20, backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
        <h3 style={{ margin:"0 0 4px", fontSize:14, fontWeight:700, color:"var(--text-bright)" }}>Team Emails</h3>
        <p style={{ margin:"0 0 16px", fontSize:12, color:"var(--text-secondary)" }}>Used for digital campaign emails — To, CC fields</p>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:12 }}>
          <div>
            <label style={labelStyle}>Primary (To)</label>
            <Input value={(settings.teamEmails||{}).primary||""} onChange={v => {
              const u = { ...data, settings: { ...settings, teamEmails: { ...(settings.teamEmails||{}), primary: v } } };
              setData(u); saveData(u);
            }} placeholder="jason@harness.com"/>
          </div>
          <div>
            <label style={labelStyle}>CC #1</label>
            <Input value={(settings.teamEmails||{}).cc1||""} onChange={v => {
              const u = { ...data, settings: { ...settings, teamEmails: { ...(settings.teamEmails||{}), cc1: v } } };
              setData(u); saveData(u);
            }} placeholder="jeremy@harness.com"/>
          </div>
          <div>
            <label style={labelStyle}>CC #2</label>
            <Input value={(settings.teamEmails||{}).cc2||""} onChange={v => {
              const u = { ...data, settings: { ...settings, teamEmails: { ...(settings.teamEmails||{}), cc2: v } } };
              setData(u); saveData(u);
            }} placeholder="rachel@harness.com"/>
          </div>
        </div>
      </div>

      {/* Letterhead — Footer Disclosure */}
      <div style={{ padding:20, backgroundColor:"var(--bg-card)", border:"1px solid var(--border)", borderRadius:8 }}>
        <h3 style={{ margin:"0 0 4px", fontSize:14, fontWeight:700, color:"var(--text-bright)" }}>Letterhead — Footer Disclosure</h3>
        <p style={{ margin:"0 0 16px", fontSize:12, color:"var(--text-secondary)" }}>Appears at the bottom of every analogue campaign letter. Typically compliance disclosures, office address, phone.</p>
        <textarea value={settings.footerDisclosure||""} onChange={e => {
          const u = { ...data, settings: { ...settings, footerDisclosure: e.target.value } };
          setData(u); saveData(u);
        }} rows={4} placeholder="Securities offered through... Registered Investment Advisory services offered through... 123 Main St, St. Louis MO 63101 · (314) 555-1234"
          style={{ padding:"6px 10px", backgroundColor:"var(--bg-base)", border:"1px solid var(--border)", borderRadius:4, color:"var(--text-primary)", fontSize:12, outline:"none", width:"100%", fontFamily:"inherit", resize:"vertical", lineHeight:1.5 }}/>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ═════════════════════════════════════════════════════════════════════════════

const LoginScreen = ({ onLogin }) => (
  <div style={{ minHeight:"100vh", backgroundColor:"#0a0e1a", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Segoe UI', -apple-system, sans-serif" }}>
    <div style={{ textAlign:"center" }}>
      <h1 style={{ margin:"0 0 8px", fontSize:28, fontWeight:800, color:"#f1f5f9", letterSpacing:"0.04em" }}>HARNESS<span style={{ color:"#3b82f6" }}>TRACK</span></h1>
      <p style={{ margin:"0 0 32px", fontSize:14, color:"#64748b" }}>Relationship Management System</p>
      <button onClick={onLogin} style={{ padding:"12px 32px", backgroundColor:"#3b82f6", color:"#fff", border:"none", borderRadius:8, fontSize:15, fontWeight:700, cursor:"pointer", fontFamily:"inherit", letterSpacing:"0.02em" }}>
        Sign in with Microsoft
      </button>
      <p style={{ marginTop:16, fontSize:11, color:"#475569" }}>Authorized users only · Entra ID + MFA required</p>
    </div>
  </div>
);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN APP (Authenticated)
// ═════════════════════════════════════════════════════════════════════════════

function AuthenticatedApp() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];

  const [data, setData] = useState(null);
  const [view, setView] = useState("dashboard");
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [relLetterFilter, setRelLetterFilter] = useState(null);
  const [relSegmentFilter, setRelSegmentFilter] = useState(null);
  const [relSearch, setRelSearch] = useState("");
  const [showNewClient, setShowNewClient] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState(null); // null | "saving" | "saved" | "error"

  // Wire up auth context for saveData
  useEffect(() => { setSaveAuth(instance, account); }, [instance, account]);
  useEffect(() => { setSaveStatusCallback(setSaveStatus); return () => setSaveStatusCallback(null); }, []);

  // Load data: try API first, fall back to localStorage, then defaults
  useEffect(() => {
    const load = async () => {
      try {
        const apiData = await apiRead(instance, account);
        if (apiData && apiData.clients) {
          const d = migrateData(apiData);
          assignPartyColors(d.parties);
          setData(d);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn("API load failed, trying localStorage:", err.message);
      }

      // Fallback: localStorage (useful during migration)
      const local = loadLocalData();
      if (local) {
        assignPartyColors(local.parties);
        setData(local);
        setLoading(false);
        // Auto-push local data to API for migration
        try { await apiWrite(instance, account, local); } catch(e) { console.warn("Migration push failed:", e.message); }
        return;
      }

      // No data anywhere — fresh install
      const d = defaultData();
      assignPartyColors(d.parties);
      setData(d);
      setLoading(false);
    };
    load();
  }, [instance, account]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if(data) assignPartyColors(data.parties); }, [data?.parties]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => { instance.logoutRedirect(); };

  if (loading||!data) return <div style={{ minHeight:"100vh", backgroundColor:"var(--bg-base)", display:"flex", alignItems:"center", justifyContent:"center", color:"var(--text-secondary)", fontSize:14 }}>Loading HarnessTrack...</div>;

  const userInitials = account?.name ? account.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0,2) : "??";

  const navItems = [
    { key:"dashboard", label:"Dashboard" },
    { key:"relationships", label:"Relationships" },
    { key:"inventory", label:"Inventory" },
    { key:"reminders", label:"Reminders" },
    { key:"campaigns", label:"Campaigns" },
    { key:"templates", label:"Templates" },
    { key:"data", label:"Data" },
    { key:"settings", label:"Settings" },
  ];
  const isDetail = view==="detail"&&selectedClientId;

  const currentTheme = THEMES[data.settings?.theme || "dark"];

  return (
    <div style={{ minHeight:"100vh", backgroundColor:"var(--bg-base)", color:"var(--text-primary)", fontFamily:"'Segoe UI', -apple-system, sans-serif" }}>
      <style>{themeCSS(currentTheme)}{`select option { background: var(--bg-card); color: var(--text-primary); } * { box-sizing: border-box; } ::-webkit-scrollbar { width: 6px; } ::-webkit-scrollbar-track { background: var(--bg-base); } ::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }`}</style>

      {showNewClient && <NewClientModal data={data} setData={setData} onClose={()=>setShowNewClient(false)}/>}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 24px", borderBottom:"1px solid var(--border)", backgroundColor:"var(--bg-nav)", position:"sticky", top:0, zIndex:100 }}>
        <div style={{ display:"flex", alignItems:"center", gap:20 }}>
          <h1 style={{ margin:0, fontSize:15, fontWeight:800, color:"var(--text-bright)", letterSpacing:"0.04em" }}>HARNESS<span style={{ color:"#3b82f6" }}>TRACK</span></h1>
          <div style={{ width:1, height:20, backgroundColor:"var(--border)" }}/>
          <div style={{ display:"flex", gap:2 }}>
            {navItems.map(n => {
              const active = view===n.key||(n.key==="relationships"&&isDetail);
              return <button key={n.key} onClick={()=>{setView(n.key);setSelectedClientId(null);}} style={{ padding:"6px 14px", backgroundColor:active?"var(--nav-active-bg)":"transparent", color:active?"var(--nav-text-active)":"var(--nav-text)", border:"none", borderRadius:4, fontSize:13, fontWeight:active?600:400, cursor:"pointer" }}>{n.label}</button>;
            })}
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          {/* Save status indicator */}
          {saveStatus === "saving" && <span style={{ fontSize:10, color:"#f59e0b", fontWeight:600 }}>Saving...</span>}
          {saveStatus === "saved" && <span style={{ fontSize:10, color:"#22c55e", fontWeight:600 }}>✓ Saved</span>}
          {saveStatus === "error" && <span style={{ fontSize:10, color:"#f43f5e", fontWeight:600 }}>Save failed</span>}
          {data._lastSavedBy && !saveStatus && (
            <span style={{ fontSize:10, color:"var(--text-muted)" }}>Last: {data._lastSavedBy}</span>
          )}
          <Btn primary small onClick={()=>setShowNewClient(true)}>+ New Relationship</Btn>
          <div onClick={handleLogout} title={`${account?.name || "User"}\nClick to sign out`} style={{ width:30, height:30, borderRadius:"50%", backgroundColor:"#3b82f6", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700, color:"#fff", cursor:"pointer" }}>
            {userInitials}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1100, margin:"0 auto", padding:"28px 24px" }}>
        {view==="dashboard"&&!selectedClientId && <Dashboard data={data} onSelectClient={id=>{setSelectedClientId(id);setView("detail");}}/>}
        {view==="relationships"&&!selectedClientId && <RelationshipsTab data={data} setData={setData} onSelectClient={id=>{setSelectedClientId(id);setView("detail");}} letterFilter={relLetterFilter} setLetterFilter={setRelLetterFilter} segmentFilter={relSegmentFilter} setSegmentFilter={setRelSegmentFilter} search={relSearch} setSearch={setRelSearch}/>}
        {view==="inventory"&&!selectedClientId && <InventoryTab data={data} setData={setData} onSelectClient={id=>{setSelectedClientId(id);setView("detail");}}/>}
        {view==="reminders"&&!selectedClientId && <RemindersTab data={data} setData={setData} onSelectClient={id=>{setSelectedClientId(id);setView("detail");}}/>}
        {view==="campaigns"&&!selectedClientId && <CampaignsTab data={data} setData={setData}/>}
        {isDetail && <ClientDetail clientId={selectedClientId} data={data} setData={setData} onBack={()=>{setSelectedClientId(null);setView("relationships");}}/>}
        {view==="templates" && <TemplateEditor data={data} setData={setData}/>}
        {view==="data" && <DataPanel data={data} setData={setData}/>}
        {view==="settings" && <SettingsPanel data={data} setData={setData}/>}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// ROOT EXPORT (MsalProvider + auth gate)
// ═════════════════════════════════════════════════════════════════════════════

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AppAuthGate />
    </MsalProvider>
  );
}

function AppAuthGate() {
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const handleLogin = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  if (!isAuthenticated) return <LoginScreen onLogin={handleLogin} />;
  return <AuthenticatedApp />;
}
